import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import axios from "axios";
import { fromLonLat } from 'ol/proj';
import { boundingExtent } from 'ol/extent';
import { useMapContext } from './MapContext';
import {
  Icon, Button, Header, Card, Modal, IconGroup, Tab
} from 'semantic-ui-react';
import { QRCodeCanvas } from "qrcode.react";

import WeatherForecastModal from './WeatherForecastModal';
import MobileTourUserView from './MobileTourUserView';
import PortOfCallCardTourUser from './PortOfCallCardTourUser';
import { fetchMarineForecast, fetchOpenMeteoWeather, normalizeWeatherbitForecast } from '../utils/forecast';
import { API_CONFIG, buildApiUrl, buildMapsUrl } from "../utils/apiConfig";
import { fetchTourTrackingLocation, fetchTourTrackingSegmentTracks } from "../utils/trackingKeysApi";
import { replaceBrTags } from '../utils/stringUtils';
import {
  formatDockingPointLabel,
  getEffectivePortCoordinates,
  getEffectivePortLocation,
  getSelectedDockingPoint,
} from '../utils/tourPortLocations';
import SidebarWrapper from './SidebarWrapper';
import {
  getPreferredSeaRouteMode,
  isRiverCruiseTourType,
  isSeaCruiseTourType,
  resolveSegmentRouteMode,
} from '../utils/routeModeResolver';
import { formatDistanceKm, formatSpeedKmh } from '../utils/unitFormatters';
import { setApiTelemetryContext } from '../utils/apiTelemetry';
import { addCruiseContextToMapUrl } from '../utils/cruiseMapContinuity';

const cruisingPathLoadingBannerStyle = {
  position: 'fixed',
  top: '12px',
  left: '50%',
  transform: 'translateX(-50%)',
  zIndex: 1600,
  display: 'inline-flex',
  alignItems: 'center',
  gap: '8px',
  padding: '8px 14px',
  borderRadius: '999px',
  background: 'rgba(255, 255, 255, 0.94)',
  border: '1px solid rgba(148, 163, 184, 0.45)',
  boxShadow: '0 8px 24px rgba(15, 23, 42, 0.16)',
  color: '#0f172a',
  fontSize: '0.92rem',
  fontWeight: 700,
  pointerEvents: 'none',
};

const EVENT_TRANSPORT_SPEED_KMH = {
  road: 60,
  river: 18,
  sea: 28,
};

const LIVE_TRACKING_TAIL_WINDOW_MS = 10 * 60 * 1000;
const LIVE_TRACKING_TAIL_MAX_BRIDGE_KM = {
  sea: 35,
  coastal: 35,
  international: 35,
  river: 12,
  road: 12,
};

const TourFileImagePreview = ({ src, alt, imageStyle = {}, containerStyle = {} }) => {
  const [isLoading, setIsLoading] = useState(Boolean(src));

  useEffect(() => {
    setIsLoading(Boolean(src));
  }, [src]);

  if (!src) return null;

  return (
    <div style={{ position: 'relative', overflow: 'hidden', background: '#f1f5f9', ...containerStyle }}>
      {isLoading ? (
        <div
          aria-label='Loading image'
          style={{
            position: 'absolute',
            inset: 0,
            zIndex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#64748b',
            background: 'rgba(241, 245, 249, 0.88)',
          }}
        >
          <Icon loading name='spinner' style={{ margin: 0, fontSize: '1.4rem' }} />
        </div>
      ) : null}
      <img
        src={src}
        alt={alt}
        loading='lazy'
        onLoad={() => setIsLoading(false)}
        onError={() => setIsLoading(false)}
        style={{ display: 'block', width: '100%', height: '100%', ...imageStyle }}
      />
    </div>
  );
};
const LOBBY_TOP_BAR_HEIGHT_PX = 56;
const LOBBY_TIMELINE_HEIGHT_PX = 128;
const MOBILE_MAP_PANEL_HEIGHT_PX = 154;

const normalizeEventRouteMode = (value, fallback = 'road') => {
  const normalized = String(value || '').trim().toLowerCase();
  if (['road', 'river', 'sea'].includes(normalized)) return normalized;
  const fallbackMode = String(fallback || '').trim().toLowerCase();
  return ['road', 'river', 'sea'].includes(fallbackMode) ? fallbackMode : 'road';
};

const normalizeRouteGeometryMode = (value, fallback = 'road') => {
  const normalized = String(value || '').trim().toLowerCase();
  if (['road', 'river', 'sea', 'coastal', 'international'].includes(normalized)) return normalized;
  const fallbackMode = String(fallback || '').trim().toLowerCase();
  if (['road', 'river', 'sea', 'coastal', 'international'].includes(fallbackMode)) return fallbackMode;
  return 'road';
};

const normalizeTourSegmentRouteMode = (value) => {
  const normalized = String(value || '').trim().toLowerCase();
  if (['road', 'river', 'sea'].includes(normalized)) return normalized;
  return null;
};

const isLandRouteMode = (value) => normalizeRouteGeometryMode(value, 'road') === 'road';

const getPlannedSegmentLineStyle = (routeMode, options = {}) => {
  const landRoute = isLandRouteMode(routeMode);
  return {
    dashed: landRoute,
    ...(options.current ? { color: '#0ea5e9' } : {}),
  };
};

const normalizeSegmentEventRouteBehavior = (value) => (
  value === 'separate' ? 'separate' : 'waypoint'
);

const calculateDistanceKm = (lon1, lat1, lon2, lat2) => {
  const R = 6371;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLon = ((lon2 - lon1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
};

const isValidLonLat = (lon, lat) => {
  const lonNumber = Number(lon);
  const latNumber = Number(lat);
  return (
    Number.isFinite(lonNumber) &&
    Number.isFinite(latNumber) &&
    lonNumber >= -180 &&
    lonNumber <= 180 &&
    latNumber >= -90 &&
    latNumber <= 90
  );
};

const resolveTourMediaUrl = (value) => {
  if (!value) return null;
  const url = String(value).trim();
  if (!url) return null;
  if (/^https?:\/\//i.test(url)) return url;
  try {
    const apiOrigin = new URL(API_CONFIG.API_BASE_URL).origin;
    if (url.startsWith('/')) return `${apiOrigin}${url}`;
    if (url.startsWith('media/')) return `${apiOrigin}/${url}`;
    return new URL(url, API_CONFIG.API_BASE_URL).toString();
  } catch (_error) {
    return url;
  }
};

const getTourPortPhotoUrl = (portVisit) => resolveTourMediaUrl(
  portVisit?.poc_info?.photo_thumbnail_url ||
  portVisit?.poc_info?.photo_small_url ||
  portVisit?.poc_info?.thumbnail_url ||
  portVisit?.poc_info?.photo_url ||
  portVisit?.poc_info?.photo ||
  portVisit?.photo_thumbnail_url ||
  portVisit?.photo_small_url ||
  portVisit?.thumbnail_url ||
  portVisit?.photo_url ||
  portVisit?.photo
);

const getTourPortKey = (poc) => String(poc?.id || poc?.poc_id || poc?.poc_info?.id || poc?.poc_info?.name || '');

const normalizeLonLat = (coords) => {
  if (!Array.isArray(coords) || coords.length < 2) return null;
  const lon = Number(coords[0]);
  const lat = Number(coords[1]);
  return isValidLonLat(lon, lat) ? [lon, lat] : null;
};

const isSameCoordinate = (a, b, precision = 6) => (
  Array.isArray(a) &&
  Array.isArray(b) &&
  Number(a[0]).toFixed(precision) === Number(b[0]).toFixed(precision) &&
  Number(a[1]).toFixed(precision) === Number(b[1]).toFixed(precision)
);

const getLastGeometryCoordinate = (geometry) => {
  if (!geometry?.type || !Array.isArray(geometry?.coordinates)) return null;
  if (geometry.type === 'LineString') {
    return normalizeLonLat(geometry.coordinates[geometry.coordinates.length - 1]);
  }
  if (geometry.type === 'MultiLineString') {
    for (let index = geometry.coordinates.length - 1; index >= 0; index -= 1) {
      const line = geometry.coordinates[index];
      if (Array.isArray(line) && line.length) {
        const coords = normalizeLonLat(line[line.length - 1]);
        if (coords) return coords;
      }
    }
  }
  return null;
};

const getSailedTrackDisplayGeometry = (track) => {
  const geometry = track?.geometry;
  if (!geometry?.type || !Array.isArray(geometry?.coordinates)) return null;

  const metadata = track?.metadata || {};
  const rawStartCount = Number(metadata.start_connector_line_count);
  const rawCleanedCount = Number(metadata.cleaned_line_count);
  const rawEndCount = Number(metadata.end_connector_line_count);
  const hasConnectorMetadata = [rawStartCount, rawCleanedCount, rawEndCount].some(Number.isFinite);

  if (!hasConnectorMetadata) return geometry;

  const startCount = Number.isFinite(rawStartCount) ? Math.max(0, rawStartCount) : 0;
  const endCount = Number.isFinite(rawEndCount) ? Math.max(0, rawEndCount) : 0;

  if (geometry.type === 'LineString') {
    return startCount || endCount ? null : geometry;
  }

  if (geometry.type !== 'MultiLineString') return geometry;

  const lines = geometry.coordinates.filter((line) => Array.isArray(line) && line.length >= 2);
  const cleanedCount = Number.isFinite(rawCleanedCount)
    ? Math.max(0, rawCleanedCount)
    : Math.max(0, lines.length - startCount - endCount);
  const displayLines = lines.slice(startCount, startCount + cleanedCount);

  if (!displayLines.length) return null;
  if (displayLines.length === 1) {
    return {
      type: 'LineString',
      coordinates: displayLines[0],
    };
  }
  return {
    type: 'MultiLineString',
    coordinates: displayLines,
  };
};

const getGeometryDistanceKm = (geometry) => {
  if (!geometry?.type || !Array.isArray(geometry?.coordinates)) return 0;
  const lineDistanceKm = (line) => {
    if (!Array.isArray(line) || line.length < 2) return 0;
    let total = 0;
    for (let index = 1; index < line.length; index += 1) {
      const previous = normalizeLonLat(line[index - 1]);
      const current = normalizeLonLat(line[index]);
      if (!previous || !current) continue;
      total += calculateDistanceKm(previous[0], previous[1], current[0], current[1]);
    }
    return total;
  };

  if (geometry.type === 'LineString') return lineDistanceKm(geometry.coordinates);
  if (geometry.type === 'MultiLineString') {
    return geometry.coordinates.reduce((total, line) => total + lineDistanceKm(line), 0);
  }
  return 0;
};

const buildLiveTailCoordinates = ({ backendEndCoords, livePositions, currentCoords, segmentStart, segmentEnd, routeMode = 'sea' }) => {
  if (!currentCoords) return [];
  const now = new Date();
  const minTime = now.getTime() - LIVE_TRACKING_TAIL_WINDOW_MS;
  const filteredPositions = (livePositions || [])
    .filter((point) => {
      const time = new Date(point.recordedAt).getTime();
      if (!Number.isFinite(time) || time < minTime) return false;
      if (segmentStart && time < segmentStart.getTime()) return false;
      if (segmentEnd && time > segmentEnd.getTime()) return false;
      return normalizeLonLat(point.coords);
    })
    .sort((a, b) => new Date(a.recordedAt).getTime() - new Date(b.recordedAt).getTime());

  const coords = [];
  const append = (candidate) => {
    const normalized = normalizeLonLat(candidate);
    if (!normalized) return;
    if (coords.length && isSameCoordinate(coords[coords.length - 1], normalized)) return;
    coords.push(normalized);
  };

  filteredPositions.forEach((point) => append(point.coords));
  append(currentCoords);

  if (backendEndCoords && coords.length) {
    const maxBridgeKm = LIVE_TRACKING_TAIL_MAX_BRIDGE_KM[normalizeRouteGeometryMode(routeMode, 'sea')]
      || LIVE_TRACKING_TAIL_MAX_BRIDGE_KM.sea;
    const bridgeDistanceKm = calculateDistanceKm(
      backendEndCoords[0],
      backendEndCoords[1],
      coords[0][0],
      coords[0][1]
    );
    if (bridgeDistanceKm <= maxBridgeKm && !isSameCoordinate(backendEndCoords, coords[0])) {
      coords.unshift(backendEndCoords);
    }
  }

  return coords.length >= 2 ? coords : [];
};

const getSelectedDockingLocation = (portVisit) => {
  if (portVisit?.docking_point?.location) return portVisit.docking_point.location;

  const dockingPointId = portVisit?.docking_point_id;
  if (dockingPointId == null) return null;

  const dockingPoints = Array.isArray(portVisit?.poc_info?.docking_points)
    ? portVisit.poc_info.docking_points
    : [];
  const dock = dockingPoints.find((item) => String(item?.id) === String(dockingPointId));
  return dock?.location || null;
};

const getTourUserPortLocation = (portVisit) => {
  const dockingPointId = portVisit?.docking_point_id;
  if (dockingPointId != null || portVisit?.docking_point) {
    return getSelectedDockingLocation(portVisit);
  }
  return getEffectivePortLocation(portVisit);
};

const getTourUserPortCoordinates = (portVisit) => {
  const coordinates = getTourUserPortLocation(portVisit)?.coordinates;
  return Array.isArray(coordinates) && coordinates.length === 2 ? coordinates : null;
};

const getEventCoordinates = (event, anchorPort = null) => {
  const coordinates = event?.custom_location?.coordinates || event?.effective_location?.coordinates;
  if (Array.isArray(coordinates) && coordinates.length >= 2) {
    return [Number(coordinates[0]), Number(coordinates[1])];
  }
  const anchor = getTourUserPortLocation(anchorPort);
  return Array.isArray(anchor?.coordinates)
    ? [Number(anchor.coordinates[0]), Number(anchor.coordinates[1])]
    : null;
};

const isEventAtPortLocation = (eventCoords, anchorPort) => {
  const anchor = getTourUserPortLocation(anchorPort);
  const portCoords = anchor?.coordinates;
  if (!Array.isArray(eventCoords) || !Array.isArray(portCoords)) return false;

  const [eventLon, eventLat] = eventCoords.map(Number);
  const [portLon, portLat] = portCoords.map(Number);
  if (!isValidLonLat(eventLon, eventLat) || !isValidLonLat(portLon, portLat)) return false;

  return calculateDistanceKm(eventLon, eventLat, portLon, portLat) <= 0.1;
};

// A live ship location should be close to the actual docking point before the
// guest view reports it as docked. This deliberately leaves room for a ship in
// a harbour, while ensuring that a delayed approach overrides the timetable.
const LIVE_SHIP_DOCKING_RADIUS_KM = 1.5;
const LIVE_SHIP_POSITION_MAX_AGE_MS = 6 * 60 * 60 * 1000;

const isFreshShipPosition = (position, now = new Date()) => {
  const timestamp = position?.recorded_at || position?.received_at || position?.timestamp;
  if (!timestamp) return false;
  const recordedAt = new Date(timestamp);
  if (Number.isNaN(recordedAt.getTime())) return false;
  return Math.abs(now.getTime() - recordedAt.getTime()) <= LIVE_SHIP_POSITION_MAX_AGE_MS;
};

const sortTourEvents = (events = []) => events
  .filter(Boolean)
  .slice()
  .sort((a, b) => {
    const aDate = a?.date ? new Date(a.date).getTime() : 0;
    const bDate = b?.date ? new Date(b.date).getTime() : 0;
    if (aDate !== bDate) return aDate - bDate;
    return String(a?.id || '').localeCompare(String(b?.id || ''));
  });

const getTourEventName = (event) => (
  event?.display_name ||
  event?.event_info?.name ||
  event?.title ||
  'Tour event'
);

const getTourEventLocationLabel = (event) => {
  if (event?.custom_location_label || event?.event_info?.custom_location_label) {
    return event.custom_location_label || event.event_info.custom_location_label;
  }
  if (event?.placement === 'segment') {
    return normalizeSegmentEventRouteBehavior(event?.segment_route_behavior) === 'separate'
      ? 'Separate route on this leg'
      : 'On this leg';
  }
  return '';
};

const getDateDayKey = (value) => {
  if (!value) return null;
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value.slice(0, 10))) {
    if (!value.includes('T') && !value.includes(' ')) return value.slice(0, 10);
  }

  const parsed = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    if (typeof value === 'string' && value.length >= 10) return value.slice(0, 10);
    return null;
  }

  const year = parsed.getFullYear();
  const month = String(parsed.getMonth() + 1).padStart(2, '0');
  const day = String(parsed.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const getUtcDateDayKey = (value) => {
  if (!value) return null;
  if (typeof value === 'string' && value.length >= 10) {
    return value.slice(0, 10);
  }
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed.toISOString().slice(0, 10);
};

const getTourEventDayKey = (event, fallbackDate = null) => (
  getDateDayKey(event?.date || event?.event_info?.date) || getDateDayKey(fallbackDate)
);

const getSegmentEndDayKey = (fromPoc, toPoc) => (
  getDateDayKey(toPoc?.day_arrival || toPoc?.day_departure)
  || getDateDayKey(fromPoc?.day_departure || fromPoc?.day_arrival)
);

const getCruisingPathFeatureProperties = (fromPoc, toPoc, routeMode, extra = {}) => {
  const segmentKey = fromPoc?.id && toPoc?.id ? `${fromPoc.id}-${toPoc.id}` : '';
  return {
    tourCruisingPath: true,
    tourCruisingPathSegmentKey: segmentKey,
    tourCruisingPathDayKey: getSegmentEndDayKey(fromPoc, toPoc),
    tourCruisingPathRouteMode: normalizeRouteGeometryMode(routeMode, 'sea'),
    ...extra,
  };
};

const doesTourEventMatchDay = (event, dayKey, fallbackDate = null) => {
  const eventDayKey = getTourEventDayKey(event, fallbackDate);
  return eventDayKey ? eventDayKey === dayKey : false;
};

const buildRouteGeoJsonFeatures = (routeGeojson, properties = {}) => {
  if (!routeGeojson) return [];

  const withProperties = (feature) => ({
    ...feature,
    properties: {
      ...(feature.properties || {}),
      ...properties,
    },
  });

  if (routeGeojson.type === 'FeatureCollection') {
    return (routeGeojson.features || []).filter(Boolean).map(withProperties);
  }

  if (routeGeojson.type === 'Feature') {
    return [withProperties(routeGeojson)];
  }

  if (routeGeojson.type) {
    return [{
      type: 'Feature',
      geometry: routeGeojson,
      properties,
    }];
  }

  return [];
};

const applyRouteDistanceLabel = (lineFeatures, startIndex, labelData) => {
  const label = String(labelData?.label || '').trim();
  const id = String(labelData?.id || '').trim();
  if (!label || !id) return;

  lineFeatures.slice(startIndex).forEach((feature) => {
    if (feature?.properties?.provisionalRouteGeometry || feature?.properties?.routeGeometryFallback) return;
    feature.properties = {
      ...(feature.properties || {}),
      routeDistanceLabel: label,
      routeDistanceLabelId: id,
    };
  });
};

const addStraightRouteFeature = ({
  lineFeatures,
  distanceByEventId,
  startCoords,
  endCoords,
  properties,
  distanceEventId = null,
}) => {
  const distanceKm = calculateDistanceKm(
    startCoords[0],
    startCoords[1],
    endCoords[0],
    endCoords[1]
  );
  if (!Number.isFinite(distanceKm) || distanceKm < 0.05) return distanceKm || 0;

  lineFeatures.push({
    type: 'Feature',
    geometry: {
      type: 'LineString',
      coordinates: [startCoords, endCoords],
    },
    properties,
  });

  if (distanceEventId != null) {
    const key = String(distanceEventId);
    distanceByEventId[key] = Number(distanceByEventId[key] || 0) + distanceKm;
  }

  return distanceKm;
};

const parseSegmentBoundaryDate = (value, fallbackHour = 12) => {
  if (!value) return null;
  const text = String(value);
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return null;
  if (!text.includes('T') && !text.includes(' ')) {
    parsed.setHours(fallbackHour, 0, 0, 0);
  }
  return parsed;
};

const parsePortVisitBoundaryDate = (value, fallbackHour = 0, fallbackMinute = 0, fallbackSecond = 0, fallbackMs = 0) => {
  if (!value) return null;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return null;
  const text = String(value);
  if (!text.includes('T') && !text.includes(' ')) {
    parsed.setHours(fallbackHour, fallbackMinute, fallbackSecond, fallbackMs);
  }
  return parsed;
};

const getDayBounds = (dayKey) => {
  if (!dayKey || !/^\d{4}-\d{2}-\d{2}$/.test(dayKey)) return null;
  const [year, month, day] = dayKey.split('-').map(Number);
  return {
    start: new Date(year, month - 1, day, 0, 0, 0, 0),
    end: new Date(year, month - 1, day, 23, 59, 59, 999),
  };
};

const getPortVisitWindowForDay = (poc, dayKey) => {
  const bounds = getDayBounds(dayKey);
  if (!bounds || !poc?.day_arrival) return null;

  const arrival = parsePortVisitBoundaryDate(poc.day_arrival, 0, 0, 0, 0);
  const departure = parsePortVisitBoundaryDate(poc.day_departure || poc.day_arrival, 23, 59, 59, 999);
  if (!arrival || !departure) return null;

  const start = arrival > bounds.start ? arrival : bounds.start;
  const end = departure < bounds.end ? departure : bounds.end;
  return start <= end ? { start, end } : null;
};

const sliceHourlyForecastByWindow = (forecast, start, end) => {
  const hourly = forecast?.hourly;
  if (!hourly?.time || !start || !end) return forecast || null;

  const indexes = hourly.time
    .map((timeValue, index) => {
      const hourStart = new Date(timeValue);
      if (Number.isNaN(hourStart.getTime())) return null;
      const hourEnd = new Date(hourStart.getTime() + 60 * 60 * 1000);
      return hourStart < end && hourEnd > start ? index : null;
    })
    .filter((index) => index !== null);

  if (!indexes.length) return null;

  const slicedHourly = Object.entries(hourly).reduce((acc, [key, value]) => {
    acc[key] = Array.isArray(value) ? indexes.map((index) => value[index]) : value;
    return acc;
  }, {});

  return {
    ...forecast,
    hourly: slicedHourly,
  };
};

const getPortForecastForDay = (poc, dayKey) => {
  const window = getPortVisitWindowForDay(poc, dayKey);
  if (!window) return null;
  return sliceHourlyForecastByWindow(poc?.weather, window.start, window.end);
};

const getPortFullDayForecast = (poc, dayKey) => {
  const bounds = getDayBounds(dayKey);
  if (!bounds) return null;
  return sliceHourlyForecastByWindow(poc?.weather, bounds.start, bounds.end);
};

const getForecastWindowBounds = (dayKey) => {
  const bounds = getDayBounds(dayKey);
  if (!bounds) return [];
  const makeWindow = (label, startHour, endHour) => {
    const start = new Date(bounds.start);
    start.setHours(startHour, 0, 0, 0);
    const end = new Date(bounds.start);
    end.setHours(endHour, 0, 0, 0);
    if (endHour <= startHour) end.setDate(end.getDate() + 1);
    return { label, start, end };
  };
  return [
    makeWindow('Morning', 6, 10),
    makeWindow('Midday', 10, 14),
    makeWindow('Afternoon', 14, 18),
    makeWindow('Evening', 18, 22),
    makeWindow('Night', 22, 6),
  ];
};

const getCondensedForecastWindowBounds = (dayKey, count = 5) => {
  const bounds = getDayBounds(dayKey);
  if (!bounds) return [];
  const makeWindow = (startHour, endHour) => {
    const start = new Date(bounds.start);
    start.setHours(startHour, 0, 0, 0);
    const end = new Date(bounds.start);
    end.setHours(endHour, 0, 0, 0);
    if (endHour <= startHour) end.setDate(end.getDate() + 1);
    return { label: `${startHour}-${endHour}`, start, end };
  };
  if (count <= 2) return [makeWindow(6, 14), makeWindow(14, 22)];
  if (count === 3) return [makeWindow(6, 12), makeWindow(12, 18), makeWindow(18, 6)];
  if (count === 4) return [makeWindow(6, 10), makeWindow(10, 14), makeWindow(14, 18), makeWindow(18, 6)];
  return getForecastWindowBounds(dayKey);
};

const formatHourRangeLabel = (start, end) => {
  if (!(start instanceof Date) || !(end instanceof Date)) return '';
  const formatHour = (date) => String(date.getHours()).padStart(2, '0');
  return `${formatHour(start)} - ${formatHour(end)}`;
};

const rangesOverlap = (aStart, aEnd, bStart, bEnd) => (
  aStart && aEnd && bStart && bEnd && aStart < bEnd && bStart < aEnd
);

const maxHourlyValueInWindow = (forecast, field, start, end) => {
  const times = forecast?.hourly?.time;
  const values = forecast?.hourly?.[field];
  if (!Array.isArray(times) || !Array.isArray(values)) return null;
  const selected = times
    .map((timeValue, index) => {
      const time = new Date(timeValue);
      return time >= start && time < end ? Number(values[index]) : null;
    })
    .filter((value) => Number.isFinite(value));
  return selected.length ? Math.max(...selected) : null;
};

const getHourlyValuesInWindow = (forecast, field, start, end) => {
  const times = forecast?.hourly?.time;
  const values = forecast?.hourly?.[field];
  if (!Array.isArray(times) || !Array.isArray(values)) return [];
  return times
    .map((timeValue, index) => {
      const time = new Date(timeValue);
      return time >= start && time < end ? Number(values[index]) : null;
    })
    .filter((value) => Number.isFinite(value));
};

const getWeatherIconName = ({ precipitation = 0, cloudCover = null, visibility = null } = {}) => {
  if (Number.isFinite(visibility) && visibility > 0 && visibility < 1000) return 'low vision';
  if (Number(precipitation) >= 2) return 'rain';
  if (Number(precipitation) > 0) return 'rain';
  if (Number(cloudCover) >= 75) return 'cloud';
  if (Number(cloudCover) >= 35) return 'cloud';
  return 'sun';
};

const summarizeWindowWeather = (entries, start, end) => {
  const temps = [];
  const precip = [];
  const clouds = [];
  const winds = [];
  entries.forEach((entry) => {
    temps.push(...getHourlyValuesInWindow(entry.forecast, 'temperature_2m', start, end));
    precip.push(...getHourlyValuesInWindow(entry.forecast, 'precipitation', start, end));
    clouds.push(...getHourlyValuesInWindow(entry.forecast, 'cloud_cover', start, end));
    winds.push(...getHourlyValuesInWindow(entry.forecast, 'wind_speed_10m', start, end));
  });
  const tempMin = temps.length ? Math.min(...temps) : null;
  const tempMax = temps.length ? Math.max(...temps) : null;
  const precipitationTotal = precip.reduce((total, value) => total + value, 0);
  const cloudAverage = clouds.length ? clouds.reduce((total, value) => total + value, 0) / clouds.length : null;
  const windMax = winds.length ? Math.max(...winds) : null;
  return {
    tempMin,
    tempMax,
    precipitationTotal,
    cloudAverage,
    windMax,
    icon: getWeatherIconName({ precipitation: precipitationTotal, cloudCover: cloudAverage }),
  };
};

const formatTemperatureSummary = (summary) => {
  if (!Number.isFinite(summary?.tempMin) || !Number.isFinite(summary?.tempMax)) return '—';
  if (Math.round(summary.tempMin) === Math.round(summary.tempMax)) return `${Math.round(summary.tempMax)}°`;
  return `${Math.round(summary.tempMin)}-\n${Math.round(summary.tempMax)}°`;
};

const isNowWithinSegmentWindow = (fromPoc, toPoc, now = new Date()) => {
  const start = parseSegmentBoundaryDate(fromPoc?.day_departure || fromPoc?.day_arrival, 12);
  const end = parseSegmentBoundaryDate(toPoc?.day_arrival || toPoc?.day_departure, 12);
  if (!start || !end) return false;

  // Only one live remaining route may own a tracking position. Buffering
  // adjacent legs here caused both routes to render around short port calls.
  return now >= start && now <= end;
};

const isSegmentPastWindow = (fromPoc, toPoc, now = new Date()) => {
  const end = parseSegmentBoundaryDate(toPoc?.day_arrival || toPoc?.day_departure, 12);
  if (!end) return false;
  return now > end;
};

const formatEventTravelTime = (hours) => {
  const numeric = Number(hours);
  if (!Number.isFinite(numeric) || numeric <= 0) return '—';
  if (numeric < 1) return `${Math.max(1, Math.round(numeric * 60))} min`;
  return `${numeric.toFixed(numeric < 10 ? 1 : 0)} h`;
};

export default function TourDetailBarTourUser({
  map, // kept in props but no direct OL usage here
  activeTourToShow,
  setActiveTourToShow,
  setShowTourDetailBar,
  setShowTourListBar,
  setNumberedMarkers,
  getApiHeaders,
  isMobile,
  setUserTours,
  activeUser,
  onOpenMapInApp,
  sidebarConfig, // Add sidebar configuration
  onHeightChange, // Add callback for height changes
  isVisible: controlledIsVisible,
  setIsVisible: setControlledIsVisible,
  showRouteLegend = true,
  isFullTourPending = false,
}) {
  const {
    addMappoints,
    addGeoJson,
    addRouteDistanceLabels,
    clearRouteDistanceLabels,
    removeFeature,
    isMapReady,
    getMap,
    clearLayer,
    // NEW: we’ll wire clicks & selection
    onMarkerClick,
    selectMarker,
    deselectMarker,
  } = useMapContext();

  const [weather, setWeather] = useState([]);
  const [navigationSegments, setNavigationSegments] = useState([]);
  const [eventRouteDistances, setEventRouteDistances] = useState({});
  const [weatherModalOpen, setWeatherModalOpen] = useState(false);
  const [selectedWeatherData, setSelectedWeatherData] = useState(null);
  const [selectedLocationName, setSelectedLocationName] = useState('');
  const [selectedDateRange, setSelectedDateRange] = useState('');
  const [selectedWeatherStayWindow, setSelectedWeatherStayWindow] = useState(null);
  const [selectedWeatherItineraryContexts, setSelectedWeatherItineraryContexts] = useState([]);
  const [mobileMapLegendOpen, setMobileMapLegendOpen] = useState(false);
  const [tourMapsModalOpen, setTourMapsModalOpen] = useState(false);
  const [selectedTourFileMarker, setSelectedTourFileMarker] = useState(null);
  const [isDownloadingTourMaps, setIsDownloadingTourMaps] = useState(false);
  const [showTutorial, setShowTutorial] = useState(false);
  const [currentTutorialStep, setCurrentTutorialStep] = useState(0);
  const [highlightPosition, setHighlightPosition] = useState(null);
  const [itineraryViewMode, setItineraryViewMode] = useState('vertical');
  const [mobileTab, setMobileTab] = useState('today');
  const [lobbyTab, setLobbyTab] = useState('today');
  const [lobbyTimelineCollapsed, setLobbyTimelineCollapsed] = useState(false);
  const [selectedMapFeature, setSelectedMapFeature] = useState(null);
  const [expandedDays, setExpandedDays] = useState(new Set());
  const [selectedCustomerMap, setSelectedCustomerMap] = useState(null);
  const [selectedTourMapMarker, setSelectedTourMapMarker] = useState(null);
  const [tourTrackingLocation, setTourTrackingLocation] = useState(null);
  const [tourTrackingLocationError, setTourTrackingLocationError] = useState('');
  const [sailedTrackSegmentKeys, setSailedTrackSegmentKeys] = useState(new Set());
  const [sailedTrackEndBySegment, setSailedTrackEndBySegment] = useState(new Map());
  const [sailedTrackDistanceBySegment, setSailedTrackDistanceBySegment] = useState(new Map());
  const [liveTrackingPositions, setLiveTrackingPositions] = useState([]);
  const [isSailedTrackLoading, setIsSailedTrackLoading] = useState(false);
  const [isRouteGeometryLoading, setIsRouteGeometryLoading] = useState(false);
  const [isWeatherLoading, setIsWeatherLoading] = useState(false);
  const [routedCurrentRemaining, setRoutedCurrentRemaining] = useState(null);
  const [marineForecastBySegmentKey, setMarineForecastBySegmentKey] = useState({});
  const [shipDepth, setShipDepth] = useState({ status: 'unavailable', meters: null });
  const [unitPreferences, setUnitPreferences] = useState({ distance_unit: 'km', speed_unit: 'kmh' });
  const [summaryClock, setSummaryClock] = useState(() => Date.now());
  const [localIsVisible, setLocalIsVisible] = useState(true);
  const trackingLocationFeatureRef = useRef(null);
  const isVisible = typeof controlledIsVisible === 'boolean' ? controlledIsVisible : localIsVisible;
  const setIsVisible = useCallback((nextValue) => {
    if (typeof setControlledIsVisible === 'function') {
      setControlledIsVisible(nextValue);
      return;
    }
    setLocalIsVisible(nextValue);
  }, [setControlledIsVisible]);
  const currentTourPosition = useMemo(() => {
    if (tourTrackingLocation?.position) {
      const isManual = tourTrackingLocation?.source === 'manual' || tourTrackingLocation.position?.source === 'manual';
      return {
        ...tourTrackingLocation.position,
        source: isManual ? 'manual' : 'owntracks',
        label: isManual
          ? (tourTrackingLocation.position?.label || tourTrackingLocation?.manual_ship_location?.label || 'Manual ship location')
          : (tourTrackingLocation?.tracking_key?.name || 'Tracking device'),
      };
    }
    return null;
  }, [tourTrackingLocation]);
  useEffect(() => {
    const mobileInitialShip = activeTourToShow?.mobile_initial?.current_ship;
    if (!mobileInitialShip || tourTrackingLocation) return;
    setTourTrackingLocation(mobileInitialShip);
    setTourTrackingLocationError('');
  }, [activeTourToShow?.mobile_initial?.current_ship, tourTrackingLocation]);
  const guestViewMode = useMemo(() => {
    if (isMobile) return 'mobile';
    if (typeof window === 'undefined') return 'lobby';
    const params = new URLSearchParams(window.location.search || '');
    const requestedMode = String(params.get('view') || params.get('guest_view') || params.get('tour_view') || '').toLowerCase();
    if (['lobby', 'desktop'].includes(requestedMode)) return requestedMode;
    return 'lobby';
  }, [isMobile]);
  const isMobileGuestView = Boolean(isMobile);
  const isLobbyGuestView = !isMobileGuestView && (guestViewMode === 'lobby' || guestViewMode === 'desktop');
  const shouldRenderMapContent = !isMobileGuestView || mobileTab === 'map';

  useEffect(() => {
    const intervalId = window.setInterval(() => setSummaryClock(Date.now()), 60000);
    return () => window.clearInterval(intervalId);
  }, []);

  useEffect(() => {
    setApiTelemetryContext({
      route: 'tour-user',
      tab: isMobileGuestView ? mobileTab : lobbyTab,
    });
  }, [isMobileGuestView, lobbyTab, mobileTab]);

  useEffect(() => {
    setLiveTrackingPositions([]);
    setSailedTrackEndBySegment(new Map());
    setSailedTrackSegmentKeys(new Set());
    setSailedTrackDistanceBySegment(new Map());
  }, [activeTourToShow?.id]);

  const isMapVisibleToTourUser = useCallback((mapItem) => {
    const mapProps = mapItem?.properties || mapItem || {};
    return mapProps?.is_visible_to_customers !== false;
  }, []);

  const getTourMapDisplayDedupeKey = useCallback((mapItem, portVisit = null) => {
    const mapProps = mapItem?.properties || mapItem || {};
    const normalize = (value) => replaceBrTags(String(value || ''))
      .trim()
      .toLowerCase()
      .replace(/\s+/g, ' ');
    const name = normalize(mapProps.area_name || mapProps.name || mapItem?.area_name || mapItem?.name);
    const country = normalize(mapProps.country_name || mapItem?.country_name);
    const portId = portVisit?.poc_info?.id || portVisit?.id || null;
    if (portId && name) return `port:${portId}|name:${name}`;
    if (name) return `${name}|${country}`;
    const id = mapItem?.id || mapProps.api_key || mapItem?.api_key;
    if (id) return `id:${id}`;
    return `port:${portId || 'unknown'}`;
  }, []);

  const scoreTourMapResource = useCallback((resource) => {
    const mapItem = resource?.mapItem || {};
    const mapProps = mapItem?.properties || mapItem || {};
    let score = 0;
    if (mapItem?.is_versioned || mapProps?.current_revision_id || mapItem?.current_revision_id) score += 100;
    if (mapProps?.jpg_200dpi_q90_path || mapProps?.jpg_200dpi_q90_path_no_qr) score += 30;
    if (mapProps?.jpg_125dpi_q85_path || mapProps?.jpg_125dpi_q85_path_no_qr) score += 20;
    if (mapProps?.pdf_vector_path || mapProps?.pdf_vector_path_no_qr) score += 15;
    if (mapProps?.pdf_path || mapProps?.pdf_path_no_qr) score += 10;
    if (resource?.portVisit) score += 5;
    return score;
  }, []);

  const dedupeTourMapResources = useCallback((resources) => {
    const bestByKey = new Map();
    (Array.isArray(resources) ? resources : []).forEach((resource) => {
      const key = getTourMapDisplayDedupeKey(resource?.mapItem, resource?.portVisit);
      const current = bestByKey.get(key);
      if (!current || scoreTourMapResource(resource) > scoreTourMapResource(current)) {
        bestByKey.set(key, resource);
      }
    });
    return Array.from(bestByKey.values());
  }, [getTourMapDisplayDedupeKey, scoreTourMapResource]);

  const tourMaps = useMemo(() => {
    const mapCollection = activeTourToShow?.map_on_tour;
    if (Array.isArray(mapCollection)) return mapCollection.filter(isMapVisibleToTourUser);
    if (Array.isArray(mapCollection?.features)) return mapCollection.features.filter(isMapVisibleToTourUser);
    return [];
  }, [activeTourToShow?.map_on_tour, isMapVisibleToTourUser]);

  const tourResourceMaps = useMemo(() => {
    const resources = [];

    tourMaps.forEach((mapItem, index) => {
      resources.push({
        mapItem,
        portVisit: null,
        sourceLabel: 'Tour map',
        key: `tour-${mapItem?.id || mapItem?.api_key || index}`,
      });
    });

    (activeTourToShow?.ports_of_call || []).forEach((poc) => {
      const portMaps = Array.isArray(poc?.poc_info?.maps_in_port?.features)
        ? poc.poc_info.maps_in_port.features.filter(isMapVisibleToTourUser)
        : [];
      portMaps.forEach((mapItem, index) => {
        const mapProps = mapItem?.properties || mapItem || {};
        resources.push({
          mapItem,
          portVisit: poc,
          sourceLabel: `${poc?.visit_order ? `${poc.visit_order}. ` : ''}${poc?.poc_info?.name || 'Port map'}`,
          key: `port-${poc?.id || poc?.visit_order || 'unknown'}-${mapItem?.id || mapProps?.api_key || index}`,
        });
      });
    });

    return dedupeTourMapResources(resources);
  }, [activeTourToShow?.ports_of_call, dedupeTourMapResources, isMapVisibleToTourUser, tourMaps]);

  const getMapAssetPath = useCallback((mapProps, mapItem, fields) => {
    for (const field of fields) {
      const value = mapProps?.[field] || mapItem?.[field];
      if (value) return value;
    }
    return null;
  }, []);

  const getPublicMapBaseUrl = useCallback(() => {
    const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
    return hostname === 'legacy.qrmaps.me'
      ? 'https://legacy.qrmaps.me'
      : 'https://qrmaps.me';
  }, []);

  const getDockingMapUrl = useCallback((mapApiKey, portVisit = null) => {
    if (!mapApiKey) return null;
    const url = new URL(`${getPublicMapBaseUrl()}/${mapApiKey}`);
    const coords = portVisit ? getEffectivePortCoordinates(portVisit) : null;
    if (coords) {
      const selectedDock = getSelectedDockingPoint(portVisit);
      url.searchParams.set('dock_lon', String(Number(coords[0]).toFixed(6)));
      url.searchParams.set('dock_lat', String(Number(coords[1]).toFixed(6)));
      url.searchParams.set(
        'dock_label',
        selectedDock ? formatDockingPointLabel(selectedDock) : (portVisit?.poc_info?.name || 'Docking location')
      );
    }
    return addCruiseContextToMapUrl(url.toString());
  }, [getPublicMapBaseUrl]);

  const getPrimaryPortMapUrl = useCallback((portVisit) => {
    const portMaps = Array.isArray(portVisit?.poc_info?.maps_in_port?.features)
      ? portVisit.poc_info.maps_in_port.features.filter(isMapVisibleToTourUser)
      : [];
    const mapItem = portMaps[0];
    const mapProps = mapItem?.properties || mapItem || {};
    return getDockingMapUrl(mapProps.api_key || mapItem?.api_key, portVisit);
  }, [getDockingMapUrl, isMapVisibleToTourUser]);

  const getTourMapMarkerCoordinates = useCallback((mapItem) => {
    const mapProps = mapItem?.properties || mapItem || {};
    if (Array.isArray(mapItem?.geometry) && mapItem.geometry.length === 2) {
      return normalizeLonLat(mapItem.geometry);
    }
    if (mapItem?.geometry?.type === 'Point' && Array.isArray(mapItem.geometry.coordinates)) {
      return normalizeLonLat(mapItem.geometry.coordinates);
    }
    if (mapProps?.geometry?.type === 'Point' && Array.isArray(mapProps.geometry.coordinates)) {
      return normalizeLonLat(mapProps.geometry.coordinates);
    }
    const ne = mapProps?.ne_r;
    const sw = mapProps?.sw_r;
    if (Array.isArray(ne) && Array.isArray(sw)) {
      return normalizeLonLat([
        (Number(ne[1]) + Number(sw[1])) / 2,
        (Number(ne[0]) + Number(sw[0])) / 2,
      ]);
    }
    const first = mapProps?.mappoints?.[0]?.center;
    if (first?.type === 'Point' && Array.isArray(first.coordinates)) {
      return normalizeLonLat(first.coordinates);
    }
    return null;
  }, []);

  const getTourMapMarkerResource = useCallback((resource, index) => {
    const mapItem = resource?.mapItem;
    const mapProps = mapItem?.properties || mapItem || {};
    const coords = getTourMapMarkerCoordinates(mapItem);
    if (!coords) return null;
    const name = replaceBrTags(mapProps.name || mapProps.area_name || `Map ${index + 1}`);
    const sourceLabel = resource?.sourceLabel || replaceBrTags(mapProps.area_name || 'Tour map');
    const highResJpgPath = getMapAssetPath(mapProps, mapItem, [
      'jpg_200dpi_q90_path_no_qr',
      'jpg_200dpi_q90_path',
    ]);
    const mapApiKey = mapProps.api_key || mapItem?.api_key;
    return {
      ...resource,
      id: `tour-resource-map-${index}`,
      name,
      sourceLabel,
      coords,
      onlineUrl: getDockingMapUrl(mapApiKey, resource?.portVisit),
      downloadUrl: highResJpgPath ? buildMapsUrl(highResJpgPath) : null,
    };
  }, [getDockingMapUrl, getMapAssetPath, getTourMapMarkerCoordinates]);

  const tourMapMarkerResources = useMemo(
    () => tourResourceMaps
      .map((resource, index) => getTourMapMarkerResource(resource, index))
      .filter(Boolean),
    [getTourMapMarkerResource, tourResourceMaps]
  );

  const tourFileMarkerResources = useMemo(() => {
    const portVisits = Array.isArray(activeTourToShow?.ports_of_call)
      ? activeTourToShow.ports_of_call
      : [];
    const files = Array.isArray(activeTourToShow?.files) ? activeTourToShow.files : [];

    return files.map((file) => {
      const customCoordinates = normalizeLonLat(file?.custom_location?.coordinates);
      const portVisit = portVisits.find((candidate) => (
        String(candidate?.id) === String(file?.port_of_call)
      ));
      const coordinates = customCoordinates || getEffectivePortCoordinates(portVisit);
      if (!coordinates) return null;

      const technicalName = file?.original_filename || file?.name || 'File';
      const name = file?.title || file?.description || technicalName;
      return {
        id: `tour-file-${file?.id}`,
        file,
        name,
        coordinates,
        locationName: file?.port_of_call_name || file?.custom_location_label || portVisit?.poc_info?.name || null,
        fileUrl: file?.download_url || file?.file_url || null,
        previewUrl: file?.file_type === 'image'
          ? (file?.download_url || file?.file_url || null)
          : (file?.thumbnail_url || null),
      };
    }).filter(Boolean);
  }, [activeTourToShow?.files, activeTourToShow?.ports_of_call]);

  const selectedTourFileGallery = useMemo(() => {
    if (!selectedTourFileMarker) return [];
    const portOfCallId = selectedTourFileMarker.file?.port_of_call;
    if (portOfCallId == null) return [selectedTourFileMarker];

    const gallery = tourFileMarkerResources.filter((resource) => (
      String(resource.file?.port_of_call) === String(portOfCallId)
      && resource.file?.file_type === 'image'
      && resource.previewUrl
    ));
    return gallery.length ? gallery : [selectedTourFileMarker];
  }, [selectedTourFileMarker, tourFileMarkerResources]);

  const tutorialSteps = useMemo(() => {
    const hasVisibleShipLocationUpdate = Boolean(currentTourPosition);
    const hasPortWeatherForecast = weather.some((poc) => poc?.weather?.hourly);
    const isControlVisible = (selector) => {
      if (typeof document === 'undefined') return false;
      const element = document.querySelector(selector);
      if (!element) return false;
      const rect = element.getBoundingClientRect();
      const styles = window.getComputedStyle(element);
      return rect.width > 0 && rect.height > 0 && styles.visibility !== 'hidden' && styles.display !== 'none';
    };
    if (guestViewMode === 'mobile') {
      return [
        {
          title: 'Today',
          description: 'Start here for the current day, ship update, route, weather, schedule, and quick files.',
          selector: '.TourUser-mobile-today',
        },
        {
          title: 'Itinerary',
          description: 'Open Itinerary to browse all days. Day cards expand and can show the matching ports on the map.',
          selector: '.TourUser-mobile-nav',
        },
        {
          title: 'Map',
          description: 'Use Map for the route, sailed history, ship marker, and quick camera actions.',
          selector: '.TourUser-mobile-nav',
        },
        {
          title: 'Files',
          description: 'Files keeps maps, daily programs, and other resources in one phone-friendly screen.',
          selector: '.TourUser-mobile-nav',
        },
        ...(hasPortWeatherForecast ? [{
          title: 'Weather Details',
          description: 'Tap weather rows in port cards to open the detailed forecast for the visit window.',
          selector: '.TourUser-port-weather-row',
          ensureOpenVisitDay: true,
        }] : []),
        ...(hasVisibleShipLocationUpdate ? [{
          title: 'Ship Position',
          description: 'Use the ship position row or Ship button to center the map on the latest tracked position.',
          selector: '.TourUser-current-location-button',
        }] : []),
      ];
    }

    const mapControlSteps = [
      ...(isControlVisible('.TourUser-map-compass-control') ? [{
        title: 'Compass And Scale',
        description: 'Use the compass to reset the map to north. The scale below it reflects the current map zoom.',
        selector: '.TourUser-map-compass-control',
      }] : []),
      {
        title: 'Map Layers',
        description: 'Open Layers to toggle route aids, device positions, scale and compass, and other map overlays.',
        selector: '.TourUser-map-layers-button',
      },
      {
        title: 'Current Device Location',
        description: 'Use this button to center the map on your current device location when browser location access is available.',
        selector: '.TourUser-map-current-device-button',
      },
    ];

    return [
      {
        title: 'Today Panel',
        description: 'The lobby view opens around today: route, weather, events, ship update, and quick actions.',
        selector: '.TourUser-title',
      },
      ...(hasVisibleShipLocationUpdate ? [{
        title: 'Current Ship Location',
        description: 'Tap the ship update to center the public map on the latest tracked or manual ship position.',
        selector: '.TourUser-current-location-button',
      }] : []),
      {
        title: 'Day Timeline',
        description: 'Use the bottom timeline to browse all days. The current day and selected day are shown separately.',
        selector: '.TourUser-lobby-timeline',
      },
      {
        title: 'All Days',
        description: 'Use the full-width day timeline to browse every day. Selecting a day also updates the map.',
        selector: '.TourUser-lobby-timeline',
      },
      {
        title: 'Maps And Files QR',
        description: 'Open Files to show maps, dailies, and other resources for guests.',
        selector: '.TourUser-lobby-files-button',
      },
      ...mapControlSteps,
      ...(hasVisibleShipLocationUpdate ? [{
        title: 'Ship Tracking On The Map',
        description: 'The orange ship marker is the current tracked position. The latest sailed history may update after the backend rebuilds the cached cruising path.',
        selector: '.TourUser-current-location-button',
      }] : []),
    ];
  }, [currentTourPosition, guestViewMode, showTutorial, weather]);

  const startTutorial = useCallback(() => {
    setShowTutorial(true);
    setCurrentTutorialStep(0);
  }, []);

  const nextTutorialStep = useCallback(() => {
    setCurrentTutorialStep((index) => {
      if (index >= tutorialSteps.length - 1) {
        setShowTutorial(false);
        return index;
      }
      return index + 1;
    });
  }, [tutorialSteps.length]);

  const prevTutorialStep = useCallback(() => {
    setCurrentTutorialStep((index) => Math.max(0, index - 1));
  }, []);

  // Build PoC lookups from the actual tour data (not weather),
  // so they’re available immediately.
  const pocs = useMemo(() => activeTourToShow?.ports_of_call || [], [activeTourToShow?.ports_of_call]);
  const currentTrackingCoords = useMemo(() => {
    const position = currentTourPosition;
    const lon = Number(position?.longitude);
    const lat = Number(position?.latitude);
    if (!isValidLonLat(lon, lat)) return null;
    return [lon, lat];
  }, [currentTourPosition]);
  const currentTrackingCoordsKey = currentTrackingCoords
    ? `${currentTrackingCoords[0].toFixed(5)},${currentTrackingCoords[1].toFixed(5)}`
    : '';

  useEffect(() => {
    let cancelled = false;
    const isLegendVisible = isVisible
      && showRouteLegend
      && (isMobileGuestView ? mobileTab === 'map' : isLobbyGuestView);
    if (!isLegendVisible || !currentTrackingCoords) {
      setShipDepth({ status: 'unavailable', meters: null });
      return undefined;
    }

    setShipDepth((previous) => ({
      status: 'loading',
      meters: previous?.meters ?? null,
    }));

    const loadShipDepth = async () => {
      try {
        const headers = await getApiHeaders();
        const response = await axios.get(buildApiUrl('bathymetry_depth/'), {
          headers,
          params: {
            longitude: currentTrackingCoords[0],
            latitude: currentTrackingCoords[1],
          },
        });
        if (cancelled) return;

        const meters = Number(response.data?.depth_meters);
        setShipDepth({
          status: response.data?.is_water && Number.isFinite(meters) ? 'available' : 'land',
          meters: response.data?.is_water && Number.isFinite(meters) ? meters : null,
        });
      } catch {
        if (!cancelled) setShipDepth({ status: 'unavailable', meters: null });
      }
    };

    void loadShipDepth();
    return () => {
      cancelled = true;
    };
  }, [
    currentTrackingCoords,
    currentTrackingCoordsKey,
    getApiHeaders,
    isLobbyGuestView,
    isMobileGuestView,
    isVisible,
    mobileTab,
    showRouteLegend,
  ]);

  const navigationSegmentsByPair = useMemo(() => {
    const map = new Map();
    navigationSegments.forEach((segment) => {
      if (!segment?.from_port_of_call_id || !segment?.to_port_of_call_id) return;
      map.set(`${segment.from_port_of_call_id}-${segment.to_port_of_call_id}`, segment);
    });
    return map;
  }, [navigationSegments]);

  const segmentEndDayKeyByPair = useMemo(() => {
    const map = new Map();
    const orderedPorts = pocs.slice().sort((a, b) => a.visit_order - b.visit_order);
    for (let index = 0; index < orderedPorts.length - 1; index += 1) {
      const fromPoc = orderedPorts[index];
      const toPoc = orderedPorts[index + 1];
      if (!fromPoc?.id || !toPoc?.id) continue;
      map.set(`${fromPoc.id}-${toPoc.id}`, getSegmentEndDayKey(fromPoc, toPoc));
    }
    return map;
  }, [pocs]);

  const resolveTourSegmentRouteMode = useCallback((fromPoc, toPoc, segment = null) => {
    const tourType = activeTourToShow?.tour_type;
    const summaryMode = normalizeRouteGeometryMode(segment?.mode, '');
    const explicitMode = normalizeTourSegmentRouteMode(fromPoc?.route_to_next_mode);

    if (isSeaCruiseTourType(tourType)) {
      if (explicitMode === 'road' || explicitMode === 'river') return explicitMode;
      if (explicitMode === 'sea') return getPreferredSeaRouteMode(tourType);
      if (summaryMode === 'coastal' || summaryMode === 'international') return summaryMode;
      return getPreferredSeaRouteMode(tourType);
    }

    if (summaryMode) return summaryMode;
    const resolvedMode = resolveSegmentRouteMode({
      startPort: fromPoc?.poc_info || fromPoc,
      endPort: toPoc?.poc_info || toPoc,
      tourType,
      preferredMode: explicitMode === 'sea' ? 'auto' : explicitMode || 'auto',
    });
    return normalizeRouteGeometryMode(resolvedMode, 'road');
  }, [activeTourToShow?.tour_type]);

  const pocsKey = useMemo(
    () => pocs.map((p) => {
      const portCoords = getTourUserPortCoordinates(p) || [];
      const eventsKey = (p?.events || [])
        .map((event) => {
          const coords = event?.custom_location?.coordinates || event?.effective_location?.coordinates || [];
          return [
            event?.id ?? 'x',
            event?.placement || 'port',
            event?.date || event?.event_info?.date || '',
            event?.segment_end_port_of_call_id || '',
            event?.route_mode || '',
            event?.segment_route_behavior || '',
            coords.join(','),
          ].join(':');
        })
        .join(',');
      return [
        p?.id ?? 'x',
        p?.visit_order ?? 'x',
        p?.day_arrival ?? '',
        p?.day_departure ?? '',
        p?.route_to_next_mode ?? '',
        p?.docking_point_id ?? '',
        portCoords.join(','),
        eventsKey,
      ].join(':');
    }).join('|'),
    [pocs]
  );

  const sortedPocsForEvents = useMemo(
    () => pocs.slice().sort((a, b) => a.visit_order - b.visit_order),
    [pocs]
  );

  const shouldRenderTourRoutePaths = useMemo(
    () => true,
    []
  );

  const eventLabelById = useMemo(() => {
    const labels = new Map();
    sortedPocsForEvents.forEach((poc) => {
      const events = [
        ...sortTourEvents((poc?.events || []).filter((event) => (event?.placement || 'port') === 'port')),
        ...sortTourEvents((poc?.events || []).filter((event) => event?.placement === 'segment')),
      ];
      events.forEach((event, index) => {
        const letter = String.fromCharCode(97 + (index % 26));
        const cycle = Math.floor(index / 26);
        labels.set(String(event.id), `${poc.visit_order}${letter}${cycle > 0 ? cycle + 1 : ''}`);
      });
    });
    return labels;
  }, [sortedPocsForEvents]);

  const getTourEventMarkerLabel = useCallback(
    (event, portVisit = null, fallbackIndex = 0) => {
      if (event?.id != null && eventLabelById.has(String(event.id))) {
        return eventLabelById.get(String(event.id));
      }
      const letter = String.fromCharCode(97 + (fallbackIndex % 26));
      return `${portVisit?.visit_order ?? '?'}${letter}`;
    },
    [eventLabelById]
  );

  const getTourEventTravelSummary = useCallback((event, portVisit, endPort = null) => {
    const anchor = getTourUserPortLocation(portVisit);
    const eventCoords = getEventCoordinates(event, portVisit);
    if (!Array.isArray(anchor?.coordinates) || !eventCoords || !isValidLonLat(eventCoords[0], eventCoords[1])) {
      return null;
    }

    let distanceKm = calculateDistanceKm(
      anchor.coordinates[0],
      anchor.coordinates[1],
      eventCoords[0],
      eventCoords[1]
    );
    const endAnchor = getTourUserPortLocation(endPort);
    if (
      event?.placement === 'segment' &&
      normalizeSegmentEventRouteBehavior(event?.segment_route_behavior) === 'separate' &&
      Array.isArray(endAnchor?.coordinates)
    ) {
      distanceKm += calculateDistanceKm(
        eventCoords[0],
        eventCoords[1],
        endAnchor.coordinates[0],
        endAnchor.coordinates[1]
      );
    }
    const routedDistanceKm = Number(eventRouteDistances[String(event?.id)]);
    if (Number.isFinite(routedDistanceKm) && routedDistanceKm > 0) {
      distanceKm = routedDistanceKm;
    }
    const mode = normalizeEventRouteMode(event?.route_mode, portVisit?.route_to_next_mode || 'road');
    if (distanceKm < 0.05) {
      return {
        mode,
        distanceLabel: 'At port',
        timeLabel: '—',
      };
    }

    return {
      mode,
      distanceLabel: formatDistanceKm(distanceKm, mode, unitPreferences),
      timeLabel: formatEventTravelTime(distanceKm / (EVENT_TRANSPORT_SPEED_KMH[mode] || EVENT_TRANSPORT_SPEED_KMH.road)),
    };
  }, [eventRouteDistances, unitPreferences]);

  const getPortVisitDayKeys = (poc) => {
    if (!poc?.id) return [];
    if (!poc.day_arrival) return [`Day-${poc.visit_order}`];

    const start = new Date(poc.day_arrival);
    const end = new Date(poc.day_departure || poc.day_arrival);
    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return [`Day-${poc.visit_order}`];

    start.setHours(0, 0, 0, 0);
    end.setHours(0, 0, 0, 0);
    const keys = [];
    const cursor = new Date(start);
    while (cursor <= end) {
      keys.push(getDateDayKey(cursor));
      cursor.setDate(cursor.getDate() + 1);
    }
    return keys.length ? keys : [`Day-${poc.visit_order}`];
  };

  // Map: dayKey -> [featureIds], featureId -> dayKey
  const dayKeyToFeatureIds = useMemo(() => {
    const m = new Map();

    pocs.forEach((p) => {
      if (!p?.id) return;

      getPortVisitDayKeys(p).forEach((dayKey) => {
        const arr = m.get(dayKey) || [];
        const markerId = `mp-${p.id}`;
        if (!arr.includes(markerId)) arr.push(markerId);
        m.set(dayKey, arr);
      });

      (p.events || []).forEach((event) => {
        if (event?.id == null) return;
        const eventDayKey = getTourEventDayKey(event, p.day_arrival);
        if (!eventDayKey) return;
        const eventArr = m.get(eventDayKey) || [];
        const eventMarkerId = `mp-event-${event.id}`;
        if (!eventArr.includes(eventMarkerId)) eventArr.push(eventMarkerId);
        m.set(eventDayKey, eventArr);
      });
    });

    return m;
  }, [pocs]);

  const featureIdToDayKey = useMemo(() => {
    const m = new Map();

    pocs.forEach((p) => {
      if (!p?.id) return;

      let key;
      if (p.day_arrival) {
        const date = new Date(p.day_arrival);
        date.setHours(0, 0, 0, 0);
        key = getDateDayKey(date);
      } else {
        key = `Day-${p.visit_order}`;
      }

      m.set(`mp-${p.id}`, key);
      (p.events || []).forEach((event) => {
        const eventDayKey = getTourEventDayKey(event, p.day_arrival);
        if (event?.id != null && eventDayKey) m.set(`mp-event-${event.id}`, eventDayKey);
      });
    });

    return m;
  }, [pocs]);

  const lastExpandedDayRef = useRef(null);
  const autoExpandedTourRef = useRef(null);
  const autoScrolledTourDayRef = useRef(null);
  const initialMarkerFitTourRef = useRef(null);
  const lobbyTimelineScrollRef = useRef(null);
  const eventRouteFeaturesRef = useRef([]);
  const sailedTrackFeaturesRef = useRef([]);
  // Keep this synchronous mirror so an in-flight planned-route request cannot
  // redraw a segment after its sailed geometry has already been rendered.
  const sailedTrackSegmentKeysRef = useRef(new Set());
  const routeBuildRequestIdRef = useRef(0);
  const routeGeometryCacheRef = useRef(new Map());
  const weatherRequestIdRef = useRef(0);

  const refreshRouteDistanceLabels = useCallback(() => {
    clearRouteDistanceLabels?.();
    addRouteDistanceLabels?.([
      ...eventRouteFeaturesRef.current,
      ...sailedTrackFeaturesRef.current,
    ]);
  }, [addRouteDistanceLabels, clearRouteDistanceLabels]);

  const filterValidWeatherData = (arr) =>
    arr?.filter(v => v !== 0 && v !== null && v !== undefined) || [];

  const getValidMinMax = (arr) => {
    const v = filterValidWeatherData(arr);
    if (!v.length) return { min: null, max: null };
    return { min: Math.min(...v), max: Math.max(...v) };
  };

  const getShortWeatherSummary = useCallback((forecast) => {
    const hourly = forecast?.hourly;
    if (!hourly) return null;

    const tempRange = getValidMinMax(hourly.temperature_2m);
    const validPrecip = filterValidWeatherData(hourly.precipitation);
    const validCloudCover = filterValidWeatherData(hourly.cloud_cover);
    const validWindSpeed = filterValidWeatherData(hourly.wind_speed_10m);

    const parts = [];
    if (tempRange.min !== null && tempRange.max !== null) {
      parts.push(`${tempRange.min.toFixed(0)}-${tempRange.max.toFixed(0)}°C`);
    }
    if (validPrecip.length > 0) {
      const totalPrecip = validPrecip.reduce((acc, value) => acc + value, 0);
      if (totalPrecip > 0) parts.push(`${Math.round(totalPrecip)}mm rain`);
    }
    if (validCloudCover.length > 0) {
      const avgCloud = validCloudCover.reduce((acc, value) => acc + value, 0) / validCloudCover.length;
      parts.push(`${Math.round(avgCloud)}% cloud`);
    }
    if (validWindSpeed.length > 0) {
      const maxKmh = Math.max(...validWindSpeed);
      if (maxKmh > 0) {
        parts.push(`${formatSpeedKmh(maxKmh, isSeaCruiseTourType(activeTourToShow?.tour_type) ? 'sea' : 'road', unitPreferences, { digits: 0 })} wind`);
      }
    }

    return parts.length ? parts.join(' · ') : null;
  }, [activeTourToShow?.tour_type, unitPreferences]);

  const normalizeHolidays = (items) => {
    if (!items) return [];
    if (Array.isArray(items)) return items.filter(Boolean);
    if (typeof items === 'object') return Object.values(items).filter(Boolean);
    return [];
  };

  const getHolidayDayKey = (value) => {
    return getUtcDateDayKey(value);
  };

  const getHolidaysForDay = (items, dayKey) =>
    normalizeHolidays(items).filter((holiday) => getHolidayDayKey(holiday?.date) === dayKey);

  const getHolidayBadgeColor = (poc, dayKey) => {
    const dayHolidays = getHolidaysForDay(poc?.holiday_info, dayKey);
    if (!dayHolidays.length) return '#e3f2fd';
    const hasNationalHoliday = dayHolidays.some((holiday) =>
      String(holiday?.holiday_type || '').includes('National')
    );
    return hasNationalHoliday ? '#fdeaea' : '#fff3cd';
  };

  // Select/deselect all markers for a given day
  const selectDayMarkers = useCallback((dayKey) => {
    const ids = dayKeyToFeatureIds.get(dayKey) || [];
    ids.forEach(id => selectMarker(id, { fit: false }));
  }, [dayKeyToFeatureIds, selectMarker]);

  const deselectDayMarkers = useCallback((dayKey) => {
    const ids = dayKeyToFeatureIds.get(dayKey) || [];
    ids.forEach(id => deselectMarker(id));
  }, [dayKeyToFeatureIds, deselectMarker]);

  const fitMapToCoordinates = useCallback((coords, { singleZoom = 12, maxZoom = 12 } = {}) => {
    const validCoords = (coords || [])
      .map((coord) => normalizeLonLat(coord))
      .filter(Boolean);
    if (!validCoords.length) return false;

    const map = getMap?.();
    const view = map?.getView?.();
    if (!view) return false;

    const projection = view.getProjection();
    const projectedCoords = validCoords.map((coord) => fromLonLat(coord, projection));
    if (projectedCoords.length === 1) {
      view.animate({
        center: projectedCoords[0],
        zoom: Math.max(view.getZoom?.() || 0, singleZoom),
        duration: 450,
      });
      return true;
    }

    view.fit(boundingExtent(projectedCoords), {
      padding: [80, 80, 80, 80],
      maxZoom,
      duration: 450,
    });
    return true;
  }, [getMap]);

  const fitMapToPort = useCallback((poc, { zoom = 12 } = {}) => {
    const coords = getTourUserPortCoordinates(poc);
    if (!coords) return;
    fitMapToCoordinates([coords], { singleZoom: zoom });
  }, [fitMapToCoordinates]);

  const fitMapToDayPorts = useCallback((dayData, { includeSegmentEndpoints = false } = {}) => {
    const dayCoords = (dayData?.dayPocs || [])
      .map((poc) => getTourUserPortCoordinates(poc))
      .filter((coord) => coord && isValidLonLat(coord[0], coord[1]));
    const segmentCoords = (dayData?.segmentEntries || [])
      .flatMap((entry) => [entry?.fromPoc, entry?.toPoc])
      .map((poc) => getTourUserPortCoordinates(poc))
      .filter((coord) => coord && isValidLonLat(coord[0], coord[1]));
    const coords = includeSegmentEndpoints || dayCoords.length <= 1
      ? [...dayCoords, ...segmentCoords]
      : dayCoords;
    const deduped = [];
    coords.forEach((coord) => {
      if (!deduped.some((existing) => isSameCoordinate(existing, coord))) deduped.push(coord);
    });
    return fitMapToCoordinates(deduped, { singleZoom: 9, maxZoom: 11 });
  }, [fitMapToCoordinates]);

  // Ensure the given day is the only expanded one, and reflect selection on the map
  const ensureDayExpanded = useCallback((dayKey) => {
    setExpandedDays(prev => {
      const alreadyOpen = prev.has(dayKey) && prev.size === 1;
      if (alreadyOpen) return prev;

      // switch selection from last to new
      if (lastExpandedDayRef.current && lastExpandedDayRef.current !== dayKey) {
        deselectDayMarkers(lastExpandedDayRef.current);
      }
      selectDayMarkers(dayKey);
      lastExpandedDayRef.current = dayKey;

      return new Set([dayKey]); // single expand
    });

    // Optional: scroll into view
    const el = document.getElementById(`day-${dayKey}`);
    el?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });
  }, [selectDayMarkers, deselectDayMarkers, dayKeyToFeatureIds]);

  // Toggle handler for UI card click - expand when collapsed, collapse when expanded
  const toggleDayExpansion = useCallback((dayKey) => {
    setExpandedDays(prev => {
      const isOpen = prev.has(dayKey);
      if (isOpen) {
        // If this day is already open, collapse it
        if (lastExpandedDayRef.current === dayKey) {
          deselectDayMarkers(dayKey);
          lastExpandedDayRef.current = null;
        }
        return new Set(); // close all
      }
      // open this day (and close others)
      if (lastExpandedDayRef.current && lastExpandedDayRef.current !== dayKey) {
        deselectDayMarkers(lastExpandedDayRef.current);
      }
      selectDayMarkers(dayKey);
      lastExpandedDayRef.current = dayKey;
      return new Set([dayKey]);
    });
  }, [selectDayMarkers, deselectDayMarkers]);

  const handleDayCardClick = useCallback((dayData, carousel = false) => {
    if (!dayData?.dayKey) return;
    if (carousel) {
      ensureDayExpanded(dayData.dayKey);
    } else {
      toggleDayExpansion(dayData.dayKey);
    }
    fitMapToDayPorts(dayData);
  }, [ensureDayExpanded, fitMapToDayPorts, toggleDayExpansion]);

  // Subscribe to map marker clicks → expand corresponding day
  useEffect(() => {
    if (!onMarkerClick) return;
    const unsub = onMarkerClick((featureId) => {
      if (!featureId) return;
      const tourMapResource = tourMapMarkerResources.find((resource) => (
        featureId === `mp-${resource.id}` || featureId === resource.id
      ));
      if (tourMapResource) {
        setSelectedTourMapMarker(tourMapResource);
        return;
      }
      const tourFileResource = tourFileMarkerResources.find((resource) => (
        featureId === `mp-${resource.id}` || featureId === resource.id
      ));
      if (tourFileResource) {
        setSelectedTourFileMarker(tourFileResource);
        return;
      }
      if (!isVisible) {
        setIsVisible(true);
      }
      setSelectedMapFeature({ kind: 'marker', featureId });
      
      const dayKey = featureIdToDayKey.get(featureId);
      if (dayKey) {
        ensureDayExpanded(dayKey);
      }
    });
    return unsub;
  }, [onMarkerClick, tourFileMarkerResources, tourMapMarkerResources, featureIdToDayKey, ensureDayExpanded, isVisible, setIsVisible]);

  useEffect(() => {
    if (activeTourToShow?.id && !isVisible) {
      setIsVisible(true);
    }
  }, [activeTourToShow?.id, isVisible, setIsVisible]);

  useEffect(() => {
    let cancelled = false;

    const loadUnitPreferences = async () => {
      try {
        const headers = await getApiHeaders();
        if (!headers.Authorization) return;
        const response = await axios({
          url: buildApiUrl('user-preferences/me/'),
          method: 'GET',
          headers,
        });
        const preferences = response.data?.preferences || {};
        if (!cancelled) {
          setUnitPreferences({
            distance_unit: preferences.distance_unit || 'km',
            speed_unit: preferences.speed_unit || 'kmh',
          });
        }
      } catch {
        if (!cancelled) {
          setUnitPreferences({ distance_unit: 'km', speed_unit: 'kmh' });
        }
      }
    };

    loadUnitPreferences();
    return () => {
      cancelled = true;
      setIsRouteGeometryLoading(false);
    };
  }, [getApiHeaders]);

  const renderTrackingLocationMarker = useCallback(({ removeExisting = true } = {}) => {
    if (!isMapReady()) return null;
    const position = currentTourPosition;
    const lon = Number(position?.longitude);
    const lat = Number(position?.latitude);

    if (removeExisting && trackingLocationFeatureRef.current && typeof removeFeature === 'function') {
      removeFeature(trackingLocationFeatureRef.current);
      trackingLocationFeatureRef.current = null;
    }

    if (!Number.isFinite(lon) || !Number.isFinite(lat)) return null;

    const features = addMappoints([{
      id: 'tour-user-current-location',
      markerLabel: 'ship',
      markerLabelType: 'currentShipLocation',
      center: { type: 'Point', coordinates: [lon, lat] },
      popup: position?.source === 'manual' ? (position?.label || 'Manual ship location') : 'Current ship location',
    }]);
    trackingLocationFeatureRef.current = features?.[0] || null;
    return trackingLocationFeatureRef.current;
  }, [addMappoints, currentTourPosition, isMapReady, removeFeature]);

  const focusTrackingLocation = useCallback(() => {
    const position = currentTourPosition;
    const lon = Number(position?.longitude);
    const lat = Number(position?.latitude);
    if (!Number.isFinite(lon) || !Number.isFinite(lat)) return;

    const map = getMap?.();
    const view = map?.getView?.();
    if (!view) return;

    view.animate({
      center: fromLonLat([lon, lat], view.getProjection()),
      zoom: Math.max(view.getZoom?.() || 0, 12),
      duration: 450,
    });
  }, [currentTourPosition, getMap]);

  // Build markers for all ports
  useEffect(() => {
    if (!activeTourToShow?.ports_of_call?.length) return;
    if (!shouldRenderMapContent) return;
    if (!isMapReady()) return;
    if (!isVisible) return;

    if (typeof clearLayer === 'function') {
      clearLayer('markers');
      trackingLocationFeatureRef.current = null;
    }

    const mappoints = activeTourToShow.ports_of_call
      .flatMap((p) => {
        const center = getTourUserPortLocation(p);
        const portMarker = center?.coordinates ? {
          id: p.id,
          number: p.visit_order,
          center,
          popup: p.poc_info.name,
        } : null;
        const eventMarkers = [
          ...sortTourEvents((p?.events || []).filter((event) => (event?.placement || 'port') === 'port')),
          ...sortTourEvents((p?.events || []).filter((event) => event?.placement === 'segment')),
        ]
          .map((event, eventIndex) => {
            const coords = getEventCoordinates(event, p);
            if (!coords || !isValidLonLat(coords[0], coords[1])) return null;
            if (isEventAtPortLocation(coords, p)) return null;
            const label = getTourEventMarkerLabel(event, p, eventIndex);
            return {
              id: `event-${event.id}`,
              number: label,
              markerLabel: label,
              markerLabelType: 'tourEvent',
              eventDisplacementIndex: eventIndex,
              center: { type: 'Point', coordinates: coords },
              popup: getTourEventName(event),
            };
          })
          .filter(Boolean);
        return [portMarker, ...eventMarkers].filter(Boolean);
      })
      .filter(Boolean);


    if (mappoints.length) {
      addMappoints(mappoints);
    }
    renderTrackingLocationMarker({ removeExisting: false });

    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTourToShow?.id, pocsKey, isVisible, renderTrackingLocationMarker, shouldRenderMapContent]);

  useEffect(() => {
    if (!shouldRenderMapContent || !isVisible || !isMapReady()) {
      if (typeof clearLayer === 'function') clearLayer('mapListMarkers');
      return;
    }

    if (typeof clearLayer === 'function') clearLayer('mapListMarkers');
    const fileMarkersVisible = isLobbyGuestView && lobbyTab === 'files';
    const resources = [
      ...tourMapMarkerResources.map((resource) => ({
        id: resource.id,
        center: { type: 'Point', coordinates: resource.coords },
        name: resource.name,
        popup: resource.name,
        isMapListMarker: true,
        isMapMarker: true,
        disableMarkerDeclutter: true,
        minZoom: 14,
      })),
      ...(fileMarkersVisible ? tourFileMarkerResources.map((resource) => ({
        id: resource.id,
        center: { type: 'Point', coordinates: resource.coordinates },
        name: resource.name,
        popup: resource.locationName ? `${resource.name} - ${resource.locationName}` : resource.name,
        isMapListMarker: true,
        isMapMarker: true,
        mapMarkerKind: 'tourFile',
        disableMarkerDeclutter: true,
        minZoom: 4,
      })) : []),
    ];
    if (!resources.length) return;

    addMappoints(resources);
  }, [addMappoints, clearLayer, isLobbyGuestView, isMapReady, isVisible, lobbyTab, shouldRenderMapContent, tourFileMarkerResources, tourMapMarkerResources]);

  useEffect(() => {
    if (!activeTourToShow?.id || !isVisible) {
      setTourTrackingLocation(null);
      setTourTrackingLocationError('');
      if (trackingLocationFeatureRef.current && typeof removeFeature === 'function') {
        removeFeature(trackingLocationFeatureRef.current);
        trackingLocationFeatureRef.current = null;
      }
      return undefined;
    }

    let cancelled = false;
    const loadTrackingLocation = async () => {
      try {
        const data = await fetchTourTrackingLocation(activeTourToShow.id, getApiHeaders);
        if (!cancelled) {
          setTourTrackingLocation(data || null);
          setTourTrackingLocationError('');
          const position = data?.position;
          if (data?.source === 'manual' || position?.source === 'manual') {
            return;
          }
          const coords = normalizeLonLat([position?.longitude, position?.latitude]);
          const recordedAt = position?.recorded_at || position?.received_at || new Date().toISOString();
          const recordedTime = new Date(recordedAt).getTime();
          if (coords && Number.isFinite(recordedTime)) {
            setLiveTrackingPositions((previous) => {
              const minTime = Date.now() - LIVE_TRACKING_TAIL_WINDOW_MS;
              const next = previous
                .filter((point) => new Date(point.recordedAt).getTime() >= minTime)
                .filter((point) => point.id !== position?.id);
              const lastPoint = next[next.length - 1];
              if (!lastPoint || !isSameCoordinate(lastPoint.coords, coords) || lastPoint.recordedAt !== recordedAt) {
                next.push({ id: position?.id ?? recordedAt, coords, recordedAt });
              }
              return next.sort((a, b) => new Date(a.recordedAt).getTime() - new Date(b.recordedAt).getTime());
            });
          }
        }
      } catch (error) {
        if (!cancelled) {
          setTourTrackingLocation(null);
          setTourTrackingLocationError(error?.response?.data?.detail || 'Current location is unavailable.');
        }
      }
    };

    const initialTrackingDelay = isMobileGuestView && mobileTab === 'today' && activeTourToShow?.mobile_initial?.current_ship
      ? 5000
      : 0;
    const initialTimeoutId = window.setTimeout(loadTrackingLocation, initialTrackingDelay);
    const intervalId = window.setInterval(loadTrackingLocation, 60000);
    return () => {
      cancelled = true;
      window.clearTimeout(initialTimeoutId);
      window.clearInterval(intervalId);
    };
  }, [activeTourToShow?.id, activeTourToShow?.mobile_initial?.current_ship, getApiHeaders, isMobileGuestView, isVisible, mobileTab, removeFeature]);

  useEffect(() => {
    renderTrackingLocationMarker();
  }, [renderTrackingLocationMarker, tourTrackingLocation]);

  const fetchRouteGeometry = useCallback(async ({ startCoords, endCoords, routeMode }) => {
    const mode = normalizeRouteGeometryMode(routeMode, 'road');
    const simplifyRoutes = false;
    const simplificationBias = 1.0;
    const smoothingIterations = 0;
    const cacheKey = [
      mode,
      'raw',
      simplificationBias,
      smoothingIterations,
      Number(startCoords[0]).toFixed(6),
      Number(startCoords[1]).toFixed(6),
      Number(endCoords[0]).toFixed(6),
      Number(endCoords[1]).toFixed(6),
    ].join(':');

    let pendingRoute = routeGeometryCacheRef.current.get(cacheKey);
    if (!pendingRoute) {
      pendingRoute = axios({
        url: buildApiUrl('route_geometry/'),
        method: 'post',
        data: {
          start_lon: startCoords[0],
          start_lat: startCoords[1],
          end_lon: endCoords[0],
          end_lat: endCoords[1],
          route_mode: mode,
          simplify: simplifyRoutes,
          route_simplification_bias: simplificationBias,
          smoothing_iterations: smoothingIterations,
        },
        headers: await getApiHeaders(),
      })
        .then((response) => response.data)
        .catch((error) => {
          routeGeometryCacheRef.current.delete(cacheKey);
          throw error;
        });
      routeGeometryCacheRef.current.set(cacheKey, pendingRoute);
    }

    return pendingRoute;
  }, [getApiHeaders]);

  const addRouteGeometryFeatures = useCallback(async ({
    lineFeatures,
    distanceByEventId,
    startCoords,
    endCoords,
    routeMode,
    properties,
    distanceEventId = null,
    allowFallback = true,
  }) => {
    try {
      const routeData = await fetchRouteGeometry({ startCoords, endCoords, routeMode });
      lineFeatures.push(...buildRouteGeoJsonFeatures(routeData?.route_geojson, properties));
      const distanceKm = Number(routeData?.display_length_km ?? routeData?.distance_km ?? routeData?.length_km);
      if (distanceEventId != null && Number.isFinite(distanceKm) && distanceKm > 0) {
        const key = String(distanceEventId);
        distanceByEventId[key] = Number(distanceByEventId[key] || 0) + distanceKm;
      }
      return Number.isFinite(distanceKm) && distanceKm > 0
        ? distanceKm
        : calculateDistanceKm(startCoords[0], startCoords[1], endCoords[0], endCoords[1]);
    } catch (error) {
      if (!allowFallback) {
        throw error;
      }
      console.warn('Falling back to straight event route:', error);
      const fallbackDistanceKm = calculateDistanceKm(
        startCoords[0],
        startCoords[1],
        endCoords[0],
        endCoords[1]
      );
      lineFeatures.push({
        type: 'Feature',
        geometry: {
          type: 'LineString',
          coordinates: [startCoords, endCoords],
        },
        properties: {
          ...(properties || {}),
          routeGeometryFallback: true,
        },
      });
      if (distanceEventId != null) {
        const key = String(distanceEventId);
        distanceByEventId[key] = Number(distanceByEventId[key] || 0) + fallbackDistanceKm;
      }
      return fallbackDistanceKm;
    }
  }, [fetchRouteGeometry]);

  const clearRenderedRouteFeatures = useCallback(() => {
    eventRouteFeaturesRef.current.forEach((feature) => {
      removeFeature?.(feature, 'geoJson');
    });
    eventRouteFeaturesRef.current = [];
    refreshRouteDistanceLabels();
    setEventRouteDistances({});
  }, [refreshRouteDistanceLabels, removeFeature]);

  const commitRouteFeatureGroup = useCallback(({ geoJsonFeatures, groupId, distanceByEventId = {} }) => {
    if (!groupId || !geoJsonFeatures?.length || typeof addGeoJson !== 'function') return;
    const includesCurrentSegmentPath = geoJsonFeatures.some(
      (feature) => Boolean(feature?.properties?.tourCurrentSegmentPath)
    );
    const isSupersededBySailedTrack = !includesCurrentSegmentPath && geoJsonFeatures.some((feature) => {
      const properties = feature?.properties || {};
      return (
        properties.tourCruisingPath &&
        !properties.tourCurrentSegmentPath &&
        sailedTrackSegmentKeysRef.current.has(properties.tourCruisingPathSegmentKey)
      );
    });
    if (isSupersededBySailedTrack) return;
    const addedFeatures = addGeoJson({
      type: 'FeatureCollection',
      features: geoJsonFeatures,
    });
    const renderedFeatures = Array.isArray(addedFeatures) ? addedFeatures : [];
    const retainedFeatures = [];
    eventRouteFeaturesRef.current.forEach((feature) => {
      if (feature?.get?.('routePathGroupId') === groupId) {
        removeFeature?.(feature, 'geoJson');
      } else {
        retainedFeatures.push(feature);
      }
    });
    eventRouteFeaturesRef.current = [...retainedFeatures, ...renderedFeatures];
    refreshRouteDistanceLabels();
    if (Object.keys(distanceByEventId).length) {
      setEventRouteDistances((previous) => ({
        ...previous,
        ...distanceByEventId,
      }));
    }
  }, [addGeoJson, refreshRouteDistanceLabels, removeFeature]);

  const clearRenderedSailedTracks = useCallback(() => {
    sailedTrackFeaturesRef.current.forEach((feature) => {
      removeFeature?.(feature, 'geoJson');
    });
    sailedTrackFeaturesRef.current = [];
    sailedTrackSegmentKeysRef.current = new Set();
    refreshRouteDistanceLabels();
    setSailedTrackSegmentKeys(new Set());
    setSailedTrackEndBySegment(new Map());
    setSailedTrackDistanceBySegment(new Map());
  }, [refreshRouteDistanceLabels, removeFeature]);

  const replacePlannedSegmentsWithSailedTracks = useCallback((segmentKeys) => {
    if (!segmentKeys?.size) return;

    const retainedFeatures = [];
    eventRouteFeaturesRef.current.forEach((feature) => {
      const segmentKey = feature?.get?.('tourCruisingPathSegmentKey');
      const isCurrentRemainingPath = Boolean(feature?.get?.('tourCurrentSegmentPath'));
      if (segmentKey && segmentKeys.has(segmentKey) && !isCurrentRemainingPath) {
        removeFeature?.(feature, 'geoJson');
      } else {
        retainedFeatures.push(feature);
      }
    });
    eventRouteFeaturesRef.current = retainedFeatures;
  }, [removeFeature]);

  useEffect(() => {
    if (!activeTourToShow?.id || !isVisible || !shouldRenderMapContent || typeof addGeoJson !== 'function' || !isMapReady()) {
      setIsSailedTrackLoading(false);
      clearRenderedSailedTracks();
      return undefined;
    }

    let cancelled = false;
    const hasActiveCruisingSegment = Boolean(
      currentTrackingCoords
      && pocs.slice(0, -1).some((portVisit, index) => (
        isNowWithinSegmentWindow(portVisit, pocs[index + 1])
      ))
    );
    const loadSailedTracks = async () => {
      setIsSailedTrackLoading(true);
      try {
        const data = await fetchTourTrackingSegmentTracks(activeTourToShow.id, getApiHeaders);
        if (cancelled) return;
        const trackFeatures = (data?.tracks || [])
          .filter((track) => track?.geometry)
          .map((track) => {
            const displayGeometry = getSailedTrackDisplayGeometry(track);
            if (!displayGeometry) return null;
            const displayDistanceKm = getGeometryDistanceKm(displayGeometry);
            const fallbackDistanceKm = Number(track.distance_km);
            const distanceKm = displayDistanceKm > 0 ? displayDistanceKm : fallbackDistanceKm;
            const routeMode = normalizeRouteGeometryMode(track?.metadata?.route_mode, 'sea');
            const distanceLabel = Number.isFinite(distanceKm) && distanceKm > 0
              ? formatDistanceKm(distanceKm, routeMode, unitPreferences)
              : '';
            return {
              type: 'Feature',
              geometry: displayGeometry,
            properties: {
              kind: 'sailedTrack',
              tourCruisingPath: true,
              tourCruisingPathSegmentKey: `${track.from_port_id}-${track.to_port_id}`,
              tourCruisingPathDayKey: segmentEndDayKeyByPair.get(`${track.from_port_id}-${track.to_port_id}`) || null,
              tourCruisingPathRouteMode: routeMode,
              segmentTrackId: track.id,
              fromPortId: track.from_port_id,
              toPortId: track.to_port_id,
                status: track.status,
                distanceKm,
                routeDistanceLabel: distanceLabel,
                routeDistanceLabelId: `sailed-segment-${track.from_port_id}-${track.to_port_id}`,
              },
            };
          })
          .filter(Boolean);
        const trackSegmentKeys = new Set();
        const trackEndBySegment = new Map();
        const trackDistanceBySegment = new Map();
        (data?.tracks || []).forEach((track) => {
          if (!track?.geometry || !track?.from_port_id || !track?.to_port_id) return;
          const displayGeometry = getSailedTrackDisplayGeometry(track);
          if (!displayGeometry) return;
          const key = `${track.from_port_id}-${track.to_port_id}`;
          trackSegmentKeys.add(key);
          const lastCoords = getLastGeometryCoordinate(displayGeometry);
          if (lastCoords) trackEndBySegment.set(key, lastCoords);
          const geometryDistanceKm = getGeometryDistanceKm(displayGeometry);
          const reportedDistanceKm = Number(track.distance_km);
          const distanceKm = geometryDistanceKm > 0 ? geometryDistanceKm : reportedDistanceKm;
          if (Number.isFinite(distanceKm) && distanceKm > 0) {
            trackDistanceBySegment.set(key, distanceKm);
          }
        });
        sailedTrackSegmentKeysRef.current = trackSegmentKeys;
        setSailedTrackSegmentKeys(trackSegmentKeys);
        setSailedTrackEndBySegment(trackEndBySegment);
        setSailedTrackDistanceBySegment(trackDistanceBySegment);
        if (!trackFeatures.length) {
          clearRenderedSailedTracks();
          return;
        }
        const features = addGeoJson({
          type: 'FeatureCollection',
          features: trackFeatures,
        });
        // Add sailed geometry first, then remove the planned/provisional owner
        // in the same synchronous turn. Leaflet never gets a paint with both.
        replacePlannedSegmentsWithSailedTracks(trackSegmentKeys);
        sailedTrackFeaturesRef.current.forEach((feature) => {
          removeFeature?.(feature, 'geoJson');
        });
        sailedTrackFeaturesRef.current = Array.isArray(features) ? features : [];
        refreshRouteDistanceLabels();
      } catch (_error) {
        // Retain the last valid sailed path on a transient polling failure.
      } finally {
        if (!cancelled) setIsSailedTrackLoading(false);
      }
    };

    loadSailedTracks();
    const intervalId = window.setInterval(
      loadSailedTracks,
      hasActiveCruisingSegment ? 60000 : 300000
    );
    return () => {
      cancelled = true;
      setIsSailedTrackLoading(false);
      window.clearInterval(intervalId);
      clearRenderedSailedTracks();
    };
  }, [activeTourToShow?.id, addGeoJson, clearRenderedSailedTracks, currentTrackingCoordsKey, getApiHeaders, isMapReady, isVisible, pocs, refreshRouteDistanceLabels, replacePlannedSegmentsWithSailedTracks, segmentEndDayKeyByPair, shouldRenderMapContent, unitPreferences]);

  useEffect(() => {
    const requestId = routeBuildRequestIdRef.current + 1;
    routeBuildRequestIdRef.current = requestId;

    if (!activeTourToShow?.ports_of_call?.length) {
      setIsRouteGeometryLoading(false);
      clearRenderedRouteFeatures();
      return undefined;
    }
    if (!shouldRenderMapContent) {
      setIsRouteGeometryLoading(false);
      clearRenderedRouteFeatures();
      return undefined;
    }
    if (!isMapReady()) {
      setIsRouteGeometryLoading(false);
      clearRenderedRouteFeatures();
      return undefined;
    }
    if (!isVisible) {
      setIsRouteGeometryLoading(false);
      clearRenderedRouteFeatures();
      return undefined;
    }
    if (!shouldRenderTourRoutePaths) {
      setIsRouteGeometryLoading(false);
      clearRenderedRouteFeatures();
      return undefined;
    }
    if (typeof addGeoJson !== 'function') {
      setIsRouteGeometryLoading(false);
      clearRenderedRouteFeatures();
      return undefined;
    }

    let cancelled = false;
    const orderedPorts = activeTourToShow.ports_of_call.slice().sort((a, b) => a.visit_order - b.visit_order);
    const provisionalLineFeatures = [];
    const provisionalDistanceByEventId = {};

    for (let portIndex = 0; portIndex < orderedPorts.length - 1; portIndex += 1) {
      const portVisit = orderedPorts[portIndex];
      const nextPort = orderedPorts[portIndex + 1];
      const anchor = getTourUserPortLocation(portVisit);
      const nextAnchor = getTourUserPortLocation(nextPort);
      if (!Array.isArray(anchor?.coordinates) || !Array.isArray(nextAnchor?.coordinates)) continue;

      const segmentKey = `${portVisit.id}-${nextPort.id}`;
      const segment = navigationSegmentsByPair.get(segmentKey);
      const segmentRouteMode = resolveTourSegmentRouteMode(portVisit, nextPort, segment);
      const isCurrentCruisingSegment = Boolean(
        currentTrackingCoords &&
        isNowWithinSegmentWindow(portVisit, nextPort)
      );
      const shouldUseSailedTrackInstead = (
        sailedTrackSegmentKeys.has(segmentKey) &&
        !isCurrentCruisingSegment &&
        isSegmentPastWindow(portVisit, nextPort)
      );
      if (shouldUseSailedTrackInstead) {
        continue;
      }

      if (isCurrentCruisingSegment && currentTrackingCoords) {
        const routePathGroupId = `segment-${portVisit.id}-${nextPort.id}-remaining`;
        const remainingLabelStartIndex = provisionalLineFeatures.length;
        const remainingDistanceKm = addStraightRouteFeature({
          lineFeatures: provisionalLineFeatures,
          distanceByEventId: provisionalDistanceByEventId,
          startCoords: currentTrackingCoords,
          endCoords: nextAnchor.coordinates,
            properties: {
              kind: 'flightPath',
              ...getCruisingPathFeatureProperties(portVisit, nextPort, segmentRouteMode),
              ...getPlannedSegmentLineStyle(segmentRouteMode, { current: true }),
              legIndex: 3,
              tourCurrentSegmentPath: true,
            tourCurrentSegmentPart: 'remaining',
            provisionalRouteGeometry: true,
            routePathGroupId,
          },
        });
        applyRouteDistanceLabel(provisionalLineFeatures, remainingLabelStartIndex, {
          id: `segment-${portVisit.id}-${nextPort.id}-remaining`,
          label: formatDistanceKm(remainingDistanceKm, segmentRouteMode, unitPreferences),
        });
        continue;
      }

      const routePathGroupId = `segment-${portVisit.id}-${nextPort.id}`;
      const segmentLabelStartIndex = provisionalLineFeatures.length;
      const directDistanceKm = addStraightRouteFeature({
        lineFeatures: provisionalLineFeatures,
        distanceByEventId: provisionalDistanceByEventId,
        startCoords: anchor.coordinates,
        endCoords: nextAnchor.coordinates,
        properties: {
          kind: 'flightPath',
          ...getCruisingPathFeatureProperties(portVisit, nextPort, segmentRouteMode),
          ...getPlannedSegmentLineStyle(segmentRouteMode),
          legIndex: 3,
          tourMainSegmentPath: true,
          provisionalRouteGeometry: true,
          routePathGroupId,
        },
      });
      const summaryDistanceKm = Number(segment?.distance_km);
      const labelDistanceKm = Number.isFinite(summaryDistanceKm) && summaryDistanceKm > 0
        ? summaryDistanceKm
        : directDistanceKm;
      applyRouteDistanceLabel(provisionalLineFeatures, segmentLabelStartIndex, {
        id: `segment-${portVisit.id}-${nextPort.id}`,
        label: formatDistanceKm(labelDistanceKm, segmentRouteMode, unitPreferences),
      });
    }

    const hasRenderedRouteFeatures = eventRouteFeaturesRef.current.length > 0;
    if (provisionalLineFeatures.length && !hasRenderedRouteFeatures) {
      const previousFeatures = eventRouteFeaturesRef.current;
      const provisionalFeatures = addGeoJson({
        type: 'FeatureCollection',
        features: provisionalLineFeatures,
      });
      previousFeatures.forEach((feature) => {
        removeFeature?.(feature, 'geoJson');
      });
      eventRouteFeaturesRef.current = Array.isArray(provisionalFeatures) ? provisionalFeatures : [];
      refreshRouteDistanceLabels();
      setEventRouteDistances(provisionalDistanceByEventId);
    } else if (!provisionalLineFeatures.length && hasRenderedRouteFeatures) {
      eventRouteFeaturesRef.current.forEach((feature) => {
        removeFeature?.(feature, 'geoJson');
      });
      eventRouteFeaturesRef.current = [];
      refreshRouteDistanceLabels();
      setEventRouteDistances({});
    }
    setIsRouteGeometryLoading(Boolean(provisionalLineFeatures.length));

    (async () => {
      const lineFeatures = [];
      const distanceByEventId = {};

      for (let portIndex = 0; portIndex < orderedPorts.length; portIndex += 1) {
        const portVisit = orderedPorts[portIndex];
        const anchor = getTourUserPortLocation(portVisit);
        if (!Array.isArray(anchor?.coordinates)) continue;

        const portEvents = sortTourEvents((portVisit.events || []).filter((event) => (event?.placement || 'port') === 'port'));
        for (const event of portEvents) {
          const eventCoords = getEventCoordinates(event, portVisit);
          if (!eventCoords || !isValidLonLat(eventCoords[0], eventCoords[1])) continue;
          const distanceKm = calculateDistanceKm(
            anchor.coordinates[0],
            anchor.coordinates[1],
            eventCoords[0],
            eventCoords[1]
          );
          if (distanceKm < 0.05) continue;
          const eventLabelStartIndex = lineFeatures.length;
          const routeDistanceKm = await addRouteGeometryFeatures({
            lineFeatures,
            distanceByEventId,
            startCoords: anchor.coordinates,
            endCoords: eventCoords,
            routeMode: event?.route_mode || 'road',
            distanceEventId: event.id,
            properties: {
              kind: 'flightPath',
              dashed: true,
              color: '#f97316',
              legIndex: 3,
              tourEventId: event.id,
            },
          });
          applyRouteDistanceLabel(lineFeatures, eventLabelStartIndex, {
            id: `event-${event.id}`,
            label: formatDistanceKm(routeDistanceKm, event?.route_mode || 'road', unitPreferences),
          });
        }

        const nextPort = orderedPorts[portIndex + 1];
        if (!nextPort) continue;
        const nextAnchor = getTourUserPortLocation(nextPort);
        if (!Array.isArray(nextAnchor?.coordinates)) continue;

        const segmentKey = `${portVisit.id}-${nextPort.id}`;
        const segment = navigationSegmentsByPair.get(segmentKey);
        const segmentRouteMode = resolveTourSegmentRouteMode(portVisit, nextPort, segment);
        const isCurrentCruisingSegment = Boolean(
          currentTrackingCoords &&
          isNowWithinSegmentWindow(portVisit, nextPort)
        );
        const shouldUseSailedTrackInstead = (
          sailedTrackSegmentKeys.has(segmentKey) &&
          !isCurrentCruisingSegment &&
          isSegmentPastWindow(portVisit, nextPort)
        );
        if (shouldUseSailedTrackInstead) {
          continue;
        }

        const waypointEvents = sortTourEvents(
          (portVisit.events || []).filter(
            (event) =>
              event?.placement === 'segment' &&
              String(event.segment_end_port_of_call_id) === String(nextPort.id) &&
              normalizeSegmentEventRouteBehavior(event?.segment_route_behavior) === 'waypoint'
          )
        );

        if (isCurrentCruisingSegment) {
          const routePathGroupId = `segment-${portVisit.id}-${nextPort.id}-remaining`;
          const routePathGroupStartIndex = lineFeatures.length;
          const remainingDistanceKm = calculateDistanceKm(
            currentTrackingCoords[0],
            currentTrackingCoords[1],
            nextAnchor.coordinates[0],
            nextAnchor.coordinates[1]
          );

          let routedRemainingDistanceKm = remainingDistanceKm;
          const hasSailedCurrentSegment = sailedTrackSegmentKeys.has(segmentKey);
          const segmentStart = parseSegmentBoundaryDate(portVisit?.day_departure || portVisit?.day_arrival, 12);
          const segmentEnd = parseSegmentBoundaryDate(nextPort?.day_arrival || nextPort?.day_departure, 12);

          if (remainingDistanceKm < 0.05) {
            throw new Error('Current tracking location is too close to a segment endpoint for a two-leg live route update.');
          }

          if (hasSailedCurrentSegment) {
            const liveTailCoords = buildLiveTailCoordinates({
              backendEndCoords: sailedTrackEndBySegment.get(segmentKey),
              livePositions: liveTrackingPositions,
              currentCoords: currentTrackingCoords,
              segmentStart,
              segmentEnd,
              routeMode: segmentRouteMode,
            });
            if (liveTailCoords.length >= 2) {
              lineFeatures.push({
                type: 'Feature',
                geometry: {
                  type: 'LineString',
                  coordinates: liveTailCoords,
                },
                properties: {
                  kind: 'sailedTrack',
                  ...getCruisingPathFeatureProperties(portVisit, nextPort, segmentRouteMode),
                  liveTrackingTail: true,
                  fromPortId: portVisit.id,
                  toPortId: nextPort.id,
                  routePathGroupId,
                },
              });
            }
          }

          if (remainingDistanceKm >= 0.05) {
            const remainingLabelStartIndex = lineFeatures.length;
            routedRemainingDistanceKm = await addRouteGeometryFeatures({
              lineFeatures,
              distanceByEventId,
              startCoords: currentTrackingCoords,
              endCoords: nextAnchor.coordinates,
              routeMode: segmentRouteMode,
              allowFallback: false,
              properties: {
                kind: 'flightPath',
                ...getCruisingPathFeatureProperties(portVisit, nextPort, segmentRouteMode),
                ...getPlannedSegmentLineStyle(segmentRouteMode, { current: true }),
                legIndex: 3,
                tourCurrentSegmentPath: true,
                tourCurrentSegmentPart: 'remaining',
                routePathGroupId,
              },
            });
            applyRouteDistanceLabel(lineFeatures, remainingLabelStartIndex, {
              id: `segment-${portVisit.id}-${nextPort.id}-remaining`,
              label: formatDistanceKm(routedRemainingDistanceKm, segmentRouteMode, unitPreferences),
            });
          }
          if (!cancelled && routeBuildRequestIdRef.current === requestId) {
            commitRouteFeatureGroup({
              geoJsonFeatures: lineFeatures.slice(routePathGroupStartIndex),
              groupId: routePathGroupId,
              distanceByEventId,
            });
            setRoutedCurrentRemaining({
              segmentKey,
              coordinateKey: currentTrackingCoordsKey,
              distanceKm: routedRemainingDistanceKm,
            });
          }
        } else if (waypointEvents.length) {
          const routePathGroupId = `segment-${portVisit.id}-${nextPort.id}`;
          const routePathGroupStartIndex = lineFeatures.length;
          const waypointCoords = waypointEvents
            .map((event) => ({
              event,
              coords: getEventCoordinates(event, portVisit),
            }))
            .filter(({ coords }) => coords && isValidLonLat(coords[0], coords[1]));

          let previousCoords = anchor.coordinates;
          let cumulativeDistanceKm = 0;
          const segmentLabelStartIndex = lineFeatures.length;
          for (const { event, coords } of waypointCoords) {
            const legDistanceKm = await addRouteGeometryFeatures({
              lineFeatures,
              distanceByEventId,
              startCoords: previousCoords,
              endCoords: coords,
              routeMode: segmentRouteMode,
              properties: {
                kind: 'flightPath',
                ...getCruisingPathFeatureProperties(portVisit, nextPort, segmentRouteMode),
                ...getPlannedSegmentLineStyle(segmentRouteMode),
                legIndex: 3,
                tourSegmentWaypointPath: true,
                tourEventId: event.id,
                routePathGroupId,
              },
            });
            cumulativeDistanceKm += Number(legDistanceKm || 0);
            distanceByEventId[String(event.id)] = cumulativeDistanceKm;
            previousCoords = coords;
          }
          const finalLegDistanceKm = await addRouteGeometryFeatures({
            lineFeatures,
            distanceByEventId,
            startCoords: previousCoords,
            endCoords: nextAnchor.coordinates,
            routeMode: segmentRouteMode,
            properties: {
              kind: 'flightPath',
              ...getCruisingPathFeatureProperties(portVisit, nextPort, segmentRouteMode),
              ...getPlannedSegmentLineStyle(segmentRouteMode),
              legIndex: 3,
              tourSegmentWaypointPath: true,
              routePathGroupId,
            },
          });
          cumulativeDistanceKm += Number(finalLegDistanceKm || 0);
          const summaryDistanceKm = Number(segment?.distance_km);
          const labelDistanceKm = Number.isFinite(cumulativeDistanceKm) && cumulativeDistanceKm > 0
            ? cumulativeDistanceKm
            : summaryDistanceKm;
          applyRouteDistanceLabel(lineFeatures, segmentLabelStartIndex, {
            id: `segment-${portVisit.id}-${nextPort.id}`,
            label: formatDistanceKm(labelDistanceKm, segmentRouteMode, unitPreferences),
          });
          if (!cancelled && routeBuildRequestIdRef.current === requestId) {
            commitRouteFeatureGroup({
              geoJsonFeatures: lineFeatures.slice(routePathGroupStartIndex),
              groupId: routePathGroupId,
              distanceByEventId,
            });
          }
        } else {
          const routePathGroupId = `segment-${portVisit.id}-${nextPort.id}`;
          const routePathGroupStartIndex = lineFeatures.length;
          const segmentLabelStartIndex = lineFeatures.length;
          const directDistanceKm = await addRouteGeometryFeatures({
            lineFeatures,
            distanceByEventId,
            startCoords: anchor.coordinates,
            endCoords: nextAnchor.coordinates,
            routeMode: segmentRouteMode,
            properties: {
              kind: 'flightPath',
              ...getCruisingPathFeatureProperties(portVisit, nextPort, segmentRouteMode),
              ...getPlannedSegmentLineStyle(segmentRouteMode),
              legIndex: 3,
              tourMainSegmentPath: true,
              routePathGroupId,
            },
          });
          const summaryDistanceKm = Number(segment?.distance_km);
          const labelDistanceKm = Number.isFinite(directDistanceKm) && directDistanceKm > 0
            ? directDistanceKm
            : summaryDistanceKm;
          applyRouteDistanceLabel(lineFeatures, segmentLabelStartIndex, {
            id: `segment-${portVisit.id}-${nextPort.id}`,
            label: formatDistanceKm(labelDistanceKm, segmentRouteMode, unitPreferences),
          });
          if (!cancelled && routeBuildRequestIdRef.current === requestId) {
            commitRouteFeatureGroup({
              geoJsonFeatures: lineFeatures.slice(routePathGroupStartIndex),
              groupId: routePathGroupId,
              distanceByEventId,
            });
          }
        }

        const separateEvents = sortTourEvents(
          (portVisit.events || []).filter(
            (event) =>
              event?.placement === 'segment' &&
              String(event.segment_end_port_of_call_id) === String(nextPort.id) &&
              normalizeSegmentEventRouteBehavior(event?.segment_route_behavior) === 'separate'
          )
        );

        for (const event of separateEvents) {
          const eventCoords = getEventCoordinates(event, portVisit);
          if (!eventCoords || !isValidLonLat(eventCoords[0], eventCoords[1])) continue;
          const routeMode = normalizeRouteGeometryMode(event?.route_mode, segmentRouteMode);
          const eventLabelStartIndex = lineFeatures.length;
          const outboundDistanceKm = await addRouteGeometryFeatures({
            lineFeatures,
            distanceByEventId,
            startCoords: anchor.coordinates,
            endCoords: eventCoords,
            routeMode,
            distanceEventId: event.id,
            properties: {
              kind: 'flightPath',
              dashed: true,
              color: '#f97316',
              legIndex: 3,
              tourSegmentSeparateEventPath: true,
              tourEventId: event.id,
            },
          });
          const inboundDistanceKm = await addRouteGeometryFeatures({
            lineFeatures,
            distanceByEventId,
            startCoords: eventCoords,
            endCoords: nextAnchor.coordinates,
            routeMode,
            distanceEventId: event.id,
            properties: {
              kind: 'flightPath',
              dashed: true,
              color: '#f97316',
              legIndex: 3,
              tourSegmentSeparateEventPath: true,
              tourEventId: event.id,
            },
          });
          const eventDistanceKm = Number(distanceByEventId[String(event.id)])
            || (Number(outboundDistanceKm || 0) + Number(inboundDistanceKm || 0));
          applyRouteDistanceLabel(lineFeatures, eventLabelStartIndex, {
            id: `event-${event.id}`,
            label: formatDistanceKm(eventDistanceKm, routeMode, unitPreferences),
          });
        }
      }

      if (cancelled || routeBuildRequestIdRef.current !== requestId) return;

      const visibleLineFeatures = lineFeatures.filter((feature) => {
        const properties = feature?.properties || {};
        return !(
          properties.tourCruisingPath &&
          !properties.tourCurrentSegmentPath &&
          sailedTrackSegmentKeysRef.current.has(properties.tourCruisingPathSegmentKey)
        );
      });
      // Cruising segments have already been committed as their individual
      // requests finish. The final pass is only for excursion geometry; doing
      // a second whole-route commit here used to briefly restore planned lines
      // over newly available sailed tracks.
      const deferredEventFeatures = visibleLineFeatures.filter(
        (feature) => !feature?.properties?.tourCruisingPath
      );
      const retainedCruisingFeatures = [];
      eventRouteFeaturesRef.current.forEach((feature) => {
        if (feature?.get?.('tourCruisingPath')) {
          retainedCruisingFeatures.push(feature);
        } else {
          removeFeature?.(feature, 'geoJson');
        }
      });
      const features = deferredEventFeatures.length
        ? addGeoJson({
            type: 'FeatureCollection',
            features: deferredEventFeatures,
          })
        : [];
      if (cancelled || routeBuildRequestIdRef.current !== requestId) {
        (Array.isArray(features) ? features : []).forEach((feature) => removeFeature?.(feature, 'geoJson'));
        return;
      }

      eventRouteFeaturesRef.current = [
        ...retainedCruisingFeatures,
        ...(Array.isArray(features) ? features : []),
      ];
      refreshRouteDistanceLabels();
      setEventRouteDistances(distanceByEventId);
      setIsRouteGeometryLoading(false);
    })().catch((error) => {
      if (!cancelled && routeBuildRequestIdRef.current === requestId) {
        console.warn('Keeping previous tour route because updated route geometry failed:', error);
        setIsRouteGeometryLoading(false);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [
    activeTourToShow?.id,
    pocsKey,
    isVisible,
    shouldRenderMapContent,
    shouldRenderTourRoutePaths,
    addGeoJson,
    addRouteGeometryFeatures,
    clearRenderedRouteFeatures,
    commitRouteFeatureGroup,
    removeFeature,
    refreshRouteDistanceLabels,
    isMapReady,
    navigationSegmentsByPair,
    resolveTourSegmentRouteMode,
    sailedTrackEndBySegment,
    sailedTrackSegmentKeys,
    liveTrackingPositions,
    currentTrackingCoords,
    currentTrackingCoordsKey,
    unitPreferences,
  ]);

  // Weather per PoC
  useEffect(() => {
    const ports = Array.isArray(activeTourToShow?.ports_of_call) ? activeTourToShow.ports_of_call : [];
    if (!ports.length) {
      weatherRequestIdRef.current += 1;
      setWeather([]);
      setIsWeatherLoading(false);
      return;
    }

    const requestId = weatherRequestIdRef.current + 1;
    weatherRequestIdRef.current = requestId;
    const now = new Date();
    const forecastWindowEnd = new Date(now.getTime() + 14 * 24 * 60 * 60 * 1000);
    const formatForecastDate = (date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };
    const getForecastRange = (startValue, endValue) => {
      const startDate = new Date(startValue);
      const endDate = new Date(endValue);
      if (Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime())) return null;
      if (startDate > forecastWindowEnd) return null;
      const requestStart = startDate;
      const requestEnd = endDate > forecastWindowEnd ? forecastWindowEnd : endDate;
      if (requestEnd < requestStart) return null;
      return {
        start: formatForecastDate(requestStart),
        end: formatForecastDate(requestEnd),
      };
    };

    setWeather(ports);
    setIsWeatherLoading(true);
    const forecastRequests = [];

    const getMobileInitialForecastPorts = () => {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const todayPorts = ports.filter((poc) => {
        if (!poc?.day_arrival) return false;
        const arrivalDate = new Date(poc.day_arrival);
        const departureDate = new Date(poc.day_departure || poc.day_arrival);
        if (Number.isNaN(arrivalDate.getTime()) || Number.isNaN(departureDate.getTime())) return false;
        arrivalDate.setHours(0, 0, 0, 0);
        departureDate.setHours(0, 0, 0, 0);
        return today >= arrivalDate && today <= departureDate;
      });
      if (todayPorts.length) return todayPorts;
      return ports.slice(0, 2);
    };

    const shouldLimitInitialMobileWeather = isMobileGuestView && mobileTab === 'today';
    const forecastPorts = shouldLimitInitialMobileWeather ? getMobileInitialForecastPorts() : ports;

    const applyEventForecast = (eventId, forecast) => {
      setWeather((prev) => prev.map((poc) => ({
        ...poc,
        events: Array.isArray(poc.events)
          ? poc.events.map((event) => (
              String(event?.id) === String(eventId) ? { ...event, weather: forecast } : event
            ))
          : poc.events,
      })));
    };

    forecastPorts.forEach((poc) => {
      const portId = poc?.id;
      const start = poc?.day_arrival;
      const end = poc?.day_departure;
      const forecastRange = getForecastRange(start, end);
      if (portId == null || !forecastRange) return;
      forecastRequests.push(
        getApiHeaders()
          .then((headers) => axios({
            url: buildApiUrl(`weatherbit-forecast/?port_id=${encodeURIComponent(portId)}`),
            method: 'get',
            headers,
          }))
          .then((response) => normalizeWeatherbitForecast(response.data))
          .then((forecast) => {
            if (weatherRequestIdRef.current !== requestId || !forecast) return;
            setWeather((prev) => prev.map((item) => (
              item.id === portId ? { ...item, weather: forecast } : item
            )));
          })
          .catch(() => null)
      );
    });

    if (!shouldLimitInitialMobileWeather) {
      ports.forEach((poc) => {
        (poc.events || []).forEach((event) => {
        const eventId = event?.id;
        const coords = getEventCoordinates(event, poc);
        const eventDate = event?.date || event?.event_info?.date || poc?.day_arrival;
        const forecastRange = getForecastRange(eventDate, eventDate);
          if (eventId == null || !coords || !forecastRange) return;
          forecastRequests.push(
            fetchOpenMeteoWeather(coords, forecastRange.start, forecastRange.end, getApiHeaders)
              .then((forecast) => {
                if (weatherRequestIdRef.current !== requestId || !forecast) return;
                applyEventForecast(eventId, forecast);
              })
              .catch(() => null)
          );
        });
      });
    }

    Promise.allSettled(forecastRequests).finally(() => {
      if (weatherRequestIdRef.current === requestId) setIsWeatherLoading(false);
    });
  }, [activeTourToShow?.id, getApiHeaders, isMobileGuestView, mobileTab, pocsKey]);

  useEffect(() => {
    if (Array.isArray(activeTourToShow?.navigation_segments)) {
      setNavigationSegments(activeTourToShow.navigation_segments);
    }
  }, [activeTourToShow?.id, activeTourToShow?.navigation_segments]);

  useEffect(() => {
    if (!activeTourToShow?.id || pocs.length < 2) {
      setNavigationSegments([]);
      return;
    }
    if (
      isMobileGuestView &&
      mobileTab === 'today' &&
      Array.isArray(activeTourToShow?.navigation_segments) &&
      activeTourToShow.navigation_segments.length
    ) {
      setNavigationSegments(activeTourToShow.navigation_segments);
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const headers = await getApiHeaders();
        const response = await axios({
          url: buildApiUrl(`tour/${activeTourToShow.id}/navigation-segments-summary/`),
          method: 'get',
          headers,
        });

        if (!cancelled) {
          setNavigationSegments(Array.isArray(response.data?.segments) ? response.data.segments : []);
        }
      } catch (error) {
        console.log('Failed to fetch navigation segment summaries:', error);
        if (!cancelled) {
          setNavigationSegments([]);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [activeTourToShow?.id, activeTourToShow?.navigation_segments, getApiHeaders, isMobileGuestView, mobileTab, pocs.length]);

  // Auto-expand “today” if present (and select its markers)
  useEffect(() => {
    if (!pocs.length || !activeTourToShow?.id) return;
    if (autoExpandedTourRef.current === activeTourToShow.id) return;

    const today = new Date(); today.setHours(0,0,0,0);
    const todayKey = getDateDayKey(today);
    const hasToday = pocs.some(p => {
      if (!p.day_arrival) return false;
      const d = new Date(p.day_arrival); d.setHours(0,0,0,0);
      return d.getTime() === today.getTime();
    });
    if (hasToday) {
      ensureDayExpanded(todayKey);
    }
    autoExpandedTourRef.current = activeTourToShow.id;
  }, [activeTourToShow?.id, pocs, ensureDayExpanded]);

  const closeTourDetailBar = useCallback(() => {
    if (lastExpandedDayRef.current) {
      deselectDayMarkers(lastExpandedDayRef.current);
      lastExpandedDayRef.current = null;
    }
    setExpandedDays(new Set());
    setWeatherModalOpen(false);
    setTourMapsModalOpen(false);
    eventRouteFeaturesRef.current.forEach((feature) => {
      removeFeature?.(feature, 'geoJson');
    });
    eventRouteFeaturesRef.current = [];
    sailedTrackFeaturesRef.current.forEach((feature) => {
      removeFeature?.(feature, 'geoJson');
    });
    sailedTrackFeaturesRef.current = [];
    sailedTrackSegmentKeysRef.current = new Set();
    setSailedTrackSegmentKeys(new Set());
    setSailedTrackEndBySegment(new Map());
    setSailedTrackDistanceBySegment(new Map());
    setLiveTrackingPositions([]);
    clearRouteDistanceLabels?.();
    setIsVisible(false);
  }, [clearRouteDistanceLabels, deselectDayMarkers, removeFeature]);

  // --- build days from weather (for display) ---
  const sortedPocs = (weather || []).slice().sort((a, b) => a.visit_order - b.visit_order);
  const tourStart = sortedPocs[0]?.day_arrival ? new Date(sortedPocs[0].day_arrival) : null;
  const tourEnd = sortedPocs[sortedPocs.length - 1]?.day_departure
    ? new Date(sortedPocs[sortedPocs.length - 1].day_departure)
    : (sortedPocs[sortedPocs.length - 1]?.day_arrival ? new Date(sortedPocs[sortedPocs.length - 1].day_arrival) : null);

  const allDays = [];
  if (tourStart && tourEnd) {
    const cur = new Date(tourStart); cur.setHours(0, 0, 0, 0);
    const end = new Date(tourEnd);  end.setHours(0, 0, 0, 0);
    while (cur <= end) { allDays.push(new Date(cur)); cur.setDate(cur.getDate() + 1); }
  }

  const processedDays = allDays.map((date, dayIndex) => {
    // Find ports that span this date (arrival <= date <= departure)
    const dayPocs = sortedPocs.filter(p => {
      if (!p.day_arrival) return false;
      
      const arrivalDate = new Date(p.day_arrival);
      const departureDate = new Date(p.day_departure || p.day_arrival);
      const currentDate = new Date(date);
      
      // Normalize all dates to start of day for comparison
      arrivalDate.setHours(0, 0, 0, 0);
      departureDate.setHours(0, 0, 0, 0);
      currentDate.setHours(0, 0, 0, 0);
      
      // Check if current date falls within the port visit period
      return currentDate >= arrivalDate && currentDate <= departureDate;
    });
    
    const hasVisits = dayPocs.length > 0;
    // no sunrise/sunset here; DayDetailModal handles that
    return { date, dayNumber: dayIndex + 1, dayKey: getDateDayKey(date), dayPocs, hasVisits, isDayAtSea: !hasVisits };
  });

  const processedDaysWithSegments = useMemo(() => {
    const findPreviousVisitedDayLastPoc = (dayIndex) => {
      for (let index = dayIndex - 1; index >= 0; index -= 1) {
        const candidate = processedDays[index];
        if (candidate?.hasVisits && candidate.dayPocs.length > 0) {
          return candidate.dayPocs[candidate.dayPocs.length - 1];
        }
      }
      return null;
    };

    const findNextVisitedDayFirstPoc = (dayIndex) => {
      for (let index = dayIndex + 1; index < processedDays.length; index += 1) {
        const candidate = processedDays[index];
        if (candidate?.hasVisits && candidate.dayPocs.length > 0) {
          return candidate.dayPocs[0];
        }
      }
      return null;
    };

    const makeSegmentEntry = (position, fromPoc, toPoc, anchorIndex = null, dayKey = null) => {
      if (!fromPoc?.id || !toPoc?.id || fromPoc.id === toPoc.id) return null;
      return {
        position,
        anchorIndex,
        key: `${position}-${fromPoc.id}-${toPoc.id}-${anchorIndex ?? 'edge'}`,
        dayKey,
        segment: navigationSegmentsByPair.get(`${fromPoc.id}-${toPoc.id}`) || null,
        fromPoc,
        toPoc,
      };
    };

    return processedDays.map((dayData, dayIndex) => {
      const segmentEntries = [];

      if (dayData.hasVisits) {
        const previousBoundaryPoc = findPreviousVisitedDayLastPoc(dayIndex);
        const nextBoundaryPoc = findNextVisitedDayFirstPoc(dayIndex);
        const firstPoc = dayData.dayPocs[0] || null;
        const lastPoc = dayData.dayPocs[dayData.dayPocs.length - 1] || null;

        const inbound = makeSegmentEntry('inbound', previousBoundaryPoc, firstPoc, null, dayData.dayKey);
        if (inbound) segmentEntries.push(inbound);

        dayData.dayPocs.forEach((poc, pocIndex) => {
          const nextPoc = dayData.dayPocs[pocIndex + 1];
          if (!nextPoc) return;
          const between = makeSegmentEntry('between', poc, nextPoc, pocIndex, dayData.dayKey);
          if (between) segmentEntries.push(between);
        });

        const outbound = makeSegmentEntry('outbound', lastPoc, nextBoundaryPoc, null, dayData.dayKey);
        if (outbound) segmentEntries.push(outbound);
      } else {
        const previousBoundaryPoc = findPreviousVisitedDayLastPoc(dayIndex);
        const nextBoundaryPoc = findNextVisitedDayFirstPoc(dayIndex);
        const spanning = makeSegmentEntry('spanning', previousBoundaryPoc, nextBoundaryPoc, null, dayData.dayKey);
        if (spanning) segmentEntries.push(spanning);
      }

      return { ...dayData, segmentEntries };
    });
  }, [navigationSegmentsByPair, processedDays]);

  const todayDayKey = getDateDayKey(new Date());
  const sailingScheduleSummary = useMemo(() => {
    const now = new Date(summaryClock);
    const orderedPorts = pocs.slice().sort((a, b) => a.visit_order - b.visit_order);
    const isSeaTour = isSeaCruiseTourType(activeTourToShow?.tour_type);
    const isRiverTour = isRiverCruiseTourType(activeTourToShow?.tour_type);
    const primaryMode = isSeaTour ? 'sea' : (isRiverTour ? 'river' : 'road');
    const formatMode = primaryMode === 'sea'
      ? getPreferredSeaRouteMode(activeTourToShow?.tour_type)
      : primaryMode;
    const label = primaryMode === 'sea'
      ? 'Sailing schedule'
      : (primaryMode === 'river' ? 'River cruising schedule' : 'Road travel schedule');
    const icon = primaryMode === 'sea' ? 'ship' : (primaryMode === 'river' ? 'anchor' : 'car');
    let sailedKm = 0;
    let remainingKm = 0;

    for (let index = 0; index < orderedPorts.length - 1; index += 1) {
      const fromPoc = orderedPorts[index];
      const toPoc = orderedPorts[index + 1];
      if (!fromPoc?.id || !toPoc?.id) continue;

      const segmentKey = `${fromPoc.id}-${toPoc.id}`;
      const plannedKm = Number(navigationSegmentsByPair.get(segmentKey)?.distance_km);
      const trackedKm = Number(sailedTrackDistanceBySegment.get(segmentKey));
      const hasTrackedDistance = Number.isFinite(trackedKm) && trackedKm > 0;
      const routeMode = resolveTourSegmentRouteMode(
        fromPoc,
        toPoc,
        navigationSegmentsByPair.get(segmentKey) || null
      );
      const isPrimarySegment = primaryMode === 'sea'
        ? routeMode === 'coastal' || routeMode === 'international'
        : routeMode === primaryMode;
      if (!isPrimarySegment) continue;
      const segmentEnd = parseSegmentBoundaryDate(
        toPoc?.day_arrival || toPoc?.day_departure,
        12
      );
      const isCompleted = Boolean(segmentEnd && segmentEnd < now);

      if (isCompleted) {
        if (hasTrackedDistance) sailedKm += trackedKm;
        else if (Number.isFinite(plannedKm) && plannedKm > 0) sailedKm += plannedKm;
        continue;
      }

      if (hasTrackedDistance) sailedKm += trackedKm;
      if (Number.isFinite(plannedKm) && plannedKm > 0) {
        // A current segment can already have a partial sailed track. Its planned
        // remainder is what is still relevant to the itinerary total.
        remainingKm += hasTrackedDistance
          ? Math.max(0, plannedKm - trackedKm)
          : plannedKm;
      }
    }

    return {
      label,
      icon,
      sailedKm,
      remainingKm,
      totalKm: sailedKm + remainingKm,
      formatDistance: (distanceKm) => formatDistanceKm(distanceKm, formatMode, unitPreferences),
    };
  }, [activeTourToShow?.tour_type, navigationSegmentsByPair, pocs, resolveTourSegmentRouteMode, sailedTrackDistanceBySegment, summaryClock, unitPreferences]);
  const actualTodayDayData = useMemo(() => (
    processedDaysWithSegments.find((dayData) => dayData.dayKey === todayDayKey) || null
  ), [processedDaysWithSegments, todayDayKey]);
  const tomorrowDayData = useMemo(() => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    const tomorrowKey = getDateDayKey(tomorrow);
    return processedDaysWithSegments.find((dayData) => dayData.dayKey === tomorrowKey) || null;
  }, [processedDaysWithSegments]);
  const yesterdayDayData = useMemo(() => {
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    const yesterdayKey = getDateDayKey(yesterday);
    return processedDaysWithSegments.find((dayData) => dayData.dayKey === yesterdayKey) || null;
  }, [processedDaysWithSegments]);
  const currentDayData = useMemo(() => (
    actualTodayDayData
    || processedDaysWithSegments.find((dayData) => dayData.hasVisits)
    || processedDaysWithSegments[0]
    || null
  ), [actualTodayDayData, processedDaysWithSegments]);
  const selectedDayKey = useMemo(() => (
    Array.from(expandedDays)[0] || currentDayData?.dayKey || ''
  ), [currentDayData?.dayKey, expandedDays]);
  const selectedDayData = useMemo(() => (
    processedDaysWithSegments.find((dayData) => dayData.dayKey === selectedDayKey)
    || currentDayData
    || null
  ), [currentDayData, processedDaysWithSegments, selectedDayKey]);
  useEffect(() => {
    if (!isMobileGuestView || mobileTab !== 'today' || !selectedDayData?.segmentEntries?.length) return undefined;

    let cancelled = false;
    const prefetchRoutes = () => {
      if (cancelled) return;
      selectedDayData.segmentEntries.forEach((entry) => {
        const start = getTourUserPortCoordinates(entry.fromPoc);
        const end = getTourUserPortCoordinates(entry.toPoc);
        if (!start || !end) return;
        const routeMode = resolveTourSegmentRouteMode(entry.fromPoc, entry.toPoc, entry.segment);
        fetchRouteGeometry({ startCoords: start, endCoords: end, routeMode }).catch(() => {});
      });
    };

    const idleId = typeof window !== 'undefined' && typeof window.requestIdleCallback === 'function'
      ? window.requestIdleCallback(prefetchRoutes, { timeout: 3500 })
      : null;
    const timerId = idleId == null && typeof window !== 'undefined'
      ? window.setTimeout(prefetchRoutes, 2800)
      : null;

    return () => {
      cancelled = true;
      if (idleId != null) window.cancelIdleCallback?.(idleId);
      if (timerId != null) window.clearTimeout(timerId);
    };
  }, [fetchRouteGeometry, isMobileGuestView, mobileTab, resolveTourSegmentRouteMode, selectedDayData]);
  const selectedDayIndex = useMemo(
    () => processedDaysWithSegments.findIndex((dayData) => dayData.dayKey === selectedDayData?.dayKey),
    [processedDaysWithSegments, selectedDayData?.dayKey]
  );
  const previousSelectedDayData = selectedDayIndex > 0
    ? processedDaysWithSegments[selectedDayIndex - 1]
    : null;
  const nextSelectedDayData = selectedDayIndex >= 0 && selectedDayIndex < processedDaysWithSegments.length - 1
    ? processedDaysWithSegments[selectedDayIndex + 1]
    : null;

  const selectGuestDay = useCallback((dayData, { focusMap = true, focusPort = null, focusZoom = 12 } = {}) => {
    if (!dayData?.dayKey) return;
    ensureDayExpanded(dayData.dayKey);
    if (focusPort) {
      fitMapToPort(focusPort, { zoom: focusZoom });
      return;
    }
    if (focusMap) fitMapToDayPorts(dayData);
  }, [ensureDayExpanded, fitMapToDayPorts, fitMapToPort]);

  useEffect(() => {
    if (!isLobbyGuestView || !isVisible || !shouldRenderMapContent || !activeTourToShow?.id || !processedDaysWithSegments.length) {
      return undefined;
    }

    const todayKey = getDateDayKey(new Date());
    const firstDayKey = processedDaysWithSegments[0]?.dayKey;
    const lastDayKey = processedDaysWithSegments[processedDaysWithSegments.length - 1]?.dayKey;
    const shouldFitFullRoute = !actualTodayDayData || (firstDayKey && todayKey < firstDayKey) || (lastDayKey && todayKey > lastDayKey);
    const fitTargetKey = shouldFitFullRoute ? 'full-route' : actualTodayDayData.dayKey;
    const targetCoords = shouldFitFullRoute
      ? pocs
          .map((poc) => getTourUserPortCoordinates(poc))
          .filter((coord) => coord && isValidLonLat(coord[0], coord[1]))
      : [
          ...(actualTodayDayData?.dayPocs || []).map((poc) => getTourUserPortCoordinates(poc)),
          ...(actualTodayDayData?.segmentEntries || []).flatMap((entry) => [
            getTourUserPortCoordinates(entry?.fromPoc),
            getTourUserPortCoordinates(entry?.toPoc),
          ]),
        ].filter((coord) => coord && isValidLonLat(coord[0], coord[1]));
    const dedupedTargetCoords = [];
    targetCoords.forEach((coord) => {
      if (!dedupedTargetCoords.some((existing) => isSameCoordinate(existing, coord))) {
        dedupedTargetCoords.push(coord);
      }
    });
    if (!dedupedTargetCoords.length) return undefined;

    const coordSignature = dedupedTargetCoords
      .map((coord) => `${Number(coord[0]).toFixed(5)},${Number(coord[1]).toFixed(5)}`)
      .join('|');
    const fitKey = `${activeTourToShow.id}:${fitTargetKey}:${coordSignature}`;
    if (initialMarkerFitTourRef.current === fitKey) return undefined;

    const fitLobbyMap = () => {
      getMap?.()?.updateSize?.();
      let fitted = false;
      if (shouldFitFullRoute) {
        fitted = fitMapToCoordinates(dedupedTargetCoords, { singleZoom: 9, maxZoom: 8 });
      } else {
        fitted = fitMapToDayPorts(actualTodayDayData, { includeSegmentEndpoints: true });
      }
      if (fitted) {
        initialMarkerFitTourRef.current = fitKey;
      }
    };

    const timers = [120, 320, 700, 1200, 2000, 3200].map((delay) => window.setTimeout(fitLobbyMap, delay));
    return () => timers.forEach((timer) => window.clearTimeout(timer));
  }, [
    activeTourToShow?.id,
    actualTodayDayData,
    fitMapToCoordinates,
    fitMapToDayPorts,
    getMap,
    isLobbyGuestView,
    isVisible,
    pocs,
    processedDaysWithSegments,
    shouldRenderMapContent,
  ]);

  useEffect(() => {
    if (!isMobileGuestView || mobileTab !== 'map' || !isVisible || !activeTourToShow?.id || !processedDaysWithSegments.length) {
      return undefined;
    }

    const todayKey = getDateDayKey(new Date());
    const firstDayKey = processedDaysWithSegments[0]?.dayKey;
    const lastDayKey = processedDaysWithSegments[processedDaysWithSegments.length - 1]?.dayKey;
    const shouldFitFullRoute = !actualTodayDayData || (firstDayKey && todayKey < firstDayKey) || (lastDayKey && todayKey > lastDayKey);
    const fitTargetKey = shouldFitFullRoute ? 'full-route' : actualTodayDayData.dayKey;
    const targetCoords = shouldFitFullRoute
      ? pocs
          .map((poc) => getTourUserPortCoordinates(poc))
          .filter((coord) => coord && isValidLonLat(coord[0], coord[1]))
      : [
          ...(actualTodayDayData?.dayPocs || []).map((poc) => getTourUserPortCoordinates(poc)),
          ...(actualTodayDayData?.segmentEntries || []).flatMap((entry) => [
            getTourUserPortCoordinates(entry?.fromPoc),
            getTourUserPortCoordinates(entry?.toPoc),
          ]),
        ].filter((coord) => coord && isValidLonLat(coord[0], coord[1]));
    const dedupedTargetCoords = [];
    targetCoords.forEach((coord) => {
      if (!dedupedTargetCoords.some((existing) => isSameCoordinate(existing, coord))) {
        dedupedTargetCoords.push(coord);
      }
    });
    if (!dedupedTargetCoords.length) return undefined;

    const coordSignature = dedupedTargetCoords
      .map((coord) => `${Number(coord[0]).toFixed(5)},${Number(coord[1]).toFixed(5)}`)
      .join('|');
    const fitKey = `${activeTourToShow.id}:mobile-map:${fitTargetKey}:${coordSignature}`;
    if (initialMarkerFitTourRef.current === fitKey) return undefined;

    const fitMobileMap = () => {
      getMap?.()?.updateSize?.();
      const fitted = shouldFitFullRoute
        ? fitMapToCoordinates(dedupedTargetCoords, { singleZoom: 9, maxZoom: 8 })
        : fitMapToDayPorts(actualTodayDayData, { includeSegmentEndpoints: true });
      if (fitted) {
        initialMarkerFitTourRef.current = fitKey;
      }
    };

    const timers = [120, 320, 700, 1200, 2000, 3200].map((delay) => window.setTimeout(fitMobileMap, delay));
    return () => timers.forEach((timer) => window.clearTimeout(timer));
  }, [
    activeTourToShow?.id,
    actualTodayDayData,
    fitMapToCoordinates,
    fitMapToDayPorts,
    getMap,
    isMobileGuestView,
    isVisible,
    mobileTab,
    pocs,
    processedDaysWithSegments,
  ]);

  useEffect(() => {
    const olMap = getMap?.() || map;
    if (!olMap?.on || !olMap?.un || !olMap?.forEachFeatureAtPixel) return undefined;

    const handleCruisingPathClick = (event) => {
      const clickedFeature = olMap.forEachFeatureAtPixel(
        event.pixel,
        (feature) => {
          const clusteredFeatures = feature?.get?.('features');
          const candidates = Array.isArray(clusteredFeatures) ? clusteredFeatures : [feature];
          return candidates.find((candidate) => (
            candidate?.get?.('tourCruisingPath') || candidate?.get?.('tourEventId')
          )) || null;
        },
        { hitTolerance: 8 }
      );

      if (!clickedFeature) return;

      const tourEventId = clickedFeature.get('tourEventId');
      if (tourEventId != null && !clickedFeature.get('tourCruisingPath')) {
        const eventDayKey = processedDaysWithSegments.find((dayData) => (
          (dayData.dayPocs || []).some((poc) => (
            (poc?.events || []).some((eventItem) => String(eventItem?.id) === String(tourEventId))
          ))
        ))?.dayKey || null;
        setSelectedMapFeature({
          kind: 'eventRoute',
          eventId: String(tourEventId),
          dayKey: eventDayKey,
        });
        if (!isVisible) setIsVisible(true);
        return;
      }

      const dayKey = clickedFeature.get('tourCruisingPathDayKey');
      const segmentKey = clickedFeature.get('tourCruisingPathSegmentKey');
      const targetDay = processedDaysWithSegments.find((dayData) => dayData.dayKey === dayKey)
        || processedDaysWithSegments.find((dayData) => (
          (dayData.segmentEntries || []).some((entry) => `${entry.fromPoc?.id}-${entry.toPoc?.id}` === segmentKey)
        ));

      if (!targetDay) return;
      if (!isVisible) setIsVisible(true);
      setSelectedMapFeature({
        kind: 'segment',
        segmentKey,
        dayKey: targetDay.dayKey,
      });
      selectGuestDay(targetDay, { focusMap: false });
    };

    olMap.on('singleclick', handleCruisingPathClick);
    return () => {
      olMap.un('singleclick', handleCruisingPathClick);
    };
  }, [getMap, isVisible, map, processedDaysWithSegments, selectGuestDay, setIsVisible]);

  const goToToday = useCallback(() => {
    if (!actualTodayDayData) return;
    setMobileTab('today');
    selectGuestDay(actualTodayDayData);
  }, [actualTodayDayData, selectGuestDay]);

  const focusTourEventOnMap = useCallback((event, poc) => {
    if (!event?.id) return;
    const eventDayKey = getTourEventDayKey(event, poc?.day_arrival);
    const targetDay = processedDaysWithSegments.find((dayData) => dayData.dayKey === eventDayKey)
      || selectedDayData
      || currentDayData;

    if (targetDay) {
      ensureDayExpanded(targetDay.dayKey);
      selectGuestDay(targetDay, { focusMap: false });
    }

    setSelectedMapFeature({
      kind: 'eventRoute',
      eventId: String(event.id),
      dayKey: eventDayKey,
    });
    selectMarker(`mp-event-${event.id}`);
    if (isMobileGuestView) setMobileTab('map');

    const eventCoords = getEventCoordinates(event, poc);
    const portCoords = getTourUserPortCoordinates(poc);
    const coords = [portCoords, eventCoords].filter(Boolean);
    window.setTimeout(() => {
      if (coords.length) {
        fitMapToCoordinates(coords, { singleZoom: 13, maxZoom: 13 });
      } else if (targetDay) {
        fitMapToDayPorts(targetDay, { includeSegmentEndpoints: true });
      }
    }, 60);
  }, [currentDayData, ensureDayExpanded, fitMapToCoordinates, fitMapToDayPorts, isMobileGuestView, processedDaysWithSegments, selectGuestDay, selectMarker, selectedDayData]);

  const focusTodayOnMap = useCallback(() => {
    if (!actualTodayDayData) return;
    ensureDayExpanded(actualTodayDayData.dayKey);
    fitMapToDayPorts(actualTodayDayData, { includeSegmentEndpoints: true });
  }, [actualTodayDayData, ensureDayExpanded, fitMapToDayPorts]);

  const fitFullRoute = useCallback(() => {
    const coords = pocs
      .map((poc) => getTourUserPortCoordinates(poc))
      .filter((coord) => coord && isValidLonLat(coord[0], coord[1]));
    fitMapToCoordinates(coords, { singleZoom: 9, maxZoom: 8 });
  }, [fitMapToCoordinates, pocs]);

  useEffect(() => {
    if (!selectedDayData?.dayKey) return undefined;
    const seaSegments = (selectedDayData.segmentEntries || []).filter((entry) => {
      const mode = resolveTourSegmentRouteMode(entry.fromPoc, entry.toPoc, entry.segment);
      return ['sea', 'coastal', 'international'].includes(mode);
    });
    if (!seaSegments.length) return undefined;

    let cancelled = false;
    const windows = getForecastWindowBounds(selectedDayData.dayKey);
    const dayStart = windows[0]?.start;
    const dayEnd = windows[windows.length - 1]?.end;
    if (!dayStart || !dayEnd) return undefined;

    seaSegments.forEach(async (entry) => {
      const key = `${selectedDayData.dayKey}-${entry.fromPoc?.id}-${entry.toPoc?.id}`;
      if (marineForecastBySegmentKey[key]) return;

      const fromCoords = getTourUserPortCoordinates(entry.fromPoc);
      const toCoords = getTourUserPortCoordinates(entry.toPoc);
      if (!fromCoords || !toCoords) return;
      const midpoint = [
        (Number(fromCoords[0]) + Number(toCoords[0])) / 2,
        (Number(fromCoords[1]) + Number(toCoords[1])) / 2,
      ];
      if (!isValidLonLat(midpoint[0], midpoint[1])) return;

      const forecast = await fetchMarineForecast(midpoint, dayStart.toISOString(), dayEnd.toISOString(), getApiHeaders);
      if (cancelled || !forecast) return;
      setMarineForecastBySegmentKey((prev) => ({ ...prev, [key]: forecast }));
    });

    return () => {
      cancelled = true;
    };
  }, [getApiHeaders, marineForecastBySegmentKey, resolveTourSegmentRouteMode, selectedDayData]);

  useEffect(() => {
    if (!showTutorial) {
      setHighlightPosition(null);
      return undefined;
    }

    const step = tutorialSteps[currentTutorialStep];
    if (!step) return undefined;

    if (step.ensureOpenVisitDay) {
      const targetDay = step.selector === '.TourUser-port-weather-row'
        ? processedDaysWithSegments.find((dayData) =>
          dayData?.hasVisits && dayData.dayPocs.some((poc) => getPortForecastForDay(poc, dayData.dayKey)?.hourly)
        )
        : processedDaysWithSegments.find((dayData) => dayData?.hasVisits);

      if (targetDay?.dayKey) {
        setItineraryViewMode('vertical');
        ensureDayExpanded(targetDay.dayKey);
      }
    }

    const updateHighlight = () => {
      const el = document.querySelector(step.selector);
      if (!el) {
        setHighlightPosition(null);
        return;
      }
      el.scrollIntoView?.({ block: 'center', inline: 'nearest', behavior: 'smooth' });
      const rect = el.getBoundingClientRect();
      setHighlightPosition({
        top: Math.max(rect.top - 4, 0),
        left: Math.max(rect.left - 4, 0),
        width: rect.width + 8,
        height: rect.height + 8,
      });
    };

    updateHighlight();
    const timers = [
      window.setTimeout(updateHighlight, 120),
      window.setTimeout(updateHighlight, 320),
    ];
    return () => timers.forEach((timer) => window.clearTimeout(timer));
  }, [
    currentTutorialStep,
    ensureDayExpanded,
    processedDaysWithSegments,
    showTutorial,
    tutorialSteps,
  ]);

  useEffect(() => {
    if (!activeTourToShow?.id || !processedDaysWithSegments.length) return;
    const todayKey = getDateDayKey(new Date());
    const hasToday = processedDaysWithSegments.some((dayData) => dayData.dayKey === todayKey);
    if (!hasToday) return;

    const scrollKey = `${activeTourToShow.id}:${todayKey}:${itineraryViewMode}`;
    if (autoScrolledTourDayRef.current === scrollKey) return;
    autoScrolledTourDayRef.current = scrollKey;

    window.setTimeout(() => {
      const el = document.getElementById(`day-${todayKey}`);
      el?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });
    }, 0);
  }, [activeTourToShow?.id, itineraryViewMode, processedDaysWithSegments]);

  useEffect(() => {
    if (!isLobbyGuestView || !activeTourToShow?.id || !processedDaysWithSegments.length) return;
    const todayKey = getDateDayKey(new Date());
    window.setTimeout(() => {
      const el = document.getElementById(`timeline-${todayKey}`);
      el?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
    }, 0);
  }, [activeTourToShow?.id, isLobbyGuestView, processedDaysWithSegments]);

  const actionButtonStyle = {
    borderRadius: '10px',
    minHeight: '34px',
    fontWeight: 600,
    boxShadow: 'none',
  };
  const renderPendingSpace = (label, { minHeight = 72 } = {}) => (
    <div
      style={{
        minHeight,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 9,
        color: '#64748b',
        fontWeight: 700,
      }}
      aria-live='polite'
    >
      <Icon loading name='spinner' style={{ margin: 0 }} />
      <span>{label}</span>
    </div>
  );
  const tourFiles = Array.isArray(activeTourToShow?.files) ? activeTourToShow.files : [];
  const dailyTourFiles = tourFiles.filter((file) => file?.day_number != null);
  const otherTourFiles = tourFiles.filter((file) => file?.day_number == null);
  const actualCruisePathFiles = tourFiles.filter((file) => (
    (file?.category_code || file?.category?.code) === 'actual_cruise_path'
  ));
  const tourResourceCount = tourResourceMaps.length + tourFiles.length;
  const latestPortById = useMemo(() => {
    const mapById = new Map();
    (activeTourToShow?.ports_of_call || []).forEach((poc) => {
      if (poc?.id != null) mapById.set(String(poc.id), poc);
    });
    return mapById;
  }, [activeTourToShow?.ports_of_call]);
  const getLatestPortVisit = useCallback((poc) => {
    if (!poc?.id) return poc;
    return latestPortById.get(String(poc.id)) || poc;
  }, [latestPortById]);
  const hydrateDayDataFromFullTour = useCallback((dayData) => {
    if (!dayData) return dayData;
    const hydrateSegmentEntry = (entry) => {
      if (!entry) return entry;
      const fromPoc = getLatestPortVisit(entry.fromPoc);
      const toPoc = getLatestPortVisit(entry.toPoc);
      if (fromPoc === entry.fromPoc && toPoc === entry.toPoc) return entry;
      return { ...entry, fromPoc, toPoc };
    };
    return {
      ...dayData,
      dayPocs: (dayData.dayPocs || []).map(getLatestPortVisit),
      segmentEntries: (dayData.segmentEntries || []).map(hydrateSegmentEntry),
    };
  }, [getLatestPortVisit]);
  const getDayResources = useCallback((dayData) => {
    if (!dayData) return { maps: [], files: [] };
    const dayFiles = tourFiles.filter((file) => Number(file?.day_number) === Number(dayData.dayNumber));
    const hydratedDayData = hydrateDayDataFromFullTour(dayData);
    const maps = (hydratedDayData.dayPocs || []).flatMap((resourcePoc) => {
      const portMaps = Array.isArray(resourcePoc?.poc_info?.maps_in_port?.features)
        ? resourcePoc.poc_info.maps_in_port.features.filter(isMapVisibleToTourUser)
        : [];
      return portMaps.map((mapItem, index) => ({
        mapItem,
        portVisit: resourcePoc,
        sourceLabel: resourcePoc?.poc_info?.name || 'Port map',
        key: `day-${dayData.dayKey}-port-${resourcePoc?.id || resourcePoc?.visit_order || 'unknown'}-${mapItem?.id || mapItem?.properties?.api_key || index}`,
      }));
    });
    return { maps: dedupeTourMapResources(maps), files: dayFiles };
  }, [dedupeTourMapResources, hydrateDayDataFromFullTour, isMapVisibleToTourUser, tourFiles]);
  const downloadTourLowResMaps = useCallback(async () => {
    if (!activeTourToShow?.id || isDownloadingTourMaps) return;

    setIsDownloadingTourMaps(true);
    try {
      const headers = await getApiHeaders();
      const response = await axios.get(
        buildApiUrl(`map/download_tour_maps_jpg_low_res/?tour_id=${activeTourToShow.id}`),
        {
          headers,
          responseType: 'blob',
        }
      );

      const blob = new Blob([response.data], { type: response.headers?.['content-type'] || 'application/zip' });
      const contentDisposition = response.headers?.['content-disposition'] || '';
      const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/i);
      const filename = filenameMatch?.[1] || `tour_${activeTourToShow.id}_resources.zip`;
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.warn('Failed to download tour low-res maps:', error);
    } finally {
      setIsDownloadingTourMaps(false);
    }
  }, [activeTourToShow?.id, getApiHeaders, isDownloadingTourMaps]);
  const renderTourMapResource = ({ mapItem, portVisit, sourceLabel, key }, index) => {
    const mapProps = mapItem?.properties || mapItem || {};
    const mapName = replaceBrTags(mapProps.name || mapProps.area_name || `Map ${index + 1}`);
    const mapArea = replaceBrTags(mapProps.country_name || mapProps.area_name || 'Unknown area');
    const mapApiKey = mapProps.api_key || mapItem?.api_key;
    const mapOnlineUrl = getDockingMapUrl(mapApiKey, portVisit);
    const preferredPdfPath = getMapAssetPath(mapProps, mapItem, [
      'pdf_vector_path_no_qr',
      'pdf_path_no_qr',
    ]);
    const highResJpgPath = getMapAssetPath(mapProps, mapItem, [
      'jpg_200dpi_q90_path_no_qr',
      'jpg_200dpi_q90_path',
    ]);
    const lowResJpgPath = getMapAssetPath(mapProps, mapItem, [
      'jpg_125dpi_q85_path_no_qr',
      'jpg_125dpi_q85_path',
    ]);

    return (
      <div
        key={key}
        style={{
          border: '1px solid #dbe3ee',
          borderRadius: 8,
          padding: '10px',
          background: '#fff',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px' }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, flex: 1, minWidth: 0 }}>
            <Icon name='map outline' style={{ color: '#0b6fcf', fontSize: 18, marginTop: 2, flex: '0 0 auto' }} />
            <div style={{ minWidth: 0 }}>
              <a
                href={mapOnlineUrl || undefined}
                style={{
                  color: '#0b6fcf',
                  fontWeight: 700,
                  textDecoration: 'none',
                  wordBreak: 'break-word',
                }}
                onClick={(event) => {
                  if (!mapOnlineUrl) event.preventDefault();
                }}
              >
                {mapName}
              </a>
              <div style={{ color: '#6b7280', marginTop: '4px' }}>
                {sourceLabel} - {mapArea}
              </div>
            </div>
          </div>
          {isMobile ? (
            <Button
              size='mini'
              icon='external alternate'
              content='Open'
              href={highResJpgPath ? buildMapsUrl(highResJpgPath) : undefined}
              target='_blank'
              disabled={!highResJpgPath}
            />
          ) : null}
        </div>

        {!isMobile ? (
          <div style={{ marginTop: '10px', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            <Button
              size='mini'
              icon='file pdf outline'
              content='PDF'
              href={preferredPdfPath ? buildMapsUrl(preferredPdfPath) : undefined}
              target='_blank'
              disabled={!preferredPdfPath}
            />
            <Button
              size='mini'
              icon='file image outline'
              content='High Res JPG'
              href={highResJpgPath ? buildMapsUrl(highResJpgPath) : undefined}
              target='_blank'
              disabled={!highResJpgPath}
            />
            <Button
              size='mini'
              icon='image outline'
              content='Low Res JPG'
              href={lowResJpgPath ? buildMapsUrl(lowResJpgPath) : undefined}
              target='_blank'
              disabled={!lowResJpgPath}
            />
            <Button
              size='mini'
              icon='external'
              content='Open online'
              onClick={() => {
                if (mapOnlineUrl) window.location.assign(mapOnlineUrl);
              }}
              disabled={!mapOnlineUrl}
            />
          </div>
        ) : null}
      </div>
    );
  };
  const renderTourFileResource = (file) => {
    const fileUrl = file?.download_url || file?.file_url || null;
    const technicalFileName = file?.original_filename || file?.name || 'Unnamed file';
    const fileName = file?.title || file?.description || technicalFileName;
    const coordinates = file?.custom_location?.coordinates;
    const longitude = Number(coordinates?.[0]);
    const latitude = Number(coordinates?.[1]);
    const coordinateLabel = Number.isFinite(longitude) && Number.isFinite(latitude)
      ? `${latitude.toFixed(4)}, ${longitude.toFixed(4)}`
      : null;
    const locationName = file?.port_of_call_name || file?.custom_location_label || coordinateLabel || file?.location_name || file?.location || null;
    const previewUrl = file?.thumbnail_url || (file?.file_type === 'image' ? fileUrl : null);
    return (
      <div
        key={`file-${file?.id || technicalFileName}`}
        style={{
          border: '1px solid #e5e7eb',
          borderRadius: 8,
          overflow: 'hidden',
          background: '#fff',
        }}
      >
        {previewUrl ? (
          <a
            href={fileUrl || undefined}
            target='_blank'
            rel='noreferrer'
            aria-label={`Open ${fileName}`}
            onClick={(event) => {
              if (!fileUrl) event.preventDefault();
            }}
            style={{ display: 'block', width: '100%', aspectRatio: '16 / 7', background: '#f1f5f9' }}
          >
            <TourFileImagePreview
              src={previewUrl}
              alt=''
              containerStyle={{ width: '100%', height: '100%' }}
              imageStyle={{ objectFit: 'cover' }}
            />
          </a>
        ) : null}
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 10, padding: '10px' }}>
          <div style={{ minWidth: 0 }}>
            <a
              href={fileUrl || undefined}
              target='_blank'
              rel='noreferrer'
              style={{
                color: fileUrl ? '#0b6fcf' : '#6b7280',
                fontWeight: 700,
                textDecoration: 'none',
                overflowWrap: 'anywhere',
              }}
              onClick={(event) => {
                if (!fileUrl) event.preventDefault();
              }}
            >
              {fileName}
            </a>
            <div style={{ color: '#64748b', fontSize: 12, marginTop: 2 }}>
              {locationName || (file?.day_number ? `Day ${file.day_number}` : (file?.category_name || 'Uploaded file'))}
            </div>
            {fileName !== technicalFileName ? (
              <div style={{ color: '#94a3b8', fontSize: 11, marginTop: 2, overflowWrap: 'anywhere' }}>
                {technicalFileName}
              </div>
            ) : null}
          </div>
          <Button
            size='mini'
            icon='external alternate'
            content='Open'
            href={fileUrl || undefined}
            target='_blank'
            disabled={!fileUrl}
          />
        </div>
      </div>
    );
  };
  const tourResourceTabs = [
    ...(tourResourceMaps.length > 0 ? [{
      menuItem: `Maps (${tourResourceMaps.length})`,
      render: () => (
        <Tab.Pane attached={false} style={{ padding: 0, border: 0, boxShadow: 'none' }}>
          <div style={{ display: 'grid', gap: '8px' }}>
            {tourResourceMaps.map((resource, index) => renderTourMapResource(resource, index))}
          </div>
        </Tab.Pane>
      ),
    }] : []),
    ...(dailyTourFiles.length > 0 ? [{
      menuItem: `Dailies (${dailyTourFiles.length})`,
      render: () => (
        <Tab.Pane attached={false} style={{ padding: 0, border: 0, boxShadow: 'none' }}>
          <div style={{ display: 'grid', gap: '8px' }}>
            {dailyTourFiles.map((file) => renderTourFileResource(file))}
          </div>
        </Tab.Pane>
      ),
    }] : []),
    ...(otherTourFiles.length > 0 ? [{
      menuItem: `Other files (${otherTourFiles.length})`,
      render: () => (
        <Tab.Pane attached={false} style={{ padding: 0, border: 0, boxShadow: 'none' }}>
          <div style={{ display: 'grid', gap: '8px' }}>
            {otherTourFiles.map((file) => renderTourFileResource(file))}
          </div>
        </Tab.Pane>
      ),
    }] : []),
  ];
  const onlineTourUrl = activeTourToShow?.unique_code
    ? `https://qrmaps.me/${activeTourToShow.unique_code}`
    : null;
  const lobbyBottomPaneHeightPx = lobbyTimelineCollapsed ? 0 : LOBBY_TIMELINE_HEIGHT_PX;
  const effectiveSidebarConfig = isLobbyGuestView
    ? {
        ...(sidebarConfig || {}),
        width: sidebarConfig?.width || '400px',
        // --vh is kept in sync with visualViewport by MainMap. Unlike 100vh,
        // it reflects the visible area on older Android browsers and TV shells.
        height: `calc((var(--vh, 1vh) * 100) - ${lobbyBottomPaneHeightPx}px)`,
      }
    : {
        ...(sidebarConfig || {}),
        height: isMobileGuestView && mobileTab !== 'map'
          ? 'calc(var(--vh, 1vh) * 100)'
          : isMobileGuestView
            ? `${MOBILE_MAP_PANEL_HEIGHT_PX}px`
            : (sidebarConfig?.height || '58vh'),
      };

  useEffect(() => {
    if (!isVisible || typeof onHeightChange !== 'function') return;
    if (isLobbyGuestView) {
      onHeightChange(`${lobbyBottomPaneHeightPx}px`);
      return;
    }
    if (isMobileGuestView) {
      onHeightChange(effectiveSidebarConfig.height);
    }
  }, [effectiveSidebarConfig.height, isLobbyGuestView, isMobileGuestView, isVisible, lobbyBottomPaneHeightPx, onHeightChange]);

  const formatSegmentDistance = useCallback((entry) => {
    const distanceValue = Number(entry?.segment?.distance_km);
    if (!Number.isFinite(distanceValue)) return null;
    const mode = resolveTourSegmentRouteMode(entry.fromPoc, entry.toPoc, entry.segment);
    return formatDistanceKm(distanceValue, mode, unitPreferences);
  }, [resolveTourSegmentRouteMode, unitPreferences]);

  const getDayWeatherSummary = useCallback((dayData) => {
    if (!dayData?.hasVisits) return null;
    const dayForecasts = dayData.dayPocs
      .map((poc) => getPortForecastForDay(poc, dayData.dayKey))
      .filter((forecast) => forecast?.hourly);
    if (!dayForecasts.length) return null;

    const allTemps = dayForecasts.map((forecast) => forecast.hourly.temperature_2m).filter(Boolean).flat();
    const allPrecip = filterValidWeatherData(dayForecasts.map((forecast) => forecast.hourly.precipitation).filter(Boolean).flat());
    const allWind = filterValidWeatherData(dayForecasts.map((forecast) => forecast.hourly.wind_speed_10m).filter(Boolean).flat());
    const tempRange = getValidMinMax(allTemps);
    const parts = [];
    if (tempRange.min !== null && tempRange.max !== null) {
      parts.push(`${tempRange.min.toFixed(0)}-${tempRange.max.toFixed(0)}°C`);
    }
    const totalPrecip = allPrecip.reduce((total, value) => total + value, 0);
    if (totalPrecip > 0) parts.push(`${Math.round(totalPrecip)} mm`);
    const maxWindKmh = allWind.length > 0 ? Math.max(...allWind) : 0;
    if (maxWindKmh > 0) {
      parts.push(formatSpeedKmh(maxWindKmh, isSeaCruiseTourType(activeTourToShow?.tour_type) ? 'sea' : 'road', unitPreferences, { digits: 0 }));
    }
    return parts.length ? parts.join(' · ') : null;
  }, [activeTourToShow?.tour_type, unitPreferences]);

  const getDayWeatherConditionLabel = useCallback((dayData) => {
    if (!dayData?.hasVisits) return null;
    const dayForecasts = dayData.dayPocs
      .map((poc) => getPortForecastForDay(poc, dayData.dayKey))
      .filter((forecast) => forecast?.hourly);
    if (!dayForecasts.length) return null;

    const precipitation = dayForecasts
      .map((forecast) => forecast.hourly.precipitation || [])
      .flat()
      .map(Number)
      .filter(Number.isFinite);
    const cloudCover = dayForecasts
      .map((forecast) => forecast.hourly.cloud_cover || [])
      .flat()
      .map(Number)
      .filter(Number.isFinite);
    const windSpeed = dayForecasts
      .map((forecast) => forecast.hourly.wind_speed_10m || [])
      .flat()
      .map(Number)
      .filter(Number.isFinite);

    const rainTotal = precipitation.reduce((total, value) => total + value, 0);
    const averageCloud = cloudCover.length
      ? cloudCover.reduce((total, value) => total + value, 0) / cloudCover.length
      : null;
    const maxWind = windSpeed.length ? Math.max(...windSpeed) : null;

    if (rainTotal >= 2) return 'Rainy';
    if (Number.isFinite(maxWind) && maxWind >= 28) return 'Windy';
    if (Number.isFinite(averageCloud) && averageCloud >= 75) return 'Cloudy';
    if (Number.isFinite(averageCloud) && averageCloud >= 35) return 'Partly cloudy';
    return 'Sunny';
  }, []);

  const getDayEvents = useCallback((dayData) => {
    if (!dayData?.dayKey) return [];
    const hydratedDayData = hydrateDayDataFromFullTour(dayData);
    const candidatesByPoc = new Map();
    const addPoc = (poc) => {
      if (!poc) return;
      const key = String(poc?.id || poc?.poc_id || poc?.poc_info?.id || poc?.poc_info?.name || candidatesByPoc.size);
      if (!candidatesByPoc.has(key)) candidatesByPoc.set(key, poc);
    };

    (hydratedDayData.dayPocs || []).forEach(addPoc);
    (hydratedDayData.segmentEntries || []).forEach((entry) => {
      addPoc(entry?.fromPoc);
      addPoc(entry?.toPoc);
    });
    (pocs || []).forEach(addPoc);

    const seenEvents = new Set();
    const matches = [];
    candidatesByPoc.forEach((poc) => {
      sortTourEvents((poc?.events || []).filter((event) =>
        doesTourEventMatchDay(event, dayData.dayKey, poc?.day_arrival || dayData.date)
      )).forEach((event) => {
        const key = String(event?.id || `${poc?.id || 'poc'}-${getTourEventDayKey(event, poc?.day_arrival || dayData.date)}-${getTourEventName(event)}`);
        if (seenEvents.has(key)) return;
        seenEvents.add(key);
        matches.push({ event, poc });
      });
    });
    return sortTourEvents(matches.map(({ event }) => event)).map((event) => matches.find((item) => item.event === event)).filter(Boolean);
  }, [hydrateDayDataFromFullTour, pocs]);

  const formatDayTitle = (dayData, options = {}) => {
    if (!dayData?.date) return 'Day';
    return dayData.date.toLocaleDateString('en-US', {
      weekday: options.short ? 'short' : 'long',
      month: 'short',
      day: 'numeric',
    });
  };

  const buildDayWeatherItineraryContexts = useCallback((dayData) => {
    const bounds = getDayBounds(dayData?.dayKey);
    if (!bounds) return [];
    const overnightForecastEnd = new Date(bounds.start);
    overnightForecastEnd.setDate(overnightForecastEnd.getDate() + 1);
    overnightForecastEnd.setHours(6, 0, 0, 0);
    const clampToDay = (start, end) => {
      if (!start || !end) return null;
      const clampedStart = start > bounds.start ? start : bounds.start;
      const clampedEnd = end < bounds.end ? end : bounds.end;
      return clampedStart < clampedEnd ? { start: clampedStart, end: clampedEnd } : null;
    };

    const portContexts = (dayData?.dayPocs || []).flatMap((poc) => {
      const arrival = parsePortVisitBoundaryDate(poc?.day_arrival, 0, 0, 0, 0);
      const departure = parsePortVisitBoundaryDate(
        poc?.day_departure || poc?.day_arrival,
        23,
        59,
        59,
        999
      );
      if (!arrival || !departure || departure <= bounds.start) return [];
      const window = {
        start: arrival > bounds.start ? arrival : bounds.start,
        // Weather's final 22:00 - 06:00 block crosses into the next day.
        // Keep an overnight berth visible in its narrative without making the
        // selected day's forecast responsible for later next-day conditions.
        end: departure < overnightForecastEnd ? departure : overnightForecastEnd,
      };
      const name = poc?.poc_info?.name;
      return window.start < window.end && name ? [{ type: 'port', name, ...window }] : [];
    });
    const segmentContexts = (dayData?.segmentEntries || []).flatMap((entry) => {
      const fromName = entry?.fromPoc?.poc_info?.name;
      const toName = entry?.toPoc?.poc_info?.name;
      const window = clampToDay(
        parseSegmentBoundaryDate(entry?.fromPoc?.day_departure || entry?.fromPoc?.day_arrival, 12),
        parseSegmentBoundaryDate(entry?.toPoc?.day_arrival || entry?.toPoc?.day_departure, 12)
      );
      if (!window || !fromName || !toName) return [];
      const routeMode = resolveTourSegmentRouteMode(entry.fromPoc, entry.toPoc, entry.segment);
      return [{
        type: ['sea', 'coastal', 'international'].includes(routeMode) ? 'sailing' : 'travel',
        fromName,
        toName,
        ...window,
      }];
    });

    return [...portContexts, ...segmentContexts]
      .sort((a, b) => a.start - b.start || a.end - b.end);
  }, []);

  const openDayWeatherDetails = useCallback((dayData) => {
    if (!dayData?.dayKey) return false;
    const forecastPort = (dayData.dayPocs || []).find((poc) => getPortFullDayForecast(poc, dayData.dayKey)?.hourly);
    if (!forecastPort) return false;
    const forecast = getPortFullDayForecast(forecastPort, dayData.dayKey);
    setSelectedWeatherData(forecast);
    setSelectedLocationName(
      (dayData.dayPocs || [])
        .map((poc) => poc?.poc_info?.name)
        .filter(Boolean)
        .join(' / ') || forecastPort?.poc_info?.name || 'Weather'
    );
    setSelectedDateRange(formatDayTitle(dayData));
    setSelectedWeatherStayWindow(null);
    setSelectedWeatherItineraryContexts(buildDayWeatherItineraryContexts(dayData));
    setWeatherModalOpen(true);
    return true;
  }, [buildDayWeatherItineraryContexts, formatDayTitle]);

  const formatPortListForHeading = (ports = []) => {
    const names = ports.map((poc) => poc?.poc_info?.name).filter(Boolean);
    if (!names.length) return 'At sea';
    if (names.length === 1) return names[0];
    if (names.length === 2) return `${names[0]} & ${names[1]}`;
    return `${names.slice(0, -1).join(', ')} & ${names[names.length - 1]}`;
  };

  const renderPortBadges = (dayData, { large = false } = {}) => (
    <div
      className='TourUser-day-port-badges'
      style={{
        display: 'flex',
        justifyContent: 'flex-end',
        alignItems: 'center',
        gap: large ? '7px 8px' : '4px 6px',
        flexWrap: 'wrap',
      }}
    >
      {(dayData?.dayPocs || []).map((poc) => (
        <span
          key={poc.id}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            backgroundColor: getHolidayBadgeColor(poc, dayData.dayKey),
            padding: large ? '5px 9px' : '2px 7px',
            borderRadius: 999,
            maxWidth: '100%',
            minWidth: 0,
          }}
        >
          <span
            style={{
              width: large ? 22 : 16,
              height: large ? 22 : 16,
              borderRadius: '50%',
              backgroundColor: '#007bff',
              color: '#fff',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: large ? 11 : 9,
              fontWeight: 'bold',
              marginRight: 5,
              flex: '0 0 auto',
            }}
          >
            {poc.visit_order}
          </span>
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {poc.poc_info.name}
          </span>
        </span>
      ))}
    </div>
  );
  const dayCardStyle = {
    margin: isMobile ? '0 0 10px 0' : '0 0 12px 0',
    width: '100%',
    backgroundColor: '#ffffff',
    border: '1px solid #dbe3ee',
    borderRadius: 12,
    boxShadow: '0 2px 8px rgba(15, 23, 42, 0.08)',
    transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
    cursor: 'pointer',
  };
  const expandedDayCardStyle = {
    ...dayCardStyle,
    border: '1px solid #93c5fd',
    boxShadow: '0 4px 14px rgba(37, 99, 235, 0.14)',
  };
  const renderSegmentSummary = (entry) => {
    if (!entry) return null;

    const segment = entry.segment;
    const mode = resolveTourSegmentRouteMode(entry.fromPoc, entry.toPoc, segment);
    const transportFamily = segment?.transport_family || (mode === 'coastal' || mode === 'international' ? 'sea' : mode);
    const isRiver = transportFamily === 'river';
    const isRoad = transportFamily === 'road';
    const isSea = transportFamily === 'sea';
    const iconName = isRoad ? 'car' : 'ship';
    const title = isRiver ? 'River segment' : isRoad ? 'Road segment' : isSea ? 'Cruising segment' : 'Segment';
    const distanceValue = Number(segment?.distance_km);
    const hasDistance = Number.isFinite(distanceValue);
    const unavailableMessage = segment?.source === 'cache_miss'
      ? 'Summary not cached yet.'
      : 'Navigation summary unavailable.';
    const segmentEvents = sortTourEvents(
      (entry.fromPoc?.events || []).filter(
        (event) =>
          event?.placement === 'segment' &&
          String(event.segment_end_port_of_call_id) === String(entry.toPoc?.id) &&
          doesTourEventMatchDay(event, entry.dayKey, entry.fromPoc?.day_arrival)
      )
    );
    const portEventsCount = (entry.fromPoc?.events || []).filter(
      (event) =>
        (event?.placement || 'port') === 'port' &&
        doesTourEventMatchDay(event, entry.dayKey, entry.fromPoc?.day_arrival)
    ).length;

    return (
      <div
        className='TourUser-segment-summary'
        key={entry.key}
        onClick={(e) => {
          e.stopPropagation();
        }}
        style={{
          border: '1px solid #dbe3ee',
          borderRadius: 10,
          padding: isMobile ? '8px 10px' : '9px 12px',
          background: '#f8fbff',
        }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: '10px',
            marginBottom: 4,
            flexWrap: 'wrap',
          }}
        >
          <span style={{ fontWeight: 700, color: '#1f2937', display: 'inline-flex', alignItems: 'center' }}>
            <Icon name={iconName} size='small' style={{ marginRight: 6, color: isRoad ? '#7c3aed' : '#0284c7' }} />
            {title}
          </span>
          <span style={{ fontSize: 12, color: '#64748b' }}>
            {entry.fromPoc?.poc_info?.name} → {entry.toPoc?.poc_info?.name}
          </span>
        </div>

        {segment?.available ? (
          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', fontSize: 12, color: '#334155' }}>
            {hasDistance && (
              <span>
                <Icon name='arrows alternate horizontal' size='small' style={{ marginRight: 4 }} />
                {formatDistanceKm(distanceValue, mode || transportFamily, unitPreferences)}
              </span>
            )}
            {isRiver && (
              <span>
                <Icon name='lock' size='small' style={{ marginRight: 4 }} />
                {Number(segment?.lock_count || 0)} lock{Number(segment?.lock_count || 0) === 1 ? '' : 's'}
              </span>
            )}
          </div>
        ) : (
          <div style={{ fontSize: 12, color: '#7c2d12' }}>
            {unavailableMessage}
          </div>
        )}

        {segmentEvents.length > 0 ? (
          <div style={{ marginTop: 8, borderTop: '1px solid #e5e7eb', paddingTop: 6 }}>
            {segmentEvents.map((event, eventIndex) => {
              const travel = getTourEventTravelSummary(event, entry.fromPoc, entry.toPoc);
              const modeForEvent = travel?.mode || normalizeEventRouteMode(event?.route_mode, transportFamily || 'road');
              const routeLabel = getTourEventLocationLabel(event);
              return (
                <div
                  key={event.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                    fontSize: 12,
                    color: '#334155',
                    marginTop: eventIndex > 0 ? 4 : 0,
                  }}
                >
                  <span
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      minWidth: 24,
                      height: 18,
                      padding: '0 5px',
                      borderRadius: 999,
                      background: '#14b8a6',
                      color: '#fff',
                      fontWeight: 700,
                      fontSize: 10,
                    }}
                  >
                    {getTourEventMarkerLabel(event, entry.fromPoc, portEventsCount + eventIndex)}
                  </span>
                  <span style={{ fontWeight: 600 }}>{getTourEventName(event)}</span>
                  {routeLabel ? <span style={{ color: '#64748b' }}>{routeLabel}</span> : null}
                  <span style={{ color: '#64748b' }}>
                    <Icon name={modeForEvent === 'road' ? 'car' : modeForEvent === 'river' ? 'ship' : 'anchor'} />
                    {travel?.distanceLabel || 'Waypoint'}
                    {travel?.timeLabel && travel.timeLabel !== '—' ? `, ${travel.timeLabel}` : ''}
                  </span>
                  {getShortWeatherSummary(event.weather) ? (
                    <span style={{ color: '#64748b' }}>
                      <Icon name='cloud' />
                      {getShortWeatherSummary(event.weather)}
                    </span>
                  ) : null}
                </div>
              );
            })}
          </div>
        ) : null}
      </div>
    );
  };
  const renderResourcesCard = (carousel = false) => (
    <Card
      key='tour-resources'
      style={{
        ...dayCardStyle,
        ...(carousel ? {
          margin: 0,
          flex: '0 0 auto',
          width: isMobile ? '82%' : '320px',
          scrollSnapAlign: 'start',
          alignSelf: 'stretch',
        } : {}),
      }}
    >
      <Card.Content style={{ padding: isMobile ? '10px' : '12px' }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '10px',
            flexWrap: 'wrap',
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'center', gap: '8px', flexWrap: 'wrap' }}>
            <Button
              className='TourUser-resources-button'
              size='small'
              icon='folder open'
              label={{
                basic: true,
                content: tourResourceCount,
                color: tourResourceCount > 0 ? 'blue' : 'grey'
              }}
              onClick={() => setTourMapsModalOpen(true)}
              title={tourResourceCount > 0 ? `View ${tourResourceCount} tour resource(s)` : 'No tour resources available'}
              disabled={tourResourceCount === 0}
              style={actionButtonStyle}
            />
          </div>
          <Button.Group className='TourUser-view-mode-controls' size='small'>
            <Button
              type='button'
              icon='list ul'
              active={itineraryViewMode === 'vertical'}
              title='Vertical itinerary'
              aria-label='Switch to vertical itinerary'
              onClick={() => setItineraryViewMode('vertical')}
              style={actionButtonStyle}
            />
            <Button
              type='button'
              icon='columns'
              active={itineraryViewMode === 'carousel'}
              title='Horizontal itinerary carousel'
              aria-label='Switch to itinerary carousel'
              onClick={() => setItineraryViewMode('carousel')}
              style={actionButtonStyle}
            />
          </Button.Group>
          <Button
            type='button'
            size='small'
            icon='info circle'
            title='Show itinerary help'
            aria-label='Show itinerary help'
            onClick={startTutorial}
            style={{
              ...actionButtonStyle,
              backgroundColor: '#f97316',
              color: '#ffffff',
              borderColor: '#ea580c',
            }}
          />
        </div>
      </Card.Content>
    </Card>
  );

  const renderDayCard = (dayData, carousel = false) => {
    const dayLabel = dayData.date.toLocaleDateString('en-US', { weekday: 'short', month: '2-digit', day: '2-digit' });
    const isSelected = expandedDays.has(dayData.dayKey);
    const showExpanded = carousel || isSelected;

    return (
      <Card
        className='TourUser-day-card'
        key={dayData.dayKey}
        id={`day-${dayData.dayKey}`}
        style={{
          ...(isSelected ? expandedDayCardStyle : dayCardStyle),
          ...(carousel ? {
            margin: 0,
            flex: '0 0 auto',
            width: isMobile ? '88%' : '38rem',
            maxWidth: '100%',
            height: '100%',
            minHeight: 0,
            scrollSnapAlign: 'start',
          } : {}),
        }}
        onClick={() => handleDayCardClick(dayData, carousel)}
      >
        <Card.Content
          style={{
            padding: isMobile ? '10px' : '12px',
            ...(carousel ? {
              display: 'flex',
              flexDirection: 'column',
              height: '100%',
              minHeight: 0,
            } : {}),
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flex: '0 0 auto' }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: 6 }}>
                <Icon
                  name={dayData.isDayAtSea ? 'ship' : 'anchor'}
                  style={{ marginRight: 8, color: '#007bff' }}
                />
                <span style={{ fontSize: 16, fontWeight: 700, color: '#1f2937' }}>
                  {dayLabel}
                </span>
              </div>

              {dayData.hasVisits && !showExpanded && (() => {
                const dayForecasts = dayData.dayPocs
                  .map((poc) => getPortForecastForDay(poc, dayData.dayKey))
                  .filter((forecast) => forecast?.hourly);
                const allTemps = dayForecasts.map((forecast) => forecast.hourly.temperature_2m).filter(Boolean).flat();
                if (!allTemps.length) return null;

                const tempRange = getValidMinMax(allTemps);
                const allPrecip = filterValidWeatherData(dayForecasts.map((forecast) => forecast.hourly.precipitation).filter(Boolean).flat());
                const allWind = filterValidWeatherData(dayForecasts.map((forecast) => forecast.hourly.wind_speed_10m).filter(Boolean).flat());

                const totalPrecip = allPrecip.reduce((a, b) => a + b, 0);
                const maxWindKmh = allWind.length > 0 ? Math.max(...allWind) : 0;

                const weatherMetricStyle = {
                  display: 'inline-flex',
                  alignItems: 'center',
                  whiteSpace: 'nowrap',
                  lineHeight: 1.25,
                };

                return (
                  <div
                    style={{
                      fontSize: 12,
                      color: '#6c757d',
                      display: 'flex',
                      alignItems: 'center',
                      columnGap: 8,
                      rowGap: 4,
                      flexWrap: 'wrap',
                    }}
                  >
                    {tempRange.min !== null && tempRange.max !== null && (
                      <span style={weatherMetricStyle}>
                        <Icon name='thermometer half' size='small' style={{ marginRight: 3, color: '#dc3545' }} />
                        {tempRange.min.toFixed(0)}-{tempRange.max.toFixed(0)}°C
                      </span>
                    )}
                    {totalPrecip > 0 && (
                      <span style={{ ...weatherMetricStyle, color: '#007bff' }}>
                        <Icon name='rain' size='small' style={{ marginRight: 3 }} />
                        {Math.round(totalPrecip)}mm
                      </span>
                    )}
                    {maxWindKmh > 0 && (
                      <span style={{ ...weatherMetricStyle, color: maxWindKmh > 27.78 ? '#dc3545' : '#ffc107' }}>
                        <Icon name='paper plane' size='small' style={{ marginRight: 3 }} />
                        {formatSpeedKmh(maxWindKmh, isSeaCruiseTourType(activeTourToShow?.tour_type) ? 'sea' : 'road', unitPreferences, { digits: 0 })}
                      </span>
                    )}
                  </div>
                );
              })()}
            </div>

            <div style={{ flex: 1, textAlign: 'right', minWidth: isMobile ? '42%' : '35%' }}>
              {dayData.hasVisits ? (
                <div
                  className='TourUser-day-port-badges'
                  style={{
                    fontSize: 13,
                    color: '#495057',
                    display: 'flex',
                    flexWrap: 'wrap',
                    justifyContent: 'flex-end',
                    alignItems: 'center',
                    gap: '4px 6px',
                  }}
                >
                  {dayData.dayPocs.map((poc) => (
                    <span
                      key={poc.id}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        backgroundColor: getHolidayBadgeColor(poc, dayData.dayKey),
                        padding: '2px 7px',
                        borderRadius: 999,
                        maxWidth: '100%',
                        minWidth: 0,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      <span
                        style={{
                          width: 16,
                          height: 16,
                          borderRadius: '50%',
                          backgroundColor: '#007bff',
                          color: '#fff',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: 9,
                          fontWeight: 'bold',
                          marginRight: 4,
                          flex: '0 0 auto',
                        }}
                      >
                        {poc.visit_order}
                      </span>
                      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {poc.poc_info.name}
                      </span>
                    </span>
                  ))}
                </div>
              ) : (
                <div style={{ fontSize: 13, color: '#856404', fontStyle: 'italic', textAlign: 'right' }}>
                  Day at Sea
                </div>
              )}
            </div>
          </div>

          {showExpanded && (dayData.hasVisits || dayData.segmentEntries?.length > 0) && (
            <div
              style={{
                marginTop: '10px',
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                gap: '10px',
                backgroundColor: 'white',
                ...(carousel ? {
                  flex: '1 1 auto',
                  minHeight: 0,
                  overflowY: 'auto',
                  paddingRight: '2px',
                } : {}),
              }}
            >
              {dayData.segmentEntries
                .filter((entry) => entry.position === 'inbound' || entry.position === 'spanning')
                .map((entry) => renderSegmentSummary(entry))}

              {dayData.hasVisits ? dayData.dayPocs.map((poc, index) => {
                const dayWeather = getPortForecastForDay(poc, dayData.dayKey);
                const modalWeather = getPortFullDayForecast(poc, dayData.dayKey);
                const weatherStayWindow = getPortVisitWindowForDay(poc, dayData.dayKey);
                const daySpecificPoc = {
                  ...poc,
                  weather: dayWeather,
                  weatherModal: modalWeather,
                  weatherModalDayKey: dayData.dayKey,
                  weatherStayWindow,
                  events: (poc?.events || []).filter((event) =>
                    doesTourEventMatchDay(event, dayData.dayKey, poc?.day_arrival)
                  ),
                  holiday_info: getHolidaysForDay(poc?.holiday_info, dayData.dayKey),
                  holiday_clients: getHolidaysForDay(poc?.holiday_clients, dayData.dayKey),
                };
                const betweenEntries = dayData.segmentEntries.filter(
                  (entry) => entry.position === 'between' && entry.anchorIndex === index
                );

                return (
                  <React.Fragment key={poc.id}>
                    <div
                      className='TourUser-port-card'
                      style={{ marginBottom: index < dayData.dayPocs.length - 1 ? '6px' : '0' }}
                      onClick={(e) => {
                        e.stopPropagation();
                        fitMapToPort(poc);
                      }}
                    >
                      <PortOfCallCardTourUser
                        poc={daySpecificPoc}
                        activeTourToShow={activeTourToShow}
                        selectedCustomerMap={selectedCustomerMap}
                        setSelectedCustomerMap={setSelectedCustomerMap}
                        onOpenMapInApp={onOpenMapInApp}
                        handleWeatherClick={(p) => {
                          const modalWeather = p?.weatherModal || p?.weather;
                          if (modalWeather?.hourly) {
                            setSelectedWeatherData(modalWeather);
                            setSelectedWeatherItineraryContexts([]);
                            setSelectedLocationName(p.poc_info.name);
                            const modalDayDate = p.weatherModalDayKey
                              ? new Date(`${p.weatherModalDayKey}T00:00:00`)
                              : null;
                            const arrivalDate = new Date(p.day_arrival).toLocaleDateString('en-US', {
                              weekday: "short", month: '2-digit', day: '2-digit'
                            });
                            const departureDate = new Date(p.day_departure).toLocaleDateString('en-US', {
                              weekday: "short", month: '2-digit', day: '2-digit'
                            });
                            const dateRange = modalDayDate && !Number.isNaN(modalDayDate.getTime())
                              ? modalDayDate.toLocaleDateString('en-US', { weekday: "short", month: '2-digit', day: '2-digit' })
                              : (p.day_arrival === p.day_departure ? arrivalDate : `${arrivalDate} - ${departureDate}`);
                            setSelectedDateRange(dateRange);
                            setSelectedWeatherStayWindow(p.weatherStayWindow || null);
                            setWeatherModalOpen(true);
                          }
                        }}
                        getValidMinMax={getValidMinMax}
                        filterValidWeatherData={filterValidWeatherData}
                        getTourEventName={getTourEventName}
                        getTourEventLocationLabel={getTourEventLocationLabel}
                        getTourEventTravelSummary={getTourEventTravelSummary}
                        getTourEventMarkerLabel={getTourEventMarkerLabel}
                        getShortWeatherSummary={getShortWeatherSummary}
                        onShowDayDetails={() => {
                          selectGuestDay(dayData, { focusMap: false });
                          if (isMobileGuestView) setMobileTab('today');
                          if (isLobbyGuestView) setLobbyTab('today');
                        }}
                        isMobile={isMobile}
                        dayFiles={(activeTourToShow?.files || []).filter((file) => Number(file?.day_number) === Number(dayData.dayNumber))}
                      />
                    </div>
                    {betweenEntries.map((entry) => renderSegmentSummary(entry))}
                  </React.Fragment>
                );
              }) : (
                <div style={{ fontSize: 13, color: '#856404', fontStyle: 'italic' }}>
                  Day at Sea
                </div>
              )}

              {dayData.segmentEntries
                .filter((entry) => entry.position === 'outbound')
                .map((entry) => renderSegmentSummary(entry))}
            </div>
          )}
        </Card.Content>
      </Card>
    );
  };

  const renderSelectedDayWeatherWindows = (dayData, { mobile = false } = {}) => {
    if (!dayData?.dayKey) {
      return <div style={{ color: '#64748b' }}>Forecast not available for this day yet.</div>;
    }

    const viewportWidth = typeof window !== 'undefined' ? window.innerWidth : 390;
    const mobileWindowCount = viewportWidth < 340 ? 2 : viewportWidth < 390 ? 3 : viewportWidth < 460 ? 4 : 5;
    const windows = mobile
      ? getCondensedForecastWindowBounds(dayData.dayKey, mobileWindowCount)
      : getForecastWindowBounds(dayData.dayKey);
    const seaSegments = (dayData.segmentEntries || []).filter((entry) => {
      const mode = resolveTourSegmentRouteMode(entry.fromPoc, entry.toPoc, entry.segment);
      return ['sea', 'coastal', 'international'].includes(mode);
    });
    const fullDayForecastPort = (dayData.dayPocs || []).find((poc) =>
      getPortFullDayForecast(poc, dayData.dayKey)?.hourly
    );
    const fullDayForecast = fullDayForecastPort
      ? getPortFullDayForecast(fullDayForecastPort, dayData.dayKey)
      : null;
    const hasPortForecast = Boolean(fullDayForecast?.hourly?.time?.length) || (dayData.dayPocs || []).some((poc) => {
      const visitWindow = getPortVisitWindowForDay(poc, dayData.dayKey);
      if (!visitWindow) return false;
      return (sliceHourlyForecastByWindow(poc?.weather, visitWindow.start, visitWindow.end)
        ?.hourly?.time?.length || 0) > 0;
    });

    if (!hasPortForecast) {
      return (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#64748b', fontWeight: 800 }}>
          <Icon name='cloud' style={{ margin: 0 }} />
          <span>Weather forecast not available</span>
        </div>
      );
    }

    const uvPointsByTime = new Map();
    (dayData.dayPocs || []).forEach((poc) => {
      const visitWindow = getPortVisitWindowForDay(poc, dayData.dayKey);
      const times = poc?.weather?.hourly?.time;
      const values = poc?.weather?.hourly?.uv_index;
      if (!visitWindow || !Array.isArray(times) || !Array.isArray(values)) return;

      times.forEach((timeValue, index) => {
        const time = new Date(timeValue);
        const value = Number(values[index]);
        if (!Number.isFinite(time.getTime()) || !Number.isFinite(value)) return;
        if (time < visitWindow.start || time > visitWindow.end) return;
        const key = time.getTime();
        if (!uvPointsByTime.has(key) || value > uvPointsByTime.get(key).value) {
          uvPointsByTime.set(key, { time, value });
        }
      });
    });
    const uvTimeline = Array.from(uvPointsByTime.values()).sort((a, b) => a.time - b.time);

    const getUvRange = (threshold, { minimum = false } = {}) => {
      const matchesThreshold = (value) => (minimum ? value >= threshold : value > threshold);
      const startIndex = uvTimeline.findIndex((point) => matchesThreshold(point.value));
      if (startIndex < 0) return null;

      let lastIndex = startIndex;
      for (let index = startIndex + 1; index < uvTimeline.length; index += 1) {
        if (!matchesThreshold(uvTimeline[index].value)) break;
        lastIndex = index;
      }

      const startsAt = uvTimeline[startIndex].time;
      const firstBelow = uvTimeline[lastIndex + 1];
      const endsAt = firstBelow?.time || new Date(uvTimeline[lastIndex].time.getTime() + (60 * 60 * 1000));
      return {
        startsAt,
        endsAt,
        durationMinutes: Math.max(0, Math.round((endsAt.getTime() - startsAt.getTime()) / 60000)),
      };
    };

    const uvAlertRange = getUvRange(3, { minimum: true });
    const uvMax = uvTimeline.length ? Math.max(...uvTimeline.map((point) => point.value)) : null;
    const uvNearPeakRange = Number.isFinite(uvMax) && uvMax > 1
      ? getUvRange(Math.max(0, uvMax - 1))
      : null;
    const formatUvTime = (date) => date?.toLocaleTimeString('en-GB', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });

    return (
      <div style={{ width: '100%' }}>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: `repeat(${windows.length || 1}, minmax(0, 1fr))`,
            gap: 0,
            width: '100%',
          }}
        >
          {windows.map((windowInfo, windowIndex) => {
            const activePortEntries = (dayData.dayPocs || [])
              .map((poc) => {
                const visitWindow = getPortVisitWindowForDay(poc, dayData.dayKey);
                if (!visitWindow || !rangesOverlap(windowInfo.start, windowInfo.end, visitWindow.start, visitWindow.end)) return null;
                const start = new Date(Math.max(windowInfo.start.getTime(), visitWindow.start.getTime()));
                const end = new Date(Math.min(windowInfo.end.getTime(), visitWindow.end.getTime()));
                const forecast = sliceHourlyForecastByWindow(poc?.weather, start, end);
                if (!(forecast?.hourly?.time?.length > 0)) return null;
                return {
                  id: poc.id,
                  name: poc?.poc_info?.name || 'Port',
                  forecast,
                };
              })
              .filter(Boolean);
            const fallbackForecast = !activePortEntries.length && fullDayForecast
              ? sliceHourlyForecastByWindow(fullDayForecast, windowInfo.start, windowInfo.end)
              : null;
            const portEntries = activePortEntries.length
              ? activePortEntries
              : (fallbackForecast?.hourly?.time?.length > 0
                ? [{
                    id: `full-day-${fullDayForecastPort?.id || 'weather'}`,
                    name: fullDayForecastPort?.poc_info?.name || 'Weather',
                    forecast: fallbackForecast,
                  }]
                : []);

          const sailingEntries = seaSegments
            .map((entry) => {
              const start = parseSegmentBoundaryDate(entry.fromPoc?.day_departure || entry.fromPoc?.day_arrival, 12);
              const end = parseSegmentBoundaryDate(entry.toPoc?.day_arrival || entry.toPoc?.day_departure, 12);
              if (!rangesOverlap(windowInfo.start, windowInfo.end, start, end)) return null;
              const key = `${dayData.dayKey}-${entry.fromPoc?.id}-${entry.toPoc?.id}`;
              const overlapStart = new Date(Math.max(windowInfo.start.getTime(), start.getTime()));
              const overlapEnd = new Date(Math.min(windowInfo.end.getTime(), end.getTime()));
              const waveHeight = maxHourlyValueInWindow(marineForecastBySegmentKey[key], 'wave_height', overlapStart, overlapEnd);
              return {
                id: key,
                name: `${entry.fromPoc?.poc_info?.name || 'Port'} → ${entry.toPoc?.poc_info?.name || 'Port'}`,
                waveHeight,
              };
            })
            .filter(Boolean);

          const weatherSummary = summarizeWindowWeather(portEntries, windowInfo.start, windowInfo.end);
          const maxWaveHeight = sailingEntries
            .map((entry) => entry.waveHeight)
            .filter((value) => Number.isFinite(value))
            .reduce((max, value) => Math.max(max, value), null);
          return (
            <div
              key={windowInfo.label}
              style={{
                border: 'none',
                borderLeft: windowIndex > 0 ? '1px solid #e5e7eb' : 'none',
                padding: mobile ? '4px 3px' : '6px 9px',
                background: 'transparent',
                minWidth: 0,
                textAlign: 'center',
              }}
            >
              <div style={{ fontWeight: 800, color: '#334155', marginBottom: mobile ? 5 : 8, fontSize: mobile ? 10 : 12 }}>
                {formatHourRangeLabel(windowInfo.start, windowInfo.end)}
              </div>
              <div style={{ display: 'grid', gap: mobile ? 4 : 6, justifyItems: 'center' }}>
                <div style={{ minHeight: mobile ? 16 : 22, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Icon
                    name={weatherSummary.icon}
                    style={{
                      color: weatherSummary.precipitationTotal > 0 ? '#0ea5e9' : '#f59e0b',
                      fontSize: mobile ? 15 : 21,
                      margin: 0,
                    }}
                  />
                </div>
                <div
                  style={{
                    height: mobile ? 28 : 38,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: 800,
                    color: '#111827',
                    fontSize: mobile ? 12 : 16,
                    lineHeight: 1.05,
                    whiteSpace: 'pre-line',
                  }}
                >
                  {formatTemperatureSummary(weatherSummary)}
                </div>
                <div style={{ minHeight: mobile ? 14 : 18, color: '#334155', fontSize: mobile ? 10 : 12, lineHeight: 1.15, whiteSpace: 'nowrap' }}>
                  <Icon name='paper plane' style={{ color: '#64748b', marginRight: 3 }} />
                  {Number.isFinite(weatherSummary.windMax)
                    ? formatSpeedKmh(weatherSummary.windMax, isSeaCruiseTourType(activeTourToShow?.tour_type) ? 'sea' : 'road', unitPreferences, { digits: 0 })
                    : '—'}
                </div>
                <div style={{ minHeight: mobile ? 14 : 18, color: sailingEntries.length > 0 ? '#334155' : '#94a3b8', fontSize: mobile ? 10 : 12, lineHeight: 1.15, whiteSpace: 'nowrap' }}>
                  <Icon name='water' style={{ color: sailingEntries.length > 0 ? '#0284c7' : '#cbd5e1', marginRight: 3 }} />
                  {sailingEntries.length > 0 && Number.isFinite(maxWaveHeight) ? `${maxWaveHeight.toFixed(1)} m` : '—'}
                </div>
              </div>
            </div>
          );
          })}
        </div>
        {uvAlertRange ? (
          <div
            style={{
              marginTop: mobile ? 8 : 10,
              paddingTop: mobile ? 7 : 9,
              borderTop: '1px solid #e5e7eb',
              display: 'grid',
              gap: mobile ? 3 : 4,
              color: '#475569',
              fontSize: mobile ? 11 : 13,
              lineHeight: 1.3,
            }}
          >
            <div>
              <Icon name='sun' style={{ color: '#f59e0b', marginRight: 5 }} />
              UV reaches 3 at {formatUvTime(uvAlertRange.startsAt)} and falls below 3 at {formatUvTime(uvAlertRange.endsAt)}.
            </div>
            {uvNearPeakRange ? (
              <div>
                <Icon name='sun' style={{ color: '#ef4444', marginRight: 5 }} />
                UV is over {(uvMax - 1).toFixed(1)} from {formatUvTime(uvNearPeakRange.startsAt)} to {formatUvTime(uvNearPeakRange.endsAt)} ({uvNearPeakRange.durationMinutes} min).
              </div>
            ) : null}
          </div>
        ) : null}
      </div>
    );
  };

  const renderCruisingSegmentsList = (entries = []) => {
    if (!entries.length) return null;
    return (
      <div style={{ marginTop: 10, maxWidth: '100%', boxSizing: 'border-box', overflow: 'hidden', border: '1px solid #dbe3ee', borderRadius: 10, background: '#ffffff', padding: '9px 10px' }}>
        <div style={{ fontWeight: 800, color: '#1f2937', marginBottom: 6 }}>
          Cruising Segments:
        </div>
        <div style={{ display: 'grid', gap: 5 }}>
          {entries.map((entry) => {
            const mode = resolveTourSegmentRouteMode(entry.fromPoc, entry.toPoc, entry.segment);
            const distanceValue = Number(entry.segment?.distance_km);
            return (
              <div
                key={entry.key}
                style={{
                  display: 'flex',
                  width: '100%',
                  maxWidth: '100%',
                  boxSizing: 'border-box',
                  overflow: 'hidden',
                  gap: 10,
                  alignItems: 'baseline',
                  color: '#334155',
                  fontSize: 13,
                }}
              >
                <span style={{ flex: '1 1 auto', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {`${entry.fromPoc?.poc_info?.name || 'Port'} -> ${entry.toPoc?.poc_info?.name || 'Port'}`}
                </span>
                <span style={{ flex: '0 0 auto', fontWeight: 800, color: '#1f2937', whiteSpace: 'nowrap' }}>
                  {Number.isFinite(distanceValue) ? formatDistanceKm(distanceValue, mode, unitPreferences) : '—'}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const renderLobbySailingStretchCard = (entry, index, fallbackFromPoc = null, fallbackToPoc = null) => {
    const fromPoc = entry?.fromPoc || fallbackFromPoc;
    const toPoc = entry?.toPoc || fallbackToPoc;
    const fromName = fromPoc?.poc_info?.name || 'Port';
    const toName = toPoc?.poc_info?.name || 'Port';
    const distance = entry?.segment?.distance_km != null
      ? formatSegmentDistance(entry)
      : null;

    return (
      <div
        key={entry?.key || `lobby-sailing-${getTourPortKey(fromPoc)}-${getTourPortKey(toPoc)}-${index}`}
        style={{
          border: '1px solid #dbeafe',
          borderRadius: 12,
          background: '#f8fbff',
          padding: '8px 11px',
          boxShadow: '0 4px 12px rgba(15, 23, 42, 0.05)',
        }}
      >
        <div style={{ display: 'grid', gridTemplateColumns: '26px minmax(0, 1fr) auto', gap: 8, alignItems: 'center' }}>
          <Icon name='ship' style={{ color: '#0284c7', fontSize: 15, margin: 0 }} />
          <div style={{ minWidth: 0, color: '#111827', fontWeight: 900, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {fromName} {'->'} {toName}
          </div>
          {distance ? (
            <div style={{ color: '#334155', fontWeight: 900, whiteSpace: 'nowrap' }}>
              {distance}
            </div>
          ) : null}
        </div>
      </div>
    );
  };

  const renderLobbyPortItineraryCard = (poc, index, dayData) => {
    const photoUrl = getTourPortPhotoUrl(poc);
    const portName = poc?.poc_info?.name || 'Port';
    const portWeather = getShortWeatherSummary(poc?.weather);

    return (
      <div
        key={`lobby-port-${getTourPortKey(poc)}-${index}`}
        style={{
          position: 'relative',
          minHeight: 124,
          border: '1px solid #064e8a',
          borderRadius: 14,
          overflow: 'hidden',
          background: photoUrl
            ? '#0f172a'
            : 'linear-gradient(135deg, rgba(2,34,75,0.98) 0%, rgba(5,91,151,0.92) 62%, rgba(14,116,144,0.76) 100%)',
          boxShadow: '0 8px 22px rgba(15, 23, 42, 0.08)',
        }}
      >
        {photoUrl ? (
            <img
              src={photoUrl}
              alt=""
              aria-hidden="true"
              loading="lazy"
              decoding="async"
              style={{
                position: 'absolute',
              inset: 0,
              width: '100%',
              height: '100%',
              objectFit: 'cover',
            }}
          />
        ) : (
          <div
            aria-hidden='true'
            style={{
              position: 'absolute',
              right: -28,
              bottom: -30,
              width: 185,
              height: 92,
              borderRadius: '55% 45% 0 0',
              background: 'linear-gradient(135deg, rgba(251,146,60,0.55), rgba(14,165,233,0.15))',
              opacity: 0.75,
            }}
          />
        )}
        {photoUrl ? (
          <div
            aria-hidden="true"
            style={{
              position: 'absolute',
              inset: 0,
              background: 'linear-gradient(90deg, rgba(2, 6, 23, 0.82) 0%, rgba(7, 89, 133, 0.64) 52%, rgba(2, 6, 23, 0.28) 100%)',
            }}
          />
        ) : null}
        <Icon name='sun outline' style={{ position: 'absolute', right: 18, top: 18, color: '#fbbf24', fontSize: 30, margin: 0 }} />
        <div style={{ position: 'relative', minHeight: 124, padding: '15px 16px 13px', color: '#ffffff', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
          <div>
            <div style={{ fontSize: 23, lineHeight: 1.08, fontWeight: 900, textShadow: '0 1px 2px rgba(2, 6, 23, 0.32)' }}>
              {portName}
            </div>
            <div style={{ marginTop: 7, color: '#dbeafe', fontWeight: 800 }}>
              {portWeather || 'Weather forecast not available'}
            </div>
          </div>
          <div style={{ marginTop: 14 }}>
            <Button
              size='small'
              icon='map outline'
              content='Open map'
              onClick={() => selectGuestDay(dayData, { focusPort: poc, focusZoom: 14.2 })}
              style={{ borderRadius: 8, fontWeight: 900 }}
            />
          </div>
        </div>
      </div>
    );
  };

  const renderLobbyItineraryCards = (dayData, activeSegments = []) => {
    const dayPorts = (dayData?.dayPocs || []).filter((poc) => poc?.poc_info?.name);
    if (!dayPorts.length && !activeSegments.length) return null;
    if (!dayPorts.length) {
      return (
        <div style={{ display: 'grid', gap: 10 }}>
          {activeSegments.map((entry, index) => renderLobbySailingStretchCard(entry, index))}
        </div>
      );
    }

    const findSegmentBetweenPorts = (fromPoc, toPoc) => activeSegments.find((entry) => (
      getTourPortKey(entry?.fromPoc) === getTourPortKey(fromPoc) &&
      getTourPortKey(entry?.toPoc) === getTourPortKey(toPoc)
    ));

    const cards = [];
    dayPorts.forEach((poc, index) => {
      cards.push(renderLobbyPortItineraryCard(poc, index, dayData));
      const nextPoc = dayPorts[index + 1];
      if (nextPoc) {
        cards.push(renderLobbySailingStretchCard(findSegmentBetweenPorts(poc, nextPoc), index, poc, nextPoc));
      }
    });

    return <div style={{ display: 'grid', gap: 10 }}>{cards}</div>;
  };

  const renderTodayPanel = ({ mobile = false } = {}) => {
    const dayData = selectedDayData || currentDayData;
    const dayEvents = getDayEvents(dayData);
    const routePorts = (dayData?.dayPocs || []).map((poc) => poc?.poc_info?.name).filter(Boolean);
    const activeSegments = (dayData?.segmentEntries || []).filter(Boolean);
    const hasSailing = activeSegments.length > 0;
    const dayHeading = hasSailing
      ? `Cruising Day: ${formatPortListForHeading(dayData?.dayPocs || [])}`
      : `Port day ${formatPortListForHeading(dayData?.dayPocs || [])}`;
    const hasWeatherForecastForDay = dayData?.dayKey && (dayData.dayPocs || []).some((poc) => {
      const visitWindow = getPortVisitWindowForDay(poc, dayData.dayKey);
      if (!visitWindow) return false;
      return (sliceHourlyForecastByWindow(poc?.weather, visitWindow.start, visitWindow.end)
        ?.hourly?.time?.length || 0) > 0;
    });

    return (
      <div className={mobile ? 'TourUser-mobile-today' : 'TourUser-lobby-today'} style={{ display: 'grid', gap: mobile ? 10 : 14 }}>
        <div style={{ border: '1px solid #dbe3ee', borderRadius: 14, padding: mobile ? 11 : 14, background: '#f8fbff' }}>
          <div style={{ fontWeight: 800, color: '#1f2937', marginBottom: 6 }}>
            <Icon name={hasSailing ? 'ship' : 'anchor'} style={{ color: '#0284c7' }} />
            {dayHeading}
          </div>
          {activeSegments.length > 0 ? (
            renderCruisingSegmentsList(activeSegments)
          ) : isFullTourPending && routePorts.length > 1 ? (
            renderPendingSpace('Loading cruising itinerary…', { minHeight: 54 })
          ) : (
            <div style={{ color: routePorts.length ? '#334155' : '#64748b', fontSize: mobile ? 14 : 16 }}>
              {routePorts.length ? routePorts.join(' → ') : 'Day at sea'}
            </div>
          )}
        </div>

        {!mobile ? renderLobbyItineraryCards(dayData, activeSegments) : null}

        {hasWeatherForecastForDay ? (
          <div style={{ border: '1px solid #dbe3ee', borderRadius: 14, padding: mobile ? 11 : 14, background: '#ffffff' }}>
            <div style={{ fontWeight: 800, color: '#1f2937', marginBottom: 6 }}>
              <Icon name='cloud' style={{ color: '#0ea5e9' }} />
              Weather
            </div>
            {renderSelectedDayWeatherWindows(dayData, { mobile })}
            {!mobile ? (
              <Button
                type='button'
                fluid
                basic
                color='blue'
                icon='chart line'
                content='Details'
                onClick={() => openDayWeatherDetails(dayData)}
                style={{ marginTop: 12, fontWeight: 800 }}
              />
            ) : null}
          </div>
        ) : isWeatherLoading ? (
          <div style={{ border: '1px solid #dbe3ee', borderRadius: 14, padding: mobile ? 11 : 14, background: '#ffffff' }}>
            {renderPendingSpace('Loading weather forecast…', { minHeight: mobile ? 66 : 82 })}
          </div>
        ) : (
          <div style={{ border: '1px solid #dbe3ee', borderRadius: 14, padding: mobile ? 11 : 14, background: '#ffffff', display: 'flex', alignItems: 'center', gap: 8, color: '#64748b', fontWeight: 800 }}>
            <Icon name='cloud' style={{ margin: 0 }} />
            <span>Weather forecast not available</span>
          </div>
        )}

        <div style={{ border: '1px solid #dbe3ee', borderRadius: 14, padding: mobile ? 11 : 14, background: '#ffffff' }}>
          <div style={{ fontWeight: 800, color: '#1f2937', marginBottom: 8 }}>
            <Icon name='calendar check outline' style={{ color: '#14b8a6' }} />
            Schedule
          </div>
          {dayEvents.length ? (
            <div style={{ display: 'grid', gap: 8 }}>
              {dayEvents.slice(0, mobile ? 4 : 6).map(({ event, poc }, index) => {
                const eventCoordinates = getEventCoordinates(event, poc);
                const isAwayFromHostPort = Boolean(eventCoordinates && !isEventAtPortLocation(eventCoordinates, poc));
                const eventWeatherSummary = isAwayFromHostPort ? getShortWeatherSummary(event.weather) : null;
                const excursionLocation = getTourEventLocationLabel(event)
                  || (isAwayFromHostPort ? 'Excursion destination' : poc?.poc_info?.name);

                return (
                  <div key={`${event.id || index}-${poc?.id || 'poc'}`} style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                    <span style={{
                      minWidth: 28,
                      height: 22,
                      borderRadius: 999,
                      background: '#14b8a6',
                      color: '#fff',
                      fontWeight: 800,
                      fontSize: 11,
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}>
                      {getTourEventMarkerLabel(event, poc, index)}
                    </span>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontWeight: 700, color: '#1f2937' }}>{getTourEventName(event)}</div>
                      {isAwayFromHostPort ? (
                        <div style={{ fontSize: 12, color: '#64748b', display: 'flex', gap: 5, alignItems: 'center', flexWrap: 'wrap' }}>
                          <span><Icon name='map marker alternate' style={{ marginRight: 3 }} />{excursionLocation}</span>
                          {eventWeatherSummary ? <span><Icon name='cloud' style={{ marginRight: 3 }} />{eventWeatherSummary}</span> : null}
                          {!eventWeatherSummary && isWeatherLoading ? <span>Loading forecast…</span> : null}
                        </div>
                      ) : (
                        <div style={{ fontSize: 12, color: '#64748b' }}>{poc?.poc_info?.name}</div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : isFullTourPending ? (
            renderPendingSpace('Loading schedule…', { minHeight: mobile ? 58 : 76 })
          ) : (
            <div style={{ color: '#64748b' }}>No scheduled events for this day.</div>
          )}
        </div>

        <div style={{ border: '1px solid #dbe3ee', borderRadius: 14, padding: mobile ? 11 : 14, background: '#ffffff' }}>
          <div style={{ fontWeight: 800, color: '#1f2937', marginBottom: 8 }}>
            <Icon name='folder open' style={{ color: '#0b6fcf' }} />
            Maps & files
          </div>
          {renderDayResourceList(dayData, { mobile })}
        </div>

        {mobile ? (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
            <Button fluid primary icon='map marker alternate' content='Show day' onClick={() => selectGuestDay(dayData)} disabled={!dayData} />
            <Button fluid icon='ship' content='Ship' onClick={focusTrackingLocation} disabled={!currentTourPosition} />
          </div>
        ) : null}
      </div>
    );
  };

  const renderLobbyFilesPanel = () => (
    <div style={{ display: 'grid', gap: 14 }}>
      <div style={{ border: '1px solid #dbe3ee', borderRadius: 16, padding: 18, textAlign: 'center', background: '#f8fbff' }}>
        <Header as='h3' style={{ marginTop: 0 }}>Scan to open on your phone</Header>
        {onlineTourUrl ? (
          <>
            <div style={{ display: 'inline-flex', padding: 12, background: '#fff', borderRadius: 14, border: '1px solid #e5e7eb' }}>
              <QRCodeCanvas value={onlineTourUrl} size={180} includeMargin level="M" />
            </div>
            <div style={{ marginTop: 10, color: '#64748b', overflowWrap: 'anywhere' }}>{onlineTourUrl}</div>
          </>
        ) : (
          <div style={{ color: '#64748b' }}>This tour does not have a public link yet.</div>
        )}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
        <Button size='large' icon='map outline' content={`View maps (${tourResourceMaps.length})`} onClick={() => setTourMapsModalOpen(true)} />
        <Button size='large' icon='file alternate outline' content={`Dailies (${dailyTourFiles.length})`} onClick={() => setTourMapsModalOpen(true)} />
      </div>
      <div style={{ border: '1px solid #dbe3ee', borderRadius: 14, padding: 12, background: '#fff' }}>
        <div style={{ fontWeight: 800, marginBottom: 8 }}>Available resources</div>
        <div style={{ display: 'grid', gap: 6, color: '#334155' }}>
          <div><Icon name='map outline' /> Maps: {tourResourceMaps.length}</div>
          <div><Icon name='file alternate outline' /> Dailies: {dailyTourFiles.length}</div>
          <div><Icon name='folder open outline' /> Other files: {otherTourFiles.length}</div>
        </div>
      </div>
    </div>
  );

  const renderAllDaysPanel = ({ mobile = false } = {}) => (
    <div style={{ display: 'grid', gap: mobile ? 10 : 12 }}>
      {processedDaysWithSegments.length ? processedDaysWithSegments.map((dayData) => renderDayCard(dayData, false)) : (
        <div style={{ padding: 20, textAlign: 'center' }}>
          <Header as='h4'>No Ports of Call</Header>
          <p>This tour doesn't have any ports of call defined yet.</p>
        </div>
      )}
    </div>
  );

  const renderFilesScreen = ({ mobile = false } = {}) => (
    <div style={{ display: 'grid', gap: mobile ? 10 : 12 }}>
      {mobile ? (
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'center' }}>
          <Header as='h3' style={{ margin: 0 }}>Files</Header>
          <Button size='small' icon='download' content='Download all' onClick={downloadTourLowResMaps} loading={isDownloadingTourMaps} disabled={isDownloadingTourMaps || tourResourceCount === 0} />
        </div>
      ) : null}
      {tourResourceTabs.length > 0 ? (
        <Tab menu={{ secondary: true, pointing: true }} panes={tourResourceTabs} />
      ) : isFullTourPending ? (
        renderPendingSpace('Loading maps and files…', { minHeight: 140 })
      ) : (
        <div style={{ color: '#6b7280', padding: '6px 2px' }}>No maps or files available for this tour.</div>
      )}
    </div>
  );

  const renderDayResourceList = (dayData, { mobile = false } = {}) => {
    const { maps, files } = getDayResources(dayData);
    const hasResources = maps.length > 0 || files.length > 0;
    if (!hasResources) {
      if (isFullTourPending) {
        return renderPendingSpace('Loading maps and files…', { minHeight: mobile ? 64 : 82 });
      }
      return <div style={{ color: '#64748b' }}>No maps or daily files attached to this day.</div>;
    }

    return (
      <div style={{ display: 'grid', gap: 8 }}>
        {maps.map((resource, index) => {
          const mapProps = resource.mapItem?.properties || resource.mapItem || {};
          const mapName = replaceBrTags(mapProps.name || mapProps.area_name || `Map ${index + 1}`);
          const mapApiKey = mapProps.api_key || resource.mapItem?.api_key;
          const mapOnlineUrl = getDockingMapUrl(mapApiKey, resource.portVisit);
          const highResJpgPath = getMapAssetPath(mapProps, resource.mapItem, [
            'jpg_200dpi_q90_path_no_qr',
            'jpg_200dpi_q90_path',
          ]);
          return (
            <div
              key={resource.key}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 8,
                border: '1px solid #e5e7eb',
                borderRadius: 10,
                padding: mobile ? '8px' : '9px 10px',
                background: '#fff',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 9, minWidth: 0 }}>
                <Icon name='map outline' style={{ color: '#0b6fcf', fontSize: mobile ? 16 : 17, marginTop: 2, flex: '0 0 auto' }} />
                <div style={{ minWidth: 0 }}>
                  <a
                    href={mapOnlineUrl || undefined}
                    onClick={(event) => {
                      if (!mapOnlineUrl) event.preventDefault();
                    }}
                    style={{ color: '#0b6fcf', fontWeight: 800, textDecoration: 'none', overflowWrap: 'anywhere' }}
                  >
                    {mapName}
                  </a>
                  <div style={{ color: '#64748b', fontSize: 12 }}>{resource.sourceLabel}</div>
                </div>
              </div>
              <Button
                size='mini'
                icon='external alternate'
                href={highResJpgPath ? buildMapsUrl(highResJpgPath) : undefined}
                target='_blank'
                disabled={!highResJpgPath}
              />
            </div>
          );
        })}
        {files.map((file) => {
          const fileUrl = file?.download_url || file?.file_url || null;
          const fileName = file?.original_filename || file?.name || 'Unnamed file';
          const fileTitle = file?.title || file?.description || file?.category_name || fileName;
          return (
            <div
              key={`day-file-${file?.id || fileName}`}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 8,
                border: '1px solid #e5e7eb',
                borderRadius: 10,
                padding: mobile ? '8px' : '9px 10px',
                background: '#fff',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 9, minWidth: 0 }}>
                <Icon name='file outline' style={{ color: '#64748b', fontSize: mobile ? 16 : 17, marginTop: 2, flex: '0 0 auto' }} />
                <div style={{ minWidth: 0 }}>
                  <a
                    href={fileUrl || undefined}
                    target='_blank'
                    rel='noreferrer'
                    onClick={(event) => {
                      if (!fileUrl) event.preventDefault();
                    }}
                    style={{ color: fileUrl ? '#0b6fcf' : '#334155', fontWeight: 800, textDecoration: 'none', overflowWrap: 'anywhere' }}
                  >
                    {fileTitle}
                  </a>
                  <div style={{ color: '#64748b', fontSize: 12, overflowWrap: 'anywhere' }}>{fileName}</div>
                </div>
              </div>
              <Button
                size='mini'
                icon='external alternate'
                href={fileUrl || undefined}
                target='_blank'
                disabled={!fileUrl}
              />
            </div>
          );
        })}
      </div>
    );
  };

  const getMapSheetDaySummary = (dayData) => {
    const dayPorts = (dayData?.dayPocs || []).map((poc) => poc?.poc_info?.name).filter(Boolean);
    const segments = (dayData?.segmentEntries || []).filter(Boolean);
    const totalDistanceKm = segments.reduce((total, entry) => {
      const value = Number(entry?.segment?.distance_km);
      return Number.isFinite(value) ? total + value : total;
    }, 0);
    const routeText = dayPorts.length >= 2
      ? `${dayPorts[0]} -> ${dayPorts[dayPorts.length - 1]}`
      : dayPorts[0] || 'Selected day';
    const typeText = segments.length ? 'Cruising day' : 'Port day';
    const distanceText = segments.length && Number.isFinite(totalDistanceKm) && totalDistanceKm > 0
      ? formatDistanceKm(totalDistanceKm, resolveTourSegmentRouteMode(segments[0].fromPoc, segments[0].toPoc, segments[0].segment), unitPreferences)
      : null;
    return { routeText, typeText, distanceText, segments };
  };

  const getCurrentMapActivity = () => {
    const now = new Date();
    const currentPort = pocs.find((poc) => {
      const start = parseSegmentBoundaryDate(poc?.day_arrival, 12);
      const end = parseSegmentBoundaryDate(poc?.day_departure || poc?.day_arrival, 12);
      return start && end && now >= start && now <= end;
    });

    // The scheduled itinerary can say the ship is already in port while the
    // tracked position still shows an approach. Prefer that real-world state
    // whenever the update is recent and outside the destination's dock area.
    if (currentPort && currentTrackingCoords && isFreshShipPosition(currentTourPosition, now)) {
      const currentPortCoords = getTourUserPortCoordinates(currentPort);
      const distanceToCurrentPortKm = currentPortCoords
        ? calculateDistanceKm(
            currentTrackingCoords[0],
            currentTrackingCoords[1],
            currentPortCoords[0],
            currentPortCoords[1]
          )
        : null;
      if (Number.isFinite(distanceToCurrentPortKm) && distanceToCurrentPortKm > LIVE_SHIP_DOCKING_RADIUS_KM) {
        const currentPortIndex = pocs.findIndex((poc) => String(poc?.id) === String(currentPort?.id));
        const previousPort = currentPortIndex > 0 ? pocs[currentPortIndex - 1] : null;
        const segmentKey = previousPort ? `${previousPort.id}-${currentPort.id}` : null;
        const plannedSegment = segmentKey ? navigationSegmentsByPair.get(segmentKey) : null;
        const routeMode = previousPort
          ? resolveTourSegmentRouteMode(previousPort, currentPort, plannedSegment)
          : 'coastal';
        return {
          title: 'Ship visible',
          detail: [
            `On the way to ${currentPort?.poc_info?.name || 'next port'}`,
            `${formatDistanceKm(distanceToCurrentPortKm, routeMode, unitPreferences)} remaining`,
          ].join(' · '),
        };
      }
    }

    if (currentPort) {
      return {
        title: currentTourPosition ? 'Ship visible' : 'Ship position unavailable',
        detail: `Docked in ${currentPort?.poc_info?.name || 'current port'}`,
      };
    }

    const currentSegment = pocs.slice(0, -1).map((fromPoc, index) => ({
      fromPoc,
      toPoc: pocs[index + 1],
    })).find(({ fromPoc, toPoc }) => isNowWithinSegmentWindow(fromPoc, toPoc, now));

    if (currentSegment) {
      const segmentKey = `${currentSegment.fromPoc?.id}-${currentSegment.toPoc?.id}`;
      const directRemainingKm = currentTrackingCoords && getTourUserPortCoordinates(currentSegment.toPoc)
        ? calculateDistanceKm(
            currentTrackingCoords[0],
            currentTrackingCoords[1],
            getTourUserPortCoordinates(currentSegment.toPoc)[0],
            getTourUserPortCoordinates(currentSegment.toPoc)[1]
          )
        : null;
      const remainingKm = (
        routedCurrentRemaining?.segmentKey === segmentKey
        && routedCurrentRemaining?.coordinateKey === currentTrackingCoordsKey
        && Number.isFinite(routedCurrentRemaining?.distanceKm)
      )
        ? routedCurrentRemaining.distanceKm
        : (isRouteGeometryLoading ? null : directRemainingKm);
      const segment = navigationSegmentsByPair.get(segmentKey);
      const mode = resolveTourSegmentRouteMode(currentSegment.fromPoc, currentSegment.toPoc, segment);
      return {
        title: currentTourPosition ? 'Ship visible' : 'Ship position unavailable',
        detail: [
          `On the way to ${currentSegment.toPoc?.poc_info?.name || 'next port'}`,
          Number.isFinite(remainingKm) ? `${formatDistanceKm(remainingKm, mode, unitPreferences)} remaining` : null,
        ].filter(Boolean).join(' · '),
      };
    }

	    const fallbackDay = currentDayData || selectedDayData;
	    const fallbackSummary = getMapSheetDaySummary(fallbackDay);
	    if (!fallbackSummary.segments?.length && fallbackDay?.dayPocs?.length) {
	      const portName = fallbackDay.dayPocs[0]?.poc_info?.name;
	      if (portName) {
	        return {
	          title: currentTourPosition ? 'Ship visible' : 'Ship position unavailable',
	          detail: `Docked in ${portName}`,
	        };
	      }
	    }
	    return {
	      title: currentTourPosition ? 'Ship visible' : 'Ship position unavailable',
	      detail: fallbackSummary.routeText ? `${fallbackSummary.routeText} visible` : 'Current route visible',
	    };
  };

  const getSelectedMapItemDetails = (selection) => {
    if (!selection) return null;
    const findDay = (dayKey) => processedDaysWithSegments.find((dayData) => dayData.dayKey === dayKey) || null;
    const formatDay = (dayData) => (dayData ? formatDayTitle(dayData, { short: true }) : 'day not set');

    if (selection.kind === 'segment') {
      const dayData = findDay(selection.dayKey)
        || processedDaysWithSegments.find((candidate) => (
          (candidate.segmentEntries || []).some((entry) => `${entry.fromPoc?.id}-${entry.toPoc?.id}` === selection.segmentKey)
        ));
      const entry = (dayData?.segmentEntries || []).find((candidate) => `${candidate.fromPoc?.id}-${candidate.toPoc?.id}` === selection.segmentKey);
      if (!entry) return null;
      const distance = formatSegmentDistance(entry);
      const segmentEnd = parseSegmentBoundaryDate(
        entry.toPoc?.day_arrival || entry.toPoc?.day_departure,
        12
      );
      const routeMode = resolveTourSegmentRouteMode(entry.fromPoc, entry.toPoc, entry.segment);
      const isRoadSegment = routeMode === 'road';
      const action = segmentEnd && segmentEnd.getTime() < Date.now()
        ? (isRoadSegment ? 'travelled' : 'sailed')
        : (isRoadSegment ? 'travelling' : 'sailing');
      return {
        label: `${entry.fromPoc?.poc_info?.name || 'Port'} -> ${entry.toPoc?.poc_info?.name || 'Port'}`,
        detail: [distance, `${action} ${formatDay(dayData)}`].filter(Boolean).join(' · '),
        dayData,
      };
    }

    const rawFeatureId = String(selection.featureId || '');
    const eventMarkerMatch = rawFeatureId.match(/^mp-event-(.+)$/);
    const portMarkerMatch = rawFeatureId.match(/^mp-(.+)$/);

    if (selection.kind === 'eventRoute' || eventMarkerMatch) {
      const eventId = String(selection.eventId || eventMarkerMatch?.[1] || '');
      for (const poc of pocs) {
        const eventItem = (poc?.events || []).find((candidate) => String(candidate?.id) === eventId);
        if (!eventItem) continue;
        const dayKey = getTourEventDayKey(eventItem, poc?.day_arrival);
        const dayData = findDay(dayKey);
        const travel = getTourEventTravelSummary(eventItem, poc);
        const isRoute = selection.kind === 'eventRoute';
        const labelPrefix = isRoute ? 'Excursion route' : 'Excursion target';
        return {
          label: `${labelPrefix}: ${getTourEventName(eventItem)}`,
          detail: [
            travel?.distanceLabel,
            poc?.poc_info?.name,
            formatDay(dayData),
          ].filter(Boolean).join(' · '),
          dayData,
        };
      }
    }

    if (portMarkerMatch && !eventMarkerMatch) {
      const portId = portMarkerMatch[1];
      const poc = pocs.find((candidate) => String(candidate?.id) === String(portId));
      if (!poc) return null;
      const dayKey = featureIdToDayKey.get(rawFeatureId) || getDateDayKey(poc.day_arrival || poc.day_departure);
      const dayData = findDay(dayKey);
      return {
        label: poc?.poc_info?.name || 'Port',
        detail: `Port visit · ${formatDay(dayData)}`,
        dayData,
      };
    }

    return null;
  };

  const renderMapScreen = () => {
    const dayData = selectedDayData || currentDayData;
    const selectedDetails = getSelectedMapItemDetails(selectedMapFeature);
    const detailTargetDay = selectedDetails?.dayData || dayData;

    return (
      <div style={{ height: '100%', display: 'flex', alignItems: 'center' }}>
        <div style={{ width: '100%', display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) auto', gap: 8, alignItems: 'center' }}>
          <button
            type="button"
            onClick={() => selectGuestDay(dayData, { focusMap: false })}
            disabled={!dayData}
            style={{
              border: 0,
              background: 'transparent',
              padding: 0,
              textAlign: 'left',
              minWidth: 0,
              cursor: dayData ? 'pointer' : 'default',
            }}
          >
            <div style={{ fontWeight: 900, color: '#0f172a', fontSize: 15, lineHeight: 1.15, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {selectedDetails?.label || 'No map item selected'}
            </div>
            <div style={{ color: '#64748b', fontWeight: 800, fontSize: 12, marginTop: 4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {selectedDetails?.detail || 'Tap a route, port, or excursion for details'}
            </div>
          </button>
          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            <Button
              circular
              size="mini"
              icon="info"
              title="Details"
              onClick={() => {
                selectGuestDay(detailTargetDay, { focusMap: false });
                setMobileTab('today');
              }}
              disabled={!detailTargetDay}
            />
            <Button circular size="mini" icon="folder open" title="Files" onClick={() => setMobileTab('files')} />
            <Button circular size="mini" icon="crosshairs" title="Center" onClick={() => selectGuestDay(dayData)} disabled={!dayData} />
          </div>
        </div>
      </div>
    );
  };

  const renderMoreScreen = () => (
    <div style={{ display: 'grid', gap: 10 }}>
      <Header as='h3' style={{ margin: 0 }}>More</Header>
      <Button icon='info circle' content='Help' onClick={startTutorial} style={{ backgroundColor: '#f97316', color: '#fff' }} />
      <Button icon='folder open' content='All maps & files' onClick={() => setMobileTab('files')} />
      <Button icon='expand arrows alternate' content='Show full route' onClick={fitFullRoute} />
      <div style={{ border: '1px solid #dbe3ee', borderRadius: 14, padding: 12, background: '#fff', color: '#64748b' }}>
        Units follow your saved preferences. Sea routes use nautical miles and knots where applicable.
      </div>
    </div>
  );

  const renderLobbyTimeline = () => {
    if (!isLobbyGuestView || !processedDaysWithSegments.length || !isVisible) return null;
    if (lobbyTimelineCollapsed) {
      return (
        <button
          type='button'
          className='TourUser-lobby-timeline'
          data-scroll-overlay="true"
          onClick={() => setLobbyTimelineCollapsed(false)}
          style={{
            position: 'fixed',
            left: '50%',
            bottom: 10,
            transform: 'translateX(-50%)',
            zIndex: 510,
            border: '1px solid rgba(148, 163, 184, 0.45)',
            borderRadius: 999,
            background: 'rgba(255, 255, 255, 0.96)',
            boxShadow: '0 -6px 18px rgba(15, 23, 42, 0.14)',
            padding: '9px 18px',
            cursor: 'pointer',
            color: '#334155',
            fontWeight: 900,
          }}
        >
          <Icon name='chevron up' style={{ margin: 0 }} />
        </button>
      );
    }

    const scrollTimeline = (direction) => {
      lobbyTimelineScrollRef.current?.scrollBy?.({
        left: direction * 520,
        behavior: 'smooth',
      });
    };

    return (
      <div
        className='TourUser-lobby-timeline'
        data-scroll-overlay="true"
        style={{
          position: 'fixed',
          left: 0,
          right: 0,
          bottom: 0,
          height: LOBBY_TIMELINE_HEIGHT_PX,
          zIndex: 510,
          display: 'flex',
          alignItems: 'stretch',
          gap: 10,
          padding: '10px 14px',
          background: 'rgba(248,250,252,0.96)',
          borderTop: '1px solid rgba(148, 163, 184, 0.45)',
          boxShadow: '0 -10px 28px rgba(15, 23, 42, 0.12)',
          pointerEvents: 'auto',
        }}
      >
        <Button circular icon='chevron left' size='large' onClick={() => scrollTimeline(-1)} />
        <button
          type='button'
          aria-label='Collapse day timeline'
          onClick={() => setLobbyTimelineCollapsed(true)}
          style={{
            position: 'absolute',
            top: -18,
            left: '50%',
            transform: 'translateX(-50%)',
            width: 56,
            height: 28,
            borderRadius: 999,
            border: '1px solid rgba(148, 163, 184, 0.45)',
            background: 'rgba(255,255,255,0.96)',
            boxShadow: '0 -4px 14px rgba(15, 23, 42, 0.10)',
            cursor: 'pointer',
            color: '#64748b',
          }}
        >
          <Icon name='chevron down' style={{ margin: 0 }} />
        </button>
        <div ref={lobbyTimelineScrollRef} className='TourUser-lobby-timeline-scroll' style={{
          flex: 1,
          display: 'flex',
          gap: 10,
          overflowX: 'auto',
          overflowY: 'hidden',
          padding: '8px',
          borderRadius: 14,
          background: 'transparent',
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
        }}>
          {processedDaysWithSegments.map((dayData) => {
            const isToday = dayData.dayKey === todayDayKey;
            const isSelected = dayData.dayKey === selectedDayData?.dayKey;
            const weatherSummary = getDayWeatherSummary(dayData);
            return (
              <button
                type='button'
                key={dayData.dayKey}
                id={`timeline-${dayData.dayKey}`}
                onClick={() => selectGuestDay(dayData)}
                style={{
                  flex: '0 0 170px',
                  textAlign: 'left',
                  borderRadius: 14,
                  border: isSelected ? '2px solid #2563eb' : isToday ? '2px solid #f97316' : '1px solid #dbe3ee',
                  background: isSelected ? '#eff6ff' : '#ffffff',
                  padding: '10px',
                  cursor: 'pointer',
                  minHeight: 72,
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 6, alignItems: 'center' }}>
                  <strong>{formatDayTitle(dayData, { short: true })}</strong>
                  {isToday ? <span style={{ color: '#f97316', fontWeight: 800, fontSize: 11 }}>Today</span> : null}
                </div>
                <div style={{ color: '#334155', fontSize: 12, marginTop: 6, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {dayData.hasVisits ? dayData.dayPocs.map((poc) => poc.poc_info.name).join(' · ') : 'Day at sea'}
                </div>
                {weatherSummary ? <div style={{ color: '#64748b', fontSize: 12, marginTop: 5 }}>{weatherSummary}</div> : null}
              </button>
            );
          })}
        </div>
        <Button circular icon='chevron right' size='large' onClick={() => scrollTimeline(1)} />
      </div>
    );
  };

  const renderLobbyTopBar = () => {
    if (!isLobbyGuestView) return null;
    const dayData = selectedDayData || currentDayData;
    const weatherSummary = getDayWeatherSummary(dayData);
    const weatherCondition = getDayWeatherConditionLabel(dayData);
    const activity = getCurrentMapActivity();
    const dayNumberLabel = Number.isFinite(Number(dayData?.dayNumber)) ? `Day ${dayData.dayNumber}` : null;
    const statusParts = [
      activeTourToShow?.name,
      dayNumberLabel,
      formatDayTitle(dayData),
      weatherSummary,
      weatherCondition,
      activity?.detail,
    ].filter(Boolean);
    const toolbarButtonStyle = (active = false) => ({
      height: 34,
      borderRadius: 8,
      margin: 0,
      fontWeight: 800,
      boxShadow: 'none',
      background: active ? '#e8f2ff' : '#ffffff',
      color: active ? '#0b6fcf' : '#1f2937',
      border: active ? '1px solid #93c5fd' : '1px solid #dbe4ef',
      paddingLeft: 18,
      paddingRight: 18,
      flex: '0 0 auto',
      minWidth: 118,
    });

    return (
      <div
        className="TourUser-lobby-topbar"
        data-scroll-overlay="true"
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          height: LOBBY_TOP_BAR_HEIGHT_PX,
          zIndex: 1600,
          pointerEvents: 'auto',
          display: 'grid',
          gridTemplateColumns: 'minmax(0, 1fr) auto',
          gap: 10,
          alignItems: 'center',
          padding: '0 14px',
          background: '#ffffff',
          borderBottom: '1px solid #dbe4ef',
          boxShadow: '0 1px 4px rgba(15, 23, 42, 0.08)',
        }}
      >
        <div
          title={statusParts.join(' · ')}
          style={{
            minWidth: 0,
            display: 'flex',
            alignItems: 'baseline',
            gap: 9,
            color: '#334155',
            fontSize: 14,
            fontWeight: 800,
            overflow: 'hidden',
            whiteSpace: 'nowrap',
          }}
        >
          <span style={{ color: '#0f172a', fontSize: 18, fontWeight: 900, lineHeight: 1, overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 80 }}>
            {activeTourToShow?.name}
          </span>
          {statusParts.slice(1).map((part, index) => (
            <React.Fragment key={`${part}-${index}`}>
              <span style={{ color: '#cbd5e1', flex: '0 0 auto', lineHeight: 1 }}>·</span>
              <span style={{ lineHeight: 1, overflow: 'hidden', textOverflow: 'ellipsis', flex: index === statusParts.length - 2 ? '1 1 auto' : '0 0 auto' }}>
                {part}
              </span>
            </React.Fragment>
          ))}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, overflow: 'visible', whiteSpace: 'nowrap', justifySelf: 'end' }}>
	          <Button
              size="mini"
              compact
	            icon='calendar'
	            content='Today'
	            onClick={() => {
	              setLobbyTab('today');
	              if (actualTodayDayData) {
	                selectGuestDay(actualTodayDayData, { focusMap: false });
	              }
	            }}
	            style={toolbarButtonStyle(lobbyTab === 'today')}
	          />
	          <Button
              size="mini"
              compact
	            icon='list'
	            content='Overview'
	            onClick={() => setLobbyTab('upcoming')}
	            style={toolbarButtonStyle(lobbyTab === 'upcoming')}
	          />
	          <Button
              size="mini"
              compact
	            icon='folder open'
	            content='Files'
	            onClick={() => setLobbyTab('files')}
	            style={toolbarButtonStyle(lobbyTab === 'files')}
	          />
	        </div>
	      </div>
	    );
	  };

  const renderLobbyUpcomingPanel = () => (
    <div style={{ display: 'grid', gap: 12 }}>
      <div style={{ border: '1px solid #dbe3ee', borderRadius: 14, padding: '14px 16px', background: '#ffffff' }}>
        <div style={{ color: '#0f172a', fontSize: 15, fontWeight: 800, marginBottom: 10 }}>
          <Icon name={sailingScheduleSummary.icon} style={{ color: '#0284c7' }} />
          {sailingScheduleSummary.label}
        </div>
        <div style={{ display: 'grid', gap: 7, color: '#475569', fontSize: 14 }}>
          {[
            ['Distance sailed', sailingScheduleSummary.sailedKm],
            ['Remaining distance', sailingScheduleSummary.remainingKm],
            ['Total distance', sailingScheduleSummary.totalKm],
          ].map(([label, distanceKm], index) => (
            <div
              key={label}
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                gap: 12,
                alignItems: 'baseline',
                paddingTop: index === 2 ? 8 : 0,
                borderTop: index === 2 ? '1px solid #e2e8f0' : 'none',
                color: index === 2 ? '#0f172a' : '#475569',
                fontWeight: index === 2 ? 800 : 600,
              }}
            >
              <span>{label}</span>
              <span style={{ whiteSpace: 'nowrap' }}>{sailingScheduleSummary.formatDistance(distanceKm)}</span>
            </div>
          ))}
        </div>
      </div>
      {actualCruisePathFiles.length ? (
        <div style={{ border: '1px solid #dbe3ee', borderRadius: 14, padding: '14px 16px', background: '#ffffff' }}>
          <div style={{ color: '#0f172a', fontSize: 15, fontWeight: 800, marginBottom: 10 }}>
            <Icon name='map signs' style={{ color: '#0f766e' }} />
            Actual sailed path
          </div>
          <div style={{ color: '#64748b', fontSize: 13, marginBottom: 10 }}>
            Download the map generated from the cleaned historical cruise track.
          </div>
          <div style={{ display: 'grid', gap: 8 }}>
            {actualCruisePathFiles.map((file) => renderTourFileResource(file))}
          </div>
        </div>
      ) : null}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 10 }}>
        <Header as='h3' style={{ margin: 0, color: '#111827' }}>Itinerary Overview</Header>
        {actualTodayDayData && selectedDayData?.dayKey !== todayDayKey ? (
          <Button size='small' icon='calendar check' content='Back to Today' onClick={() => {
            setLobbyTab('today');
            goToToday();
          }} />
        ) : null}
      </div>
      {renderAllDaysPanel()}
    </div>
  );

  const renderLobbyView = () => (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0, background: '#f8fafc', paddingTop: LOBBY_TOP_BAR_HEIGHT_PX }}>
      <div
        style={{
          flex: '1 1 auto',
          minHeight: 0,
          overflowY: 'auto',
          // Preserve visible breathing room after the final card, including
          // on devices that reserve a bottom system inset.
          padding: '14px 14px calc(28px + env(safe-area-inset-bottom, 0px))',
        }}
      >
        {lobbyTab === 'upcoming'
          ? renderLobbyUpcomingPanel()
          : lobbyTab === 'files'
            ? renderFilesScreen()
            : renderTodayPanel()}
      </div>
    </div>
  );

  const renderMapLegendContent = ({ compact = false } = {}) => {
    const lineSample = (style = {}) => (
      <span
        style={{
          width: 34,
          height: style.width || 4,
          background: style.dashed
            ? `repeating-linear-gradient(to right, ${style.color} 0 7px, transparent 7px 11px)`
            : style.color,
          borderRadius: 999,
          display: 'inline-block',
          flex: '0 0 auto',
        }}
      />
    );
    const sailedLineSample = () => (
      <span
        style={{
          width: 34,
          height: 5,
          borderRadius: 999,
          background: '#d946ef',
          display: 'inline-block',
          flex: '0 0 auto',
        }}
      />
    );
    const items = [
      { key: 'planned', label: 'Planned path', sample: lineSample({ color: '#2980b9', width: 4 }) },
      { key: 'sailed', label: 'Sailed path', sample: sailedLineSample() },
      {
        key: 'distance',
        label: 'Distance labels',
        sample: (
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              minWidth: 34,
              height: 18,
              padding: '0 5px',
              border: '1px solid #93c5fd',
              background: 'rgba(255,255,255,0.95)',
              color: '#075985',
              fontSize: 10,
              fontWeight: 800,
              lineHeight: 1,
              flex: '0 0 auto',
            }}
          >
            12
          </span>
        ),
      },
      { key: 'excursion', label: 'Land excursions', sample: lineSample({ color: '#f97316', width: 4, dashed: true }) },
      { key: 'land', label: 'Land portion', sample: lineSample({ color: '#2980b9', width: 4, dashed: true }) },
    ];

    return (
      <div style={{ display: 'grid', gap: compact ? 7 : 6 }}>
        {items.map((item) => (
          <div key={item.key} style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
            {item.sample}
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.label}</span>
          </div>
        ))}
      </div>
    );
  };

  const renderMapLegendOverlay = () => {
    if (!isVisible) return null;
    if (!showRouteLegend) return null;
    if (isMobileGuestView && mobileTab !== 'map') return null;
    if (!isMobileGuestView && !isLobbyGuestView) return null;
    const baseBottomOffset = isLobbyGuestView && !isMobile
      ? LOBBY_TIMELINE_HEIGHT_PX + 18
      : 18;
    const bottomOffset = baseBottomOffset;
    const shipUpdateAt = currentTourPosition?.recorded_at || currentTourPosition?.received_at;
    const shipUpdateDate = shipUpdateAt ? new Date(shipUpdateAt) : null;
    const shipUpdateLabel = shipUpdateDate && !Number.isNaN(shipUpdateDate.getTime())
      ? `${String(shipUpdateDate.getDate()).padStart(2, '0')}/${String(shipUpdateDate.getMonth() + 1).padStart(2, '0')} ${String(shipUpdateDate.getHours()).padStart(2, '0')}:${String(shipUpdateDate.getMinutes()).padStart(2, '0')}`
      : 'Unavailable';
    const depthLabel = shipDepth.status === 'available'
      ? `${Math.round(shipDepth.meters)} m`
      : shipDepth.status === 'land'
        ? 'On land'
        : shipDepth.status === 'loading'
          ? 'Looking up...'
          : 'Unavailable';
    const statusLineStyle = {
      display: 'grid',
      gridTemplateColumns: 'auto minmax(0, 1fr)',
      gap: 7,
      alignItems: 'center',
      minWidth: 0,
      color: '#475569',
    };

    return (
      <div
        className='TourUser-map-legend'
        data-scroll-overlay="true"
        style={{
          position: 'fixed',
          left: 14,
          bottom: bottomOffset,
          zIndex: 520,
          display: 'grid',
          gap: 6,
          padding: isMobile ? '7px 8px' : '8px 10px',
          borderRadius: 10,
          border: '1px solid rgba(148, 163, 184, 0.45)',
          background: 'rgba(255, 255, 255, 0.92)',
          boxShadow: '0 8px 24px rgba(15, 23, 42, 0.14)',
          color: '#334155',
          fontSize: isMobile ? 10 : 12,
          fontWeight: 700,
          pointerEvents: 'none',
          maxWidth: isMobile ? 170 : 210,
        }}
      >
        {renderMapLegendContent()}
        <div style={{ borderTop: '1px solid rgba(148, 163, 184, 0.35)', paddingTop: 6, display: 'grid', gap: 5 }}>
          <div style={statusLineStyle}>
            <Icon name="ship" style={{ color: '#f97316', margin: 0 }} />
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              Ship update: {shipUpdateLabel}
            </span>
          </div>
          <div style={statusLineStyle}>
            <Icon name="tint" style={{ color: '#0284c7', margin: 0 }} />
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              Approximate depth: {depthLabel}
            </span>
          </div>
        </div>
      </div>
    );
  };

  const renderMobileMapActionsOverlay = () => {
    if (!isVisible || !isMobileGuestView || mobileTab !== 'map') return null;
    const activity = getCurrentMapActivity();
    const dayData = selectedDayData || currentDayData;
    const chipStyle = {
      border: '1px solid rgba(191, 219, 254, 0.9)',
      borderRadius: 999,
      background: 'rgba(255, 255, 255, 0.94)',
      color: '#0f172a',
      boxShadow: '0 8px 20px rgba(15, 23, 42, 0.16)',
      fontWeight: 800,
      margin: 0,
      flex: '0 0 auto',
    };
    return (
      <div
        className="TourUser-mobile-map-actions"
        data-scroll-overlay="true"
        style={{
          position: 'fixed',
          top: 'calc(12px + env(safe-area-inset-top, 0px))',
          left: 10,
          right: 10,
          zIndex: 560,
          display: 'grid',
          gap: 8,
          padding: '2px 2px 6px',
          pointerEvents: 'auto',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            justifySelf: 'start',
            maxWidth: 'calc(100vw - 22px)',
            borderRadius: 16,
            padding: '8px 11px',
            background: 'rgba(255,255,255,0.94)',
            border: '1px solid rgba(191,219,254,0.88)',
            boxShadow: '0 8px 20px rgba(15, 23, 42, 0.14)',
          }}
        >
          <div style={{ color: '#475569', fontWeight: 800, fontSize: 12, maxWidth: 270, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {activity.detail}
          </div>
        </div>
        <div
          style={{
            display: 'flex',
            gap: 6,
            overflowX: 'auto',
            overflowY: 'hidden',
            scrollbarWidth: 'none',
            whiteSpace: 'nowrap',
            maxWidth: '100%',
            flexWrap: 'nowrap',
            paddingBottom: 2,
          }}
        >
          <Button
            size="mini"
            compact
            icon="calendar"
            content="Today"
            onClick={focusTodayOnMap}
            disabled={!actualTodayDayData}
            style={chipStyle}
          />
          <Button
            size="mini"
            compact
            icon="map marker alternate"
            content="Selected day"
            onClick={() => {
              if (dayData) {
                fitMapToDayPorts(dayData, { includeSegmentEndpoints: true });
              }
            }}
            disabled={!dayData}
            style={chipStyle}
          />
          <Button
            size="mini"
            compact
            icon="expand arrows alternate"
            content="Full Route"
            onClick={fitFullRoute}
            style={chipStyle}
          />
          {currentTourPosition ? (
            <Button
              size="mini"
              compact
              icon="ship"
              content="Ship location"
              onClick={focusTrackingLocation}
              style={chipStyle}
            />
          ) : null}
        </div>
      </div>
    );
  };

  const renderLobbyMapActionsOverlay = () => {
    if (!isLobbyGuestView) return null;
    const dayData = selectedDayData || currentDayData;
    const chipStyle = {
      border: '1px solid rgba(191, 219, 254, 0.9)',
      borderRadius: 999,
      background: 'rgba(255, 255, 255, 0.94)',
      color: '#0f172a',
      boxShadow: '0 8px 20px rgba(15, 23, 42, 0.16)',
      fontWeight: 800,
      margin: 0,
      flex: '0 0 auto',
    };

    return (
      <div
        className="TourUser-lobby-map-actions"
        data-scroll-overlay="true"
        style={{
          position: 'fixed',
          top: `calc(${LOBBY_TOP_BAR_HEIGHT_PX}px + 14px)`,
          left: 16,
          zIndex: 780,
          display: 'flex',
          gap: 8,
          alignItems: 'center',
          pointerEvents: 'auto',
        }}
      >
        <Button
          size="small"
          icon="calendar"
          content="Today"
          onClick={focusTodayOnMap}
          disabled={!actualTodayDayData}
          style={chipStyle}
        />
        <Button
          size="small"
          icon="map marker alternate"
          content="Selected day"
          onClick={() => {
            if (dayData) {
              fitMapToDayPorts(dayData, { includeSegmentEndpoints: true });
            }
          }}
          disabled={!dayData}
          style={chipStyle}
        />
        <Button
          size="small"
          icon="expand arrows alternate"
          content="Full Route"
          onClick={fitFullRoute}
          style={chipStyle}
        />
        {currentTourPosition ? (
          <Button
            size="small"
            icon="ship"
            content="Ship location"
            onClick={focusTrackingLocation}
            style={chipStyle}
          />
        ) : null}
      </div>
    );
  };

  return (
    <>
      {(isSailedTrackLoading || isRouteGeometryLoading) && (
        <div style={cruisingPathLoadingBannerStyle}>
          <Icon loading name="spinner" style={{ margin: 0 }} />
          <span>Loading cruising path</span>
        </div>
      )}
      <style>
        {`
          .TourUser-lobby-timeline-scroll::-webkit-scrollbar {
            display: none;
          }
        `}
      </style>
      {renderLobbyTopBar()}
      {renderLobbyMapActionsOverlay()}
      {renderMobileMapActionsOverlay()}
      {renderMapLegendOverlay()}
      <SidebarWrapper
        isMobile={isMobile}
        sidebarConfig={effectiveSidebarConfig}
        onClose={closeTourDetailBar}
        onToggleVisibility={isLobbyGuestView ? null : setIsVisible}
        isVisible={isVisible}
        panelLabel="Itinerary"
        onHeightChange={onHeightChange}
        zIndex={isMobileGuestView && mobileTab !== 'map' && !weatherModalOpen ? 1400 : 500}
        minHeightVh={isMobileGuestView && mobileTab === 'map' ? 16 : 20}
        maxHeightVh={isMobileGuestView && mobileTab === 'map' ? 92 : 90}
        showCloseButton={!isLobbyGuestView && !isMobile}
        showVisibilityHandle={!isMobileGuestView}
      >
      {showTutorial && highlightPosition && (
        <div
          style={{
            position: 'fixed',
            top: highlightPosition.top,
            left: highlightPosition.left,
            width: highlightPosition.width,
            height: highlightPosition.height,
            borderRadius: 8,
            backgroundColor: 'rgba(37, 99, 235, 0.24)',
            border: '2px solid rgba(37, 99, 235, 0.65)',
            zIndex: 1700,
            pointerEvents: 'none',
          }}
        />
      )}

      {showTutorial && tutorialSteps[currentTutorialStep] && (
        <div
          style={{
            position: 'fixed',
            top: isMobile ? '18%' : '30%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            background: '#ffffff',
            borderRadius: 8,
            padding: 18,
            width: 'min(420px, calc(100vw - 28px))',
            zIndex: 1710,
            boxShadow: '0 12px 34px rgba(15, 23, 42, 0.28)',
            border: '1px solid #dbe3ee',
          }}
        >
          <Header as='h3' style={{ marginTop: 0 }}>
            {tutorialSteps[currentTutorialStep].title}
          </Header>
          <p style={{ lineHeight: 1.5, color: '#475569' }}>
            {tutorialSteps[currentTutorialStep].description}
          </p>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, marginTop: 18, flexWrap: 'wrap' }}>
            <Button onClick={prevTutorialStep} disabled={currentTutorialStep === 0}>
              Back
            </Button>
            <Button onClick={nextTutorialStep} primary>
              {currentTutorialStep === tutorialSteps.length - 1 ? 'Finish' : 'Next step'}
            </Button>
            <Button onClick={() => setShowTutorial(false)}>
              Close help
            </Button>
          </div>
        </div>
      )}

      {isMobileGuestView ? (
        <MobileTourUserView
          activeTourToShow={activeTourToShow}
          mobileTab={mobileTab}
          setMobileTab={setMobileTab}
          selectedDayData={hydrateDayDataFromFullTour(selectedDayData)}
          currentDayData={hydrateDayDataFromFullTour(currentDayData)}
          yesterdayDayData={hydrateDayDataFromFullTour(previousSelectedDayData)}
          tomorrowDayData={hydrateDayDataFromFullTour(nextSelectedDayData)}
          currentTourPosition={currentTourPosition}
          currentShipActivity={getCurrentMapActivity()}
          formatDayTitle={formatDayTitle}
          formatDistanceForSegment={formatSegmentDistance}
          getDayEvents={getDayEvents}
          getDayWeatherSummary={getDayWeatherSummary}
          renderDayResourceList={renderDayResourceList}
          renderWeatherDetails={renderSelectedDayWeatherWindows}
          openWeatherDetails={openDayWeatherDetails}
          getPrimaryPortMapUrl={getPrimaryPortMapUrl}
          renderUpcomingPanel={renderLobbyUpcomingPanel}
          renderMapScreen={renderMapScreen}
          renderFilesScreen={renderFilesScreen}
          renderMoreScreen={renderMoreScreen}
          selectGuestDay={selectGuestDay}
          focusTrackingLocation={focusTrackingLocation}
          focusTourEventOnMap={focusTourEventOnMap}
          getApiHeaders={getApiHeaders}
          isFullTourPending={isFullTourPending}
        />
      ) : renderLobbyView()}

      <WeatherForecastModal
        open={weatherModalOpen}
        onClose={() => {
          setWeatherModalOpen(false);
          setSelectedWeatherStayWindow(null);
          setSelectedWeatherItineraryContexts([]);
        }}
        weatherData={selectedWeatherData}
        locationName={selectedLocationName}
        dateRange={selectedDateRange}
        stayWindow={selectedWeatherStayWindow}
        itineraryContexts={selectedWeatherItineraryContexts}
        desktopFullscreen={isLobbyGuestView}
        desktopViewportPercent={80}
      />

      <Modal
        open={Boolean(selectedTourMapMarker)}
        onClose={() => setSelectedTourMapMarker(null)}
        size='tiny'
      >
        <Modal.Header>
          <Icon name='map outline' />
          {selectedTourMapMarker?.name || 'Map'}
        </Modal.Header>
        <Modal.Content>
          <div style={{ color: '#64748b', fontWeight: 700, marginBottom: 14 }}>
            {selectedTourMapMarker?.sourceLabel || 'Tour map'}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 10 }}>
            <Button
              primary
              size='large'
              icon='external alternate'
              content='Open online'
              onClick={() => {
                if (selectedTourMapMarker?.onlineUrl) {
                  window.location.assign(selectedTourMapMarker.onlineUrl);
                }
              }}
              disabled={!selectedTourMapMarker?.onlineUrl}
              style={{ minHeight: 54, borderRadius: 12, fontWeight: 900 }}
            />
            <Button
              size='large'
              icon='external alternate'
              content='Open'
              href={selectedTourMapMarker?.downloadUrl || undefined}
              target='_blank'
              disabled={!selectedTourMapMarker?.downloadUrl}
              style={{ minHeight: 54, borderRadius: 12, fontWeight: 900 }}
            />
          </div>
        </Modal.Content>
      </Modal>

      <Modal
        open={Boolean(selectedTourFileMarker)}
        onClose={() => setSelectedTourFileMarker(null)}
        size='small'
      >
        <Modal.Header>
          <Icon name='file outline' />
          {selectedTourFileMarker?.name || 'File'}
        </Modal.Header>
        <Modal.Content>
          {selectedTourFileMarker?.locationName ? (
            <div style={{ color: '#64748b', fontWeight: 700, marginBottom: 14 }}>
              {selectedTourFileMarker.locationName}
            </div>
          ) : null}
          <div style={{ display: 'grid', gap: 14, maxHeight: '64vh', overflowY: 'auto', paddingRight: 4 }}>
            {selectedTourFileGallery.map((resource) => (
              <div key={resource.id} style={{ border: '1px solid #dbe3ee', borderRadius: 10, overflow: 'hidden', background: '#fff' }}>
                {resource.previewUrl ? (
                  <TourFileImagePreview
                    src={resource.previewUrl}
                    alt={resource.name || ''}
                    containerStyle={{ width: '100%', maxHeight: '48vh', borderRadius: 0 }}
                    imageStyle={{ height: 'auto', maxHeight: '48vh', objectFit: 'contain' }}
                  />
                ) : null}
                <div style={{ padding: 12, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
                  <div style={{ minWidth: 0, fontWeight: 800, color: '#1f2937', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {resource.name || 'Photo'}
                  </div>
                  <div style={{ display: 'flex', gap: 6, flex: '0 0 auto' }}>
                    <Button
                      as='a'
                      size='small'
                      basic
                      icon='external alternate'
                      content='Open'
                      href={resource.fileUrl || undefined}
                      target='_blank'
                      rel='noreferrer'
                      disabled={!resource.fileUrl}
                      style={{ fontWeight: 800 }}
                    />
                    <Button
                      as='a'
                      size='small'
                      icon='download'
                      content='Download'
                      href={resource.fileUrl || undefined}
                      download
                      target='_blank'
                      rel='noreferrer'
                      disabled={!resource.fileUrl}
                      style={{ fontWeight: 800 }}
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </Modal.Content>
      </Modal>

      <Modal open={tourMapsModalOpen} onClose={() => setTourMapsModalOpen(false)} size='small'>
        <Modal.Header>
          <Icon name='folder open' />
          Tour Maps & Files
        </Modal.Header>
        <Modal.Content scrolling>
          {tourResourceTabs.length > 0 ? (
            <Tab
              menu={{ secondary: true, pointing: true }}
              panes={tourResourceTabs}
            />
          ) : (
            <div style={{ color: '#6b7280', padding: '6px 2px' }}>No maps or files available for this tour.</div>
          )}
        </Modal.Content>
        <Modal.Actions>
          <Button
            primary
            icon='download'
            content='Download all low-res JPGs'
            onClick={downloadTourLowResMaps}
            loading={isDownloadingTourMaps}
            disabled={isDownloadingTourMaps || tourResourceCount === 0}
          />
          <Button onClick={() => setTourMapsModalOpen(false)}>Close</Button>
        </Modal.Actions>
      </Modal>

      </SidebarWrapper>
      {renderLobbyTimeline()}
    </>
  );
}
